import React, { useState, useEffect, useRef, useMemo, useCallback, startTransition } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Tooltip,
  Divider,
  Button,
  Grid,
  Snackbar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  Alert,
  Switch,
  FormControlLabel,
  LinearProgress,
  Checkbox,
  Select,
  Slider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  AutoFixHigh,
  ContentPasteRounded,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  HistoryRounded,
  BoltRounded,
  ContentCopyRounded,
  StorageRounded,
  ScienceRounded,
  HubRounded,
  ChecklistRounded,
  SettingsRounded,
  PublicRounded,
  Palette as PaletteIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  BarChart,
  Person,
  AutoStories,
  Checkroom,
  Speed as SpeedIcon,
  PlayArrow as PlayArrowIcon,
  SaveAlt as SaveAltIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  PhotoLibrary,
  ArrowBack as ArrowBackIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  MoreVert as MoreVertIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  NightsStay as NightsStayIcon,
  WbSunny as WbSunnyIcon,
  StarRounded,
  StarBorder as StarBorderIcon,
  ManageSearch as ManageSearchIcon,
  AutoMode as AutoModeIcon,
  PlayCircle as PlayCircleIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  ForwardToInbox as ForwardToInboxIcon,
  SearchRounded as SearchRoundedIcon,
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useHotkeys } from 'react-hotkeys-hook';
import { DndContext, useDraggable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

// Firebase
import { db, auth, isFirebaseConfigured } from './src/firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';

// Components — AIAvatar and VoiceInput are sync (needed on initial render)
import AIAvatar from './src/components/AIAvatar';
import VoiceInput from './src/components/VoiceInput';

// withSuspense: wraps a lazy component in a Suspense boundary so JSX usage unchanged
function withSuspense(LazyComp) {
  return function Wrapped(props) {
    return (
      <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}><CircularProgress sx={{ color: 'rgba(0,255,255,0.6)' }} /></Box>}>
        <LazyComp {...props} />
      </React.Suspense>
    );
  };
}

function EtsyConnectionCard({ apiBase }) {
  const [status, setStatus] = useState({ loading: true, connected: false, configured: false, message: '' });
  const [connecting, setConnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/api/etsy/oauth/status`);
      if (!response.ok) throw new Error('Unable to load Etsy status');
      setStatus({ ...await response.json(), loading: false });
    } catch {
      setStatus({ loading: false, connected: false, configured: false, message: 'Unable to load Etsy status' });
    }
  }, [apiBase]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${apiBase}/api/etsy/oauth/start`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok || !data.authorization_url) throw new Error(data.error || 'Unable to start Etsy connection');
      window.location.assign(data.authorization_url);
    } catch (error) {
      setStatus((current) => ({ ...current, message: error.message }));
      setConnecting(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Etsy</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            {status.loading ? 'Checking connection…' : status.message}
          </Typography>
        </Box>
        {status.connected ? (
          <Chip label="Connected" size="small" color="success" />
        ) : (
          <Button variant="contained" size="small" disabled={!status.configured || connecting} onClick={connect}>
            {connecting ? 'Opening Etsy…' : 'Connect Etsy'}
          </Button>
        )}
      </Stack>
      {!status.loading && !status.configured && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#f6d38c' }}>
          Add `ETSY_SHARED_SECRET` in Railway, then reload this page.
        </Typography>
      )}
    </Paper>
  );
}

// Lazy-loaded feature panels — separate chunks prevent MUI circular-dep TDZ in Rollup
const CommandPalette = withSuspense(React.lazy(() => import('./src/components/CommandPalette')));
const Canvas = withSuspense(React.lazy(() => import('./src/components/Canvas')));
const DeepResearch = withSuspense(React.lazy(() => import('./src/components/DeepResearch')));
const ImageGenerator = withSuspense(React.lazy(() => import('./src/components/ImageGenerator')));
const VideoCreator = withSuspense(React.lazy(() => import('./src/components/VideoCreator')));
const KnowledgeGraph = withSuspense(React.lazy(() => import('./src/components/KnowledgeGraph')));
const GuidedLearning = withSuspense(React.lazy(() => import('./src/components/GuidedLearning')));
const ChartComponent = withSuspense(React.lazy(() => import('./src/components/ChartComponent')));
const GameLazy = React.lazy(() => import('./src/game/Game'));
const SystemDiagnostics = withSuspense(React.lazy(() => import('./src/components/SystemDiagnostics')));
const SystemStatusCard = withSuspense(React.lazy(() => import('./src/components/SystemStatusCard')));
const WeatherWidget = withSuspense(React.lazy(() => import('./src/components/WeatherWidget')));
const CockpitPanel = withSuspense(React.lazy(() => import('./src/components/CockpitPanel')));
const CreativeSuite = withSuspense(React.lazy(() => import('./src/components/CreativeSuite')));
const Sassy = withSuspense(React.lazy(() => import('./src/components/Sassy')));
const MediaGallery = withSuspense(React.lazy(() => import('./src/components/MediaGallery')));
const AvatarStudio = withSuspense(React.lazy(() => import('./src/components/AvatarStudio')));
const VesperAvatar3D = withSuspense(React.lazy(() => import('./src/components/VesperAvatar3D')));
const IntegrationsHub = withSuspense(React.lazy(() => import('./src/components/IntegrationsHub')));
const IncomeDashboard = withSuspense(React.lazy(() => import('./src/components/IncomeDashboard')));
const GapsJournal = withSuspense(React.lazy(() => import('./src/components/GapsJournal')));
const MorningBrief = withSuspense(React.lazy(() => import('./src/components/MorningBrief')));
const BackgroundStudio = withSuspense(React.lazy(() => import('./src/components/BackgroundStudio')));
const SetupWizard = withSuspense(React.lazy(() => import('./src/components/SetupWizard')));

// Styles
import './App.css';
import './src/enhancements.css';

// ─── AutopilotPanel — standalone component so it has clean hooks scope ────────
function AutopilotPanel({ apiBase, onBack, jobs, setJobs, log, setLog, loading, setLoading, running, setRunning, niches, setNiches, accentColor, setToast }) {
  const accent = accentColor || '#00ffff';

  const fetchJobs = React.useCallback(async () => {
    try {
      const [jr, lr] = await Promise.all([
        fetch(`${apiBase}/api/autopilot/jobs`),
        fetch(`${apiBase}/api/autopilot/log?limit=20`),
      ]);
      if (jr.ok) { const d = await jr.json(); setJobs(d.jobs || []); }
      if (lr.ok) { const d = await lr.json(); setLog(d.log || []); }
    } catch (e) { /* network failure */ }
  }, [apiBase, setJobs, setLog]);

  React.useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const toggleJob = async (job) => {
    const updated = { enabled: !job.enabled };
    try {
      const r = await fetch(`${apiBase}/api/autopilot/jobs/${job.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      if (r.ok) { await fetchJobs(); setToast({ open: true, message: job.enabled ? `${job.name} paused` : `${job.name} enabled — runs on schedule`, severity: 'success' }); }
    } catch (e) { setToast({ open: true, message: 'Failed to update job', severity: 'error' }); }
  };

  const saveNiche = async (job) => {
    const niche = (niches[job.id] ?? job.niche) || '';
    try {
      const r = await fetch(`${apiBase}/api/autopilot/jobs/${job.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ niche }),
      });
      if (r.ok) { await fetchJobs(); setToast({ open: true, message: 'Niche saved', severity: 'success' }); }
    } catch (e) {}
  };

  const runNow = async (job) => {
    setRunning(prev => ({ ...prev, [job.id]: true }));
    setToast({ open: true, message: `▶ ${job.name} started in background…`, severity: 'info' });
    try {
      await fetch(`${apiBase}/api/autopilot/run-now/${job.id}`, { method: 'POST' });
      // Poll for completion: check log for new entry
      let polls = 0;
      const check = setInterval(async () => {
        polls++;
        if (polls > 60) { clearInterval(check); setRunning(prev => ({ ...prev, [job.id]: false })); return; }
        await fetchJobs();
        const updatedJobs = await fetch(`${apiBase}/api/autopilot/jobs`).then(r => r.json()).catch(() => null);
        if (updatedJobs) {
          const j = (updatedJobs.jobs || []).find(x => x.id === job.id);
          if (j && j.last_run) {
            clearInterval(check);
            setRunning(prev => ({ ...prev, [job.id]: false }));
            await fetchJobs();
            const st = j.last_status === 'ok' ? 'success' : 'warning';
            setToast({ open: true, message: `${job.name}: ${j.last_output || 'Done'}`, severity: st });
          }
        }
      }, 3000);
    } catch (e) {
      setRunning(prev => ({ ...prev, [job.id]: false }));
    }
  };

  const JOB_ICONS = { weekly_pipeline: '🏭', daily_article: '✍️', weekly_keywords: '🔑', social_drip: '📲' };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: { xs: 2, md: 3 }, maxWidth: 860, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <AutoModeIcon sx={{ color: accent, fontSize: '1.4rem' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>
            Autopilot
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Vesper works while you sleep
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Chip
            label={jobs.filter(j => j.enabled).length + ' active'}
            size="small"
            sx={{ bgcolor: jobs.some(j => j.enabled) ? 'rgba(107,203,119,0.15)' : 'rgba(255,255,255,0.06)', color: jobs.some(j => j.enabled) ? '#6bcb77' : 'rgba(255,255,255,0.4)', fontWeight: 700, fontSize: '0.65rem' }}
          />
        </Box>
      </Box>

      {/* Job cards */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {jobs.map((job) => {
          const isRunning = running[job.id];
          const icon = JOB_ICONS[job.type] || '⚙️';
          const currentNiche = niches[job.id] ?? job.niche ?? '';
          return (
            <Paper key={job.id} elevation={0} sx={{
              p: 2.5,
              border: `1px solid ${job.enabled ? `${accent}40` : 'rgba(255,255,255,0.07)'}`,
              borderRadius: 2,
              bgcolor: job.enabled ? `${accent}08` : 'rgba(255,255,255,0.02)',
              transition: 'border-color 0.2s',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Typography sx={{ fontSize: '1.5rem', lineHeight: 1, mt: 0.2 }}>{icon}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
                      {job.name}
                    </Typography>
                    {job.enabled && (
                      <Chip label="ON" size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: `${accent}30`, color: accent, fontWeight: 800 }} />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                    {job.description}
                  </Typography>

                  {/* Niche input */}
                  {job.niche !== undefined && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Your niche (e.g. AI productivity, Etsy sellers)"
                        value={currentNiche}
                        onChange={e => setNiches(prev => ({ ...prev, [job.id]: e.target.value }))}
                        sx={{
                          flex: 1,
                          '& .MuiInputBase-root': { fontSize: '0.75rem', bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1 },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                          '& input': { color: '#fff', py: 0.8 },
                        }}
                      />
                      <Button size="small" onClick={() => saveNiche(job)} sx={{ color: accent, fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 1.5 }}>
                        Save
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}>
                        {job.schedule_label || 'Scheduled'}
                      </Typography>
                    </Box>

                    {job.last_run && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {job.last_status === 'ok'
                          ? <CheckCircleIcon sx={{ fontSize: '0.75rem', color: '#6bcb77' }} />
                          : <ErrorOutlineIcon sx={{ fontSize: '0.75rem', color: '#ff6b6b' }} />}
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>
                          Last: {new Date(job.last_run).toLocaleDateString()} — {job.last_output?.slice(0, 60) || ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Controls */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!job.enabled}
                        onChange={() => toggleJob(job)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-track': { bgcolor: job.enabled ? `${accent}60` : 'rgba(255,255,255,0.1)' },
                          '& .Mui-checked .MuiSwitch-thumb': { bgcolor: accent },
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                  <Tooltip title="Run now (single run in background)" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isRunning}
                        onClick={() => runNow(job)}
                        sx={{ color: isRunning ? 'rgba(255,255,255,0.2)' : accent, border: `1px solid ${isRunning ? 'rgba(255,255,255,0.1)' : `${accent}40`}`, borderRadius: 1, p: 0.5 }}
                      >
                        {isRunning
                          ? <CircularProgress size={14} sx={{ color: accent }} />
                          : <PlayCircleIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* Activity log */}
      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', letterSpacing: '0.15em', mb: 1, display: 'block' }}>
        Activity Log
      </Typography>
      {log.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
          No jobs have run yet. Enable a job or hit Run Now to start.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {log.slice(0, 15).map((entry, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, p: 1.2, borderRadius: 1.5, bgcolor: entry.success ? 'rgba(107,203,119,0.05)' : 'rgba(255,107,107,0.05)', border: `1px solid ${entry.success ? 'rgba(107,203,119,0.12)' : 'rgba(255,107,107,0.12)'}` }}>
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4, flexShrink: 0 }}>
                {entry.success ? '✓' : '✗'}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: entry.success ? '#6bcb77' : '#ff6b6b', fontSize: '0.7rem' }}>
                    {entry.job_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>
                    {entry.ended ? new Date(entry.ended).toLocaleString() : ''}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.output}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}


function EmailPanel({ apiBase, onBack, to, setTo, subject, setSubject, body, setBody, sending, setSending, config, setConfig, accentColor, setToast }) {
  const accent = accentColor || '#00ffff';

  React.useEffect(() => {
    fetch(`${apiBase}/api/email/config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setConfig(d); })
      .catch(() => {});
  }, [apiBase, setConfig]);

  const sendEmail = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      setToast({ open: true, message: 'Please fill in To, Subject, and Body', severity: 'warning' });
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${apiBase}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: to.trim(), subject: subject.trim(), body: body.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setToast({ open: true, message: d.message || 'Email sent!', severity: 'success' });
        setTo(''); setSubject(''); setBody('');
      } else {
        setToast({ open: true, message: d.detail || 'Failed to send email', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, message: 'Network error — could not reach backend', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  const providerActive = config && (config.brevo || config.smtp || config.resend);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: { xs: 2, md: 3 }, maxWidth: 700, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>
            Email
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Send emails from Vesper
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          {config !== null && (
            <Chip
              label={providerActive ? 'Provider ready' : 'No provider'}
              size="small"
              sx={{
                bgcolor: providerActive ? 'rgba(107,203,119,0.15)' : 'rgba(255,107,107,0.1)',
                color: providerActive ? '#6bcb77' : '#ff6b6b',
                fontWeight: 700,
                fontSize: '0.65rem',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Provider status */}
      {config && !providerActive && (
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, bgcolor: 'rgba(255,107,107,0.05)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: '#ff6b6b', fontWeight: 700, mb: 0.5 }}>
            ⚠ No email provider configured
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
            Add one of these to your backend <code style={{ color: accent }}>.env</code>:
          </Typography>
          <Box component="pre" sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', overflowX: 'auto' }}>
{`BREVO_API_KEY=your_key_here       # brevo.com (free 300/day)
RESEND_API_KEY=your_key_here      # resend.com (free 3k/mo)
SMTP_HOST=smtp.gmail.com          # or custom SMTP
SMTP_PORT=587
SMTP_USER=you@gmail.com
SMTP_PASS=your_app_password`}
          </Box>
        </Paper>
      )}

      {/* Compose form */}
      <Stack spacing={2}>
        <TextField
          label="To"
          placeholder="recipient@example.com"
          value={to}
          onChange={e => setTo(e.target.value)}
          fullWidth
          size="small"
          sx={{
            '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
            '& input': { color: '#fff' },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
          }}
        />
        <TextField
          label="Subject"
          placeholder="Message subject"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          fullWidth
          size="small"
          sx={{
            '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
            '& input': { color: '#fff' },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
          }}
        />
        <TextField
          label="Body"
          placeholder="Write your message here…"
          value={body}
          onChange={e => setBody(e.target.value)}
          fullWidth
          multiline
          rows={8}
          sx={{
            '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.04)' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
            '& textarea': { color: '#fff' },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
          }}
        />
        <Button
          variant="contained"
          disabled={sending || !providerActive}
          onClick={sendEmail}
          startIcon={sending ? <CircularProgress size={16} sx={{ color: '#000' }} /> : null}
          sx={{
            bgcolor: accent,
            color: '#000',
            fontWeight: 700,
            textTransform: 'none',
            alignSelf: 'flex-start',
            px: 3,
            '&:hover': { bgcolor: accent, opacity: 0.85 },
            '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
          }}
        >
          {sending ? 'Sending…' : 'Send Email'}
        </Button>
      </Stack>
    </Box>
  );
}

const baseTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#00ffff' },
    background: { default: '#000', paper: 'rgba(8, 10, 24, 0.8)' },
  },
  typography: { fontFamily: '"Inter", "Segoe UI", -apple-system, sans-serif' },
});

// ─── Section Quick-Start Prompts ─────────────────────────────────────────────
const SECTION_QUICK_STARTS = {
  tasks: [
    'Break my current project into actionable tasks',
    'What should I prioritize today?',
    'Create a sprint plan for this week',
  ],
  income: [
    'Plan my income streams for this month',
    'Find profitable keywords in my niche',
    'Write an SEO article about [topic]',
    'Create a 30-day content calendar',
    'Generate 5 digital product ideas for my audience',
    'Analyze my income gap and suggest fixes',
  ],
  research: [
    'Research the latest trends in AI',
    'Summarize recent news in tech',
    'Compare top tools for my workflow',
  ],
  documents: [
    'Summarize my recent documents',
    'Extract key points from my files',
    'What topics appear across my docs?',
  ],
  'morning-brief': [
    'What should I focus on today?',
    'Give me a motivational kickoff',
    'What are my priorities this week?',
  ],
};

// ─── Slash Command Definitions ───────────────────────────────────────────────
const SLASH_CMD_LIST = [
  { cmd: '/task',    icon: '📋', desc: 'Quick add a task',          hint: '/task Plan sprint review' },
  { cmd: '/remember',icon: '🧠', desc: 'Save something to memory',  hint: '/remember Standup is 9am' },
  { cmd: '/search',  icon: '🔍', desc: 'Jump to research + search', hint: '/search quantum computing' },
  { cmd: '/focus',   icon: '🎯', desc: 'Start Focus / Pomodoro mode', hint: '/focus' },
  { cmd: '/export',  icon: '📦', desc: 'Export current chat',        hint: '/export' },
  { cmd: '/stats',   icon: '📊', desc: 'View your stats',             hint: '/stats' },
  { cmd: '/tone',    icon: '🎭', desc: 'Set response tone',          hint: '/tone casual | formal | bullet' },
  { cmd: '/code',    icon: '💻', desc: 'Ask for code solution',      hint: '/code sort a list in Python' },
  { cmd: '/remind',  icon: '⏰', desc: 'Set a timed reminder',       hint: '/remind in 30 min to review PR' },
  { cmd: '/star',    icon: '⭐', desc: 'View saved messages',        hint: '/star' },
  { cmd: '/global',  icon: '🌐', desc: 'Search all conversations',   hint: '/global neural networks' },
];

// ─── Vesper Personality Quips ────────────────────────────────────────────────
const VESPER_QUIPS = {
  taskDone:    ['⚡ Task obliterated. You\'re on fire.', '✅ Done deal. Vesper approves.', '💫 Check! Another one bites the dust.', '🔥 That\'s how it\'s done. Boom.', '🎯 Mission accomplished.', '✨ Tick. You magnificent human.'],
  taskAdded:   ['📋 Task locked and loaded.', '⚙️ Added to the matrix. Let\'s get it.', '🎯 New target acquired.', '📌 Pinned. Don\'t ghost it.'],
  memoryAdded: ['🧠 Locked in. Vesper won\'t forget.', '💾 Stored in the vault.', '🗝️ Memory crystal archived.', '✨ Remembered. Forever.'],
  focusStart:  ['🎯 Focus mode: ACTIVATED. No distractions.', '⏲️ 25 minutes. You\'ve got this.', '🔥 All systems focused. Let\'s GO.', '🧠 Deep work mode. Vesper is watching over you.'],
  focusDone:   ['🎉 Work session complete! Take your break.', '✨ Pomodoro CRUSHED. Rest up.', '🔥 Session done. Vesper is proud.', '💫 That\'s 25 focused minutes. Beautiful.'],
  breakDone:   ['🎯 Break over — back to domination.', '⚡ Recharged. Let\'s go again.', '💫 Back in the zone.', '🔥 Break\'s done. Finish what you started.'],
};

// ─── Theme Helpers ─────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  if (!hex || !hex.startsWith('#')) return '0, 255, 255';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

const normalizeVoiceId = (voiceId) => {
  if (!voiceId) return '';
  const trimmed = String(voiceId).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('eleven:') || trimmed.startsWith('edge:')) return trimmed;
  // Backward compatibility: legacy stored raw ElevenLabs IDs.
  if (/^[A-Za-z0-9_-]{16,}$/.test(trimmed)) return `eleven:${trimmed}`;
  return trimmed;
};

// ─── Theme Categories & Massive Theme Catalog ─────────────────────────────
const THEME_CATEGORIES = [
  { id: 'packages', label: '✨ Theme Packages', desc: 'Full immersive visual worlds — textures, fonts, animations & all' },
  { id: 'tech', label: '⚡ Tech & Cyber', desc: 'High-tech neon vibes' },
  { id: 'soft', label: '🌸 Soft & Minimal', desc: 'Clean, gentle aesthetics' },
  { id: 'dark', label: '🖤 Dark & Moody', desc: 'Deep shadows, rich tones' },
  { id: 'nature', label: '🌿 Nature & Landscape', desc: 'Earthy, organic hues' },
  { id: 'season', label: '🍂 Seasons', desc: 'Seasonal color palettes' },
  { id: 'holiday', label: '🎄 Holidays', desc: 'Festive celebrations' },
  { id: 'retro', label: '📼 Retro & Vintage', desc: 'Nostalgic throwbacks' },
  { id: 'cosmic', label: '🌌 Cosmic & Fantasy', desc: 'Otherworldly dreamscapes' },
];

const THEMES = [
  // ── THEME PACKAGES ─ Full immersive visual worlds ──────────────────────────
  { id: 'oak-workshop',    label: '🪵 Oak Workshop',    accent: '#d4a855', glow: '#e8b84b', sub: '#a07828', category: 'packages', bg: 'linear-gradient(160deg, #1c0d04 0%, #2a1508 45%, #1a0b03 100%)', panelBg: 'rgba(58,30,10,0.97)',  sound: 'ambient',  scanlines: false, style: 'wood'     },
  { id: 'iron-forge',      label: '⚙️ Iron Forge',      accent: '#a8c4d8', glow: '#c0d8f0', sub: '#6090b0', category: 'packages', bg: 'linear-gradient(160deg, #080d14 0%, #0d1828 50%, #080d14 100%)', panelBg: 'rgba(18,24,38,0.98)', sound: 'digital',  scanlines: false, style: 'metal'    },
  { id: 'deep-rainforest', label: '🌴 Deep Rainforest', accent: '#34d058', glow: '#22bb44', sub: '#16803a', category: 'packages', bg: 'linear-gradient(160deg, #030d05 0%, #061508 45%, #030b04 100%)', panelBg: 'rgba(10,32,14,0.97)',  sound: 'nature',   scanlines: false, style: 'forest'   },
  { id: 'ocean-abyss',     label: '🌊 Ocean Abyss',     accent: '#38bdf8', glow: '#0ea5e9', sub: '#0369a1', category: 'packages', bg: 'linear-gradient(180deg, #010610 0%, #000b1e 50%, #000814 100%)', panelBg: 'rgba(6,16,40,0.97)',   sound: 'ambient',  scanlines: false, style: 'ocean'    },
  { id: 'volcanic-forge',  label: '🌋 Volcanic Forge',  accent: '#f97316', glow: '#ea580c', sub: '#b91c1c', category: 'packages', bg: 'linear-gradient(160deg, #0d0200 0%, #1a0300 50%, #0d0100 100%)', panelBg: 'rgba(44,14,4,0.98)',   sound: 'dark',     scanlines: false, style: 'volcanic' },
  { id: 'arctic-glass',    label: '🧊 Arctic Glass',    accent: '#bae6fd', glow: '#e0f2fe', sub: '#7dd3fc', category: 'packages', bg: 'linear-gradient(180deg, #030c18 0%, #051020 50%, #030c18 100%)', panelBg: 'rgba(12,24,46,0.88)',  sound: 'ambient',  scanlines: false, style: 'arctic'   },
  { id: 'marble-palace',   label: '🏛️ Marble Palace',   accent: '#e4d5be', glow: '#fff6ea', sub: '#c6a978', category: 'packages', bg: 'radial-gradient(circle at 18% 14%, rgba(255,248,237,0.12), transparent 18%), linear-gradient(155deg, #171412 0%, #2d2722 38%, #131110 100%)', panelBg: 'rgba(38,32,28,0.95)', sound: 'ambient',  scanlines: false, style: 'lux' },
  { id: 'diamond-vault',   label: '💎 Diamond Vault',   accent: '#d8eef8', glow: '#ffffff', sub: '#9bc5d8', category: 'packages', bg: 'radial-gradient(circle at 82% 8%, rgba(218,239,255,0.14), transparent 20%), linear-gradient(165deg, #090b11 0%, #141922 44%, #090c12 100%)', panelBg: 'rgba(13,18,26,0.94)', sound: 'digital',  scanlines: false, style: 'lux' },
  { id: 'stained-glass',   label: '🪟 Stained Glass',   accent: '#ffd88d', glow: '#fff0c7', sub: '#cf9dff', category: 'packages', bg: 'radial-gradient(circle at 50% 0%, rgba(255,240,214,0.12), transparent 18%), linear-gradient(150deg, #160d16 0%, #12182d 46%, #1d120b 100%)', panelBg: 'rgba(26,18,36,0.94)', sound: 'synth',    scanlines: false, style: 'lux' },

  // ── TECH & CYBER ──────────────────────────────────
  { id: 'cyan', label: 'Cyan Matrix', accent: '#00ffff', glow: '#00fff2', sub: '#00ff88', category: 'tech', bg: 'linear-gradient(135deg, #000a0f, #001a1a)', panelBg: 'rgba(0,0,0,0.75)', sound: 'digital', scanlines: true },
  { id: 'green', label: 'Neon Green', accent: '#00ff00', glow: '#00ff00', sub: '#00dd00', category: 'tech', bg: 'linear-gradient(135deg, #000800, #001a00)', panelBg: 'rgba(0,5,0,0.75)', sound: 'digital', scanlines: true },
  { id: 'purple', label: 'Purple Haze', accent: '#c084fc', glow: '#a855f7', sub: '#7c3aed', category: 'tech', bg: 'linear-gradient(135deg, #0a0015, #150025)', panelBg: 'rgba(10,0,20,0.75)', sound: 'synth' },
  { id: 'blue', label: 'Electric Blue', accent: '#5ad7ff', glow: '#4ba3ff', sub: '#3b82f6', category: 'tech', bg: 'linear-gradient(135deg, #000a1a, #001030)', panelBg: 'rgba(0,5,15,0.75)', sound: 'digital' },
  { id: 'pink', label: 'Cyber Pink', accent: '#ff6ad5', glow: '#ff8bd7', sub: '#ff4db8', category: 'tech', bg: 'linear-gradient(135deg, #1a0012, #200018)', panelBg: 'rgba(15,0,10,0.75)', sound: 'synth' },
  { id: 'orange', label: 'Solar Flare', accent: '#ff8800', glow: '#ff9933', sub: '#ff6600', category: 'tech', bg: 'linear-gradient(135deg, #1a0c00, #201000)', panelBg: 'rgba(10,5,0,0.75)', sound: 'digital' },
  { id: 'red', label: 'Blood Moon', accent: '#ff0044', glow: '#ff3366', sub: '#cc0033', category: 'tech', bg: 'linear-gradient(135deg, #1a0008, #200010)', panelBg: 'rgba(15,0,5,0.75)', sound: 'dark' },
  { id: 'lime', label: 'Toxic Waste', accent: '#c0ff00', glow: '#d4ff33', sub: '#a8cc00', category: 'tech', bg: 'linear-gradient(135deg, #0a0d00, #141a00)', panelBg: 'rgba(5,8,0,0.75)', sound: 'digital', scanlines: true },
  { id: 'hacker', label: 'Hacker Terminal', accent: '#33ff33', glow: '#00ff00', sub: '#009900', category: 'tech', bg: '#000000', panelBg: 'rgba(0,0,0,0.9)', sound: 'digital', scanlines: true },
  { id: 'vaporwave', label: 'Vaporwave', accent: '#ff71ce', glow: '#01cdfe', sub: '#b967ff', category: 'tech', bg: 'linear-gradient(180deg, #1a0033, #000033, #330033)', panelBg: 'rgba(10,0,20,0.7)', sound: 'synth' },

  // ── SOFT & MINIMAL ────────────────────────────────
  { id: 'rose', label: 'Rose Quartz', accent: '#f4a4b8', glow: '#f9c4d2', sub: '#e8849c', category: 'soft', bg: 'linear-gradient(135deg, #1a0f12, #1a1015)', panelBg: 'rgba(20,10,14,0.8)', sound: 'ambient', scanlines: false },
  { id: 'lavender', label: 'Lavender Dream', accent: '#b8a9e8', glow: '#d1c4f0', sub: '#9b8bd4', category: 'soft', bg: 'linear-gradient(135deg, #100d18, #15102a)', panelBg: 'rgba(12,8,20,0.8)', sound: 'ambient', scanlines: false },
  { id: 'cream', label: 'Warm Cream', accent: '#e8c99b', glow: '#f0dab8', sub: '#d4aa70', category: 'soft', bg: 'linear-gradient(135deg, #18140c, #1a150d)', panelBg: 'rgba(18,14,8,0.8)', sound: 'ambient', scanlines: false },
  { id: 'sage', label: 'Sage Mist', accent: '#8fb89a', glow: '#a8d0b3', sub: '#6a9c78', category: 'soft', bg: 'linear-gradient(135deg, #0c140e, #0e180f)', panelBg: 'rgba(8,14,10,0.8)', sound: 'nature', scanlines: false },
  { id: 'peach', label: 'Peach Blossom', accent: '#ffb399', glow: '#ffc8b3', sub: '#ff9977', category: 'soft', bg: 'linear-gradient(135deg, #1a120c, #1e1410)', panelBg: 'rgba(16,10,8,0.8)', sound: 'ambient', scanlines: false },
  { id: 'cloud', label: 'Cloud Nine', accent: '#b8d4e3', glow: '#d0e8f5', sub: '#90b8d0', category: 'soft', bg: 'linear-gradient(135deg, #0c1218, #101820)', panelBg: 'rgba(8,12,18,0.8)', sound: 'ambient', scanlines: false },
  { id: 'blush', label: 'Blush Pink', accent: '#e89bb0', glow: '#f0b8c8', sub: '#d47890', category: 'soft', bg: 'linear-gradient(135deg, #180c10, #1c1014)', panelBg: 'rgba(16,8,12,0.8)', sound: 'ambient', scanlines: false },

  // ── DARK & MOODY ──────────────────────────────────
  { id: 'gold', label: 'Golden Hour', accent: '#ffd700', glow: '#ffed4e', sub: '#ffb700', category: 'dark', bg: 'linear-gradient(135deg, #0d0a00, #1a1400)', panelBg: 'rgba(8,6,0,0.8)', sound: 'ambient' },
  { id: 'ice', label: 'Arctic Frost', accent: '#e0f7ff', glow: '#b3e5fc', sub: '#81d4fa', category: 'dark', bg: 'linear-gradient(135deg, #060a0f, #0a1018)', panelBg: 'rgba(4,8,12,0.85)', sound: 'wind' },
  { id: 'teal', label: 'Deep Ocean', accent: '#00d9ff', glow: '#00bcd4', sub: '#0097a7', category: 'dark', bg: 'linear-gradient(135deg, #000d12, #001418)', panelBg: 'rgba(0,8,12,0.85)', sound: 'ocean' },
  { id: 'violet', label: 'Midnight Violet', accent: '#9d00ff', glow: '#b24bf3', sub: '#7b00cc', category: 'dark', bg: 'linear-gradient(135deg, #0a0018, #120024)', panelBg: 'rgba(8,0,16,0.85)', sound: 'dark' },
  { id: 'obsidian', label: 'Obsidian', accent: '#888888', glow: '#aaaaaa', sub: '#666666', category: 'dark', bg: '#050505', panelBg: 'rgba(8,8,8,0.9)', sound: 'dark', scanlines: false },
  { id: 'ember', label: 'Dying Ember', accent: '#ff4400', glow: '#ff6633', sub: '#cc3300', category: 'dark', bg: 'linear-gradient(135deg, #0d0200, #1a0500)', panelBg: 'rgba(10,2,0,0.85)', sound: 'fire' },
  { id: 'abyss', label: 'Abyss', accent: '#4466ff', glow: '#6688ff', sub: '#2244cc', category: 'dark', bg: 'linear-gradient(135deg, #000008, #000012)', panelBg: 'rgba(0,0,8,0.9)', sound: 'dark' },
  { id: 'noir', label: 'Film Noir', accent: '#d4c5a0', glow: '#e8dcc0', sub: '#b8a880', category: 'dark', bg: '#080808', panelBg: 'rgba(6,6,6,0.9)', sound: 'jazz', scanlines: false },

  // ── NATURE & LANDSCAPE ────────────────────────────
  { id: 'forest', label: 'Forest Canopy', accent: '#4caf50', glow: '#66cc6a', sub: '#388e3c', category: 'nature', bg: 'linear-gradient(180deg, #020d02, #041a04, #021002)', panelBg: 'rgba(2,10,2,0.8)', sound: 'forest', scanlines: false },
  { id: 'ocean', label: 'Pacific Depths', accent: '#0288d1', glow: '#29b6f6', sub: '#0277bd', category: 'nature', bg: 'linear-gradient(180deg, #000a14, #001828, #000a14)', panelBg: 'rgba(0,6,14,0.8)', sound: 'ocean', scanlines: false },
  { id: 'desert', label: 'Desert Sand', accent: '#e6a855', glow: '#f0c478', sub: '#cc8833', category: 'nature', bg: 'linear-gradient(180deg, #1a1000, #201808, #1a1000)', panelBg: 'rgba(16,10,0,0.8)', sound: 'wind', scanlines: false },
  { id: 'aurora', label: 'Northern Lights', accent: '#00e676', glow: '#69f0ae', sub: '#00c853', category: 'nature', bg: 'linear-gradient(180deg, #000510, #001020, #050018)', panelBg: 'rgba(0,4,10,0.75)', sound: 'wind', scanlines: false },
  { id: 'volcano', label: 'Volcanic', accent: '#ff5722', glow: '#ff7043', sub: '#e64a19', category: 'nature', bg: 'linear-gradient(180deg, #100200, #1a0500, #0d0100)', panelBg: 'rgba(10,2,0,0.85)', sound: 'fire', scanlines: false },
  { id: 'meadow', label: 'Spring Meadow', accent: '#7cb342', glow: '#9ccc65', sub: '#558b2f', category: 'nature', bg: 'linear-gradient(135deg, #040a02, #081004)', panelBg: 'rgba(4,8,2,0.8)', sound: 'nature', scanlines: false },
  { id: 'mountain', label: 'Mountain Peak', accent: '#90a4ae', glow: '#b0bec5', sub: '#607d8b', category: 'nature', bg: 'linear-gradient(180deg, #060810, #0a0e16, #060810)', panelBg: 'rgba(6,8,12,0.85)', sound: 'wind', scanlines: false },

  // ── SEASONS ───────────────────────────────────────
  { id: 'springbloom', label: 'Spring Bloom', accent: '#ff80ab', glow: '#ff99bb', sub: '#ff4081', category: 'season', bg: 'linear-gradient(135deg, #120810, #180c14)', panelBg: 'rgba(12,6,10,0.8)', sound: 'nature', scanlines: false },
  { id: 'summersky', label: 'Summer Sky', accent: '#00b0ff', glow: '#40c4ff', sub: '#0091ea', category: 'season', bg: 'linear-gradient(180deg, #000814, #001028, #000814)', panelBg: 'rgba(0,6,14,0.75)', sound: 'ocean', scanlines: false },
  { id: 'autumn', label: 'Autumn Harvest', accent: '#ff9800', glow: '#ffb74d', sub: '#f57c00', category: 'season', bg: 'linear-gradient(135deg, #140a00, #1a0e02)', panelBg: 'rgba(14,8,0,0.8)', sound: 'wind', scanlines: false },
  { id: 'winter', label: 'Winter Frost', accent: '#b3e5fc', glow: '#e1f5fe', sub: '#81d4fa', category: 'season', bg: 'linear-gradient(180deg, #080c10, #0e1218, #080c10)', panelBg: 'rgba(6,8,12,0.85)', sound: 'wind', scanlines: false },
  { id: 'monsoon', label: 'Monsoon', accent: '#5c6bc0', glow: '#7986cb', sub: '#3f51b5', category: 'season', bg: 'linear-gradient(180deg, #060810, #0a0e1a, #060810)', panelBg: 'rgba(4,6,12,0.85)', sound: 'rain', scanlines: false },

  // ── HOLIDAYS ──────────────────────────────────────
  { id: 'christmas', label: 'Christmas', accent: '#ff1744', glow: '#ff5252', sub: '#00c853', category: 'holiday', bg: 'linear-gradient(135deg, #0a0000, #000a04)', panelBg: 'rgba(8,2,2,0.85)', sound: 'bells', scanlines: false },
  { id: 'halloween', label: 'Halloween', accent: '#ff9100', glow: '#ffab40', sub: '#9c27b0', category: 'holiday', bg: 'linear-gradient(135deg, #0d0600, #100008)', panelBg: 'rgba(10,4,0,0.85)', sound: 'spooky', scanlines: false },
  { id: 'valentine', label: 'Valentine', accent: '#ff1744', glow: '#ff5252', sub: '#ff80ab', category: 'holiday', bg: 'linear-gradient(135deg, #180008, #200010)', panelBg: 'rgba(16,0,6,0.85)', sound: 'ambient', scanlines: false },
  { id: 'newyear', label: 'New Year\'s Eve', accent: '#ffd740', glow: '#ffe57f', sub: '#ffc400', category: 'holiday', bg: 'linear-gradient(180deg, #080600, #0a0800, #060400)', panelBg: 'rgba(6,4,0,0.85)', sound: 'bells' },
  { id: 'stpatricks', label: 'St. Patrick\'s', accent: '#00e676', glow: '#69f0ae', sub: '#00c853', category: 'holiday', bg: 'linear-gradient(135deg, #001a06, #002a0a)', panelBg: 'rgba(0,12,4,0.85)', sound: 'nature', scanlines: false },
  { id: 'fourthjuly', label: '4th of July', accent: '#ff1744', glow: '#2979ff', sub: '#e0e0e0', category: 'holiday', bg: 'linear-gradient(180deg, #0a0000, #000010, #0a0000)', panelBg: 'rgba(6,0,6,0.85)', sound: 'ambient' },
  { id: 'easter', label: 'Easter', accent: '#ab47bc', glow: '#ce93d8', sub: '#80cbc4', category: 'holiday', bg: 'linear-gradient(135deg, #0e0814, #080e10)', panelBg: 'rgba(10,6,12,0.8)', sound: 'nature', scanlines: false },
  { id: 'thanksgiving', label: 'Thanksgiving', accent: '#d4a056', glow: '#e8b870', sub: '#c08040', category: 'holiday', bg: 'linear-gradient(135deg, #140c04, #1a1008)', panelBg: 'rgba(14,10,4,0.85)', sound: 'ambient', scanlines: false },

  // ── RETRO & VINTAGE ───────────────────────────────
  { id: 'synthwave', label: 'Synthwave', accent: '#ff2a6d', glow: '#ff6b9d', sub: '#05d9e8', category: 'retro', bg: 'linear-gradient(180deg, #0d0021, #1a0035, #0d0021)', panelBg: 'rgba(10,0,18,0.8)', sound: 'synth' },
  { id: 'retrogame', label: 'Retro Arcade', accent: '#ffeb3b', glow: '#fff176', sub: '#f44336', category: 'retro', bg: '#050005', panelBg: 'rgba(5,0,5,0.9)', sound: 'retro', scanlines: true },
  { id: 'terminal', label: 'Amber Terminal', accent: '#ffb000', glow: '#ffc844', sub: '#cc8800', category: 'retro', bg: '#000000', panelBg: 'rgba(0,0,0,0.9)', sound: 'digital', scanlines: true },
  { id: 'crt', label: 'CRT Green', accent: '#33ff33', glow: '#66ff66', sub: '#00cc00', category: 'retro', bg: '#000200', panelBg: 'rgba(0,2,0,0.9)', sound: 'digital', scanlines: true },
  { id: 'sepia', label: 'Sepia Film', accent: '#c8a874', glow: '#d8c098', sub: '#b08850', category: 'retro', bg: 'linear-gradient(135deg, #0e0a04, #140e08)', panelBg: 'rgba(10,8,4,0.85)', sound: 'jazz', scanlines: false },

  // ── COSMIC & FANTASY ──────────────────────────────
  { id: 'nebula', label: 'Nebula', accent: '#e040fb', glow: '#ea80fc', sub: '#aa00ff', category: 'cosmic', bg: 'linear-gradient(135deg, #08001a, #1a0030, #08001a)', panelBg: 'rgba(6,0,14,0.8)', sound: 'cosmic' },
  { id: 'stardust', label: 'Stardust', accent: '#e0e0e0', glow: '#fafafa', sub: '#9e9e9e', category: 'cosmic', bg: 'linear-gradient(180deg, #000004, #020208, #000004)', panelBg: 'rgba(2,2,6,0.85)', sound: 'cosmic', scanlines: false },
  { id: 'galaxy', label: 'Galaxy Core', accent: '#7c4dff', glow: '#b388ff', sub: '#651fff', category: 'cosmic', bg: 'linear-gradient(135deg, #040010, #0a0020, #040010)', panelBg: 'rgba(4,0,10,0.8)', sound: 'cosmic' },
  { id: 'enchanted', label: 'Enchanted Forest', accent: '#69f0ae', glow: '#b9f6ca', sub: '#00e676', category: 'cosmic', bg: 'linear-gradient(180deg, #000a04, #001a0a, #000a04)', panelBg: 'rgba(0,8,4,0.8)', sound: 'forest', scanlines: false },
  { id: 'dragonfire', label: 'Dragon Fire', accent: '#ff6e40', glow: '#ff9e80', sub: '#dd2c00', category: 'cosmic', bg: 'linear-gradient(180deg, #0a0200, #140400, #0a0200)', panelBg: 'rgba(8,2,0,0.85)', sound: 'fire' },
  { id: 'twilight', label: 'Twilight Zone', accent: '#7e57c2', glow: '#b39ddb', sub: '#512da8', category: 'cosmic', bg: 'linear-gradient(180deg, #06040e, #0e0818, #06040e)', panelBg: 'rgba(6,4,10,0.85)', sound: 'dark', scanlines: false },
];

const NAV = [
  { id: 'chat',         label: 'Chat',           icon: HubRounded      },
  { id: 'income',       label: 'Money Ops',      icon: TrendingUpIcon  },
  { id: 'autopilot',    label: 'Autopilot',      icon: AutoModeIcon    },
  { id: 'tasks',        label: 'Task Matrix',    icon: ChecklistRounded },
  { id: 'research',     label: 'Research Tools', icon: ScienceRounded  },
  { id: 'morning-brief',label: 'Morning Brief',  icon: WbSunnyIcon     },
  { id: 'gaps',         label: 'Memory',         icon: NightsStayIcon  },
  { id: 'email',         label: 'Email',          icon: ForwardToInboxIcon },
  { id: 'settings',     label: 'Settings',       icon: SettingsRounded },
];

const UI_RELEASE = 'LUX-OPS R4 · 2026-04-01';

// ── Proactive nudge map: tool name → suggested next actions ──────────────
const NUDGE_MAP = {
  // Creative / income tools
  generate_image:        ['Make a variation', 'Add to wallpaper', 'Build a product listing'],
  write_seo_article:     ['Generate social posts', 'Publish to Medium', 'Repurpose to email'],
  create_ebook:          ['Generate a cover image', 'Create a sales page', 'Publish on Gumroad'],
  keyword_research:      ['Write the article now', 'Build a content calendar', 'Find affiliate angles'],
  plan_income_stream:    ['Build the top stream now', 'Create a content calendar', 'Set a revenue goal'],
  create_content_calendar:['Write the first article', 'Schedule to social', 'Save to Creative Suite'],
  create_email_sequence: ['Set up the automation', 'Write a lead magnet', 'Add a Gumroad product'],
  affiliate_research:    ['Write affiliate content', 'Build a review article', 'Find related keywords'],
  product_idea_generator:['Build the top product', 'Create a sales page', 'Research the market'],
  write_sales_page:      ['Generate product images', 'Build an email sequence', 'Publish on Gumroad'],
  income_gap_analyzer:   ['Fix the biggest gap', 'Create a new income stream', 'Set a 30-day goal'],
  create_landing_page:   ['Connect to Gumroad', 'Write a launch email', 'Build social posts'],
  create_course_outline: ['Write the first module', 'Build a landing page', 'Set a launch date'],
  // General tools
  web_search:            ['Go deeper on this', 'Save as research note', 'Summarize key points'],
  search_web:            ['Go deeper on this', 'Save as research note', 'Summarize key points'],
  create_task:           ['Add subtasks', 'Set a deadline', 'Mark in progress'],
  create_song:           ['Generate album art', 'Push to Creative Suite', 'Write a second verse'],
  execute_python:        ['Explain this code', 'Test edge cases', 'Optimize it'],
  vesper_read_file:      ['Edit this file', 'Summarize the key sections', 'Search for related files'],
  vesper_write_file:     ['Run git diff', 'Commit this change', 'Test the changes'],
  scrape_url:            ['Extract key data', 'Save to research notes', 'Find competitor data'],
};

// ── Voice personality per theme sound type ─────────────────────────────────
const SOUND_VOICE_PARAMS = {
  digital:  { stability: 0.60, similarity_boost: 0.75 },
  synth:    { stability: 0.42, similarity_boost: 0.62 },
  ambient:  { stability: 0.72, similarity_boost: 0.80 },
  dark:     { stability: 0.32, similarity_boost: 0.54 },
  nature:   { stability: 0.65, similarity_boost: 0.70 },
  jazz:     { stability: 0.28, similarity_boost: 0.48 },
  forest:   { stability: 0.68, similarity_boost: 0.72 },
  ocean:    { stability: 0.70, similarity_boost: 0.76 },
  wind:     { stability: 0.55, similarity_boost: 0.64 },
  fire:     { stability: 0.38, similarity_boost: 0.56 },
  cosmic:   { stability: 0.45, similarity_boost: 0.60 },
  rain:     { stability: 0.60, similarity_boost: 0.68 },
  bells:    { stability: 0.55, similarity_boost: 0.70 },
  retro:    { stability: 0.50, similarity_boost: 0.65 },
  spooky:   { stability: 0.30, similarity_boost: 0.50 },
};

// ─── Voice Persona Assigner Component ──────────────────────────────────
function PersonaAssigner({ apiBase, cloudVoices, setToast, playVoicePreview, onSave }) {
  const [personas, setPersonas] = React.useState(null);
  const [saving, setSaving] = React.useState('');
  const [personasError, setPersonasError] = React.useState(false);

  const loadPersonas = React.useCallback(async () => {
    setPersonas(null);
    setPersonasError(false);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const r = await fetch(`${apiBase}/api/voice/personas`);
        const d = await r.json();
        setPersonas(d.personas || {});
        return;
      } catch (e) {
        if (attempt < 3) await new Promise(r => setTimeout(r, 2000 * attempt));
      }
    }
    setPersonasError(true);
  }, [apiBase]);

  React.useEffect(() => { loadPersonas(); }, [apiBase]);

  const assignVoice = async (personaId, voiceId) => {
    setSaving(personaId);
    try {
      const res = await fetch(`${apiBase}/api/voice/personas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona_id: personaId, voice_id: voiceId }),
      });
      const data = await res.json();
      if (data.success) {
        setPersonas(prev => ({ ...prev, [personaId]: { ...prev[personaId], voice_id: voiceId } }));
        const voiceName = voiceId ? (cloudVoices.find(v => v.id === voiceId)?.name || voiceId) : 'Default';
        setToast(`✅ ${personas[personaId]?.label}: ${voiceName}`);
        if (onSave) onSave(); // refresh App-level persona cache
      } else {
        setToast('Failed: ' + (data.error || 'Unknown'));
      }
    } catch (e) { setToast('Error: ' + e.message); }
    setSaving('');
  };

  if (personasError) return (
    <Box>
      <Typography variant="caption" sx={{ color: 'rgba(255,100,100,0.7)' }}>⚠️ Could not load personas</Typography>
      <Box onClick={loadPersonas} sx={{ mt: 0.5, cursor: 'pointer', color: 'var(--accent)', fontSize: '0.7rem', textDecoration: 'underline' }}>↻ Retry</Box>
    </Box>
  );
  if (!personas) return <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>⏳ Loading personas...</Typography>;

  return (
    <Stack spacing={1}>
      {Object.entries(personas).map(([id, p]) => {
        const currentVoice = cloudVoices.find(v => v.id === p.voice_id);
        return (
          <Box key={id} sx={{
            p: 1.5, borderRadius: 2,
            border: p.voice_id ? '1px solid rgba(0,255,136,0.3)' : '1px solid rgba(255,255,255,0.1)',
            bgcolor: p.voice_id ? 'rgba(0,255,136,0.04)' : 'rgba(255,255,255,0.02)',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: p.voice_id ? '#00ff88' : 'rgba(255,255,255,0.7)' }}>
                {p.icon} {p.label}
              </Typography>
              {saving === id && <CircularProgress size={14} sx={{ color: 'var(--accent)' }} />}
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
              {p.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <select
                value={p.voice_id || ''}
                onChange={(e) => assignVoice(id, e.target.value)}
                style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(0,255,255,0.3)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  color: currentVoice ? '#00ff88' : 'rgba(255,255,255,0.5)',
                  fontSize: '0.8rem',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="" style={{ background: '#111', color: '#999' }}>Default (uses main voice)</option>
                {cloudVoices.map(v => (
                  <option key={v.id} value={v.id} style={{ background: '#111', color: '#ffbb44' }}>
                    {v.name} — {v.locale || 'American'} • {v.style || 'neural'}
                  </option>
                ))}
              </select>
              {currentVoice?.preview_url && playVoicePreview && (
                <button
                  onClick={() => playVoicePreview(currentVoice.preview_url)}
                  title="Preview voice"
                  style={{
                    background: 'rgba(0,255,255,0.1)',
                    border: '1px solid rgba(0,255,255,0.3)',
                    borderRadius: 6, padding: '4px 8px',
                    color: 'var(--accent)', cursor: 'pointer', fontSize: '0.9rem',
                  }}
                >▶</button>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

// ─── Product Launch Card ──────────────────────────────────────────────────────
// Rendered when Vesper runs launch_product. Shows a styled product card with
// a live Stripe buy button, doc link, copy URL, and first-sale watcher.
function ProductLaunchCard({ message }) {
  const BACKEND = import.meta.env.VITE_BACKEND_URL || 'https://vesper-backend-production-b486.up.railway.app';
  const [copied, setCopied] = React.useState(false);
  const [watching, setWatching] = React.useState(false);
  const [saleFound, setSaleFound] = React.useState(null);
  const [pollCount, setPollCount] = React.useState(0);
  const pollRef = React.useRef(null);

  const paymentUrl = message.payment_url || '';
  const docUrl = message.doc_url || '';
  const productName = message.product_name || 'Your Product';
  const price = message.price || '';
  const billing = message.billing || 'one_time';
  const salesCopy = message.sales_copy || '';
  const launchedAt = message.launched_at || '';

  const billingLabel = billing === 'monthly' ? '/mo' : billing === 'yearly' ? '/yr' : '';

  function handleCopy() {
    if (!paymentUrl) return;
    navigator.clipboard.writeText(paymentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function startWatching() {
    if (watching || saleFound) return;
    setWatching(true);
    setPollCount(0);
    pollRef.current = setInterval(async () => {
      try {
        const params = new URLSearchParams({ after: launchedAt });
        if (message.link_id) params.set('link_id', message.link_id);
        const res = await fetch(`${BACKEND}/api/stripe/first-sale?${params}`);
        const data = await res.json();
        if (data.found && data.payment) {
          setSaleFound(data.payment);
          setWatching(false);
          clearInterval(pollRef.current);
        } else {
          setPollCount(c => c + 1);
        }
      } catch (_e) {
        setPollCount(c => c + 1);
      }
    }, 3000);
    // Auto-stop after 10 minutes
    setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        setWatching(false);
      }
    }, 600000);
  }

  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const accentGreen = '#00e676';
  const accentCyan = 'var(--accent, #00ffff)';

  if (saleFound) {
    return (
      <Box sx={{
        minWidth: 300, maxWidth: 480, borderRadius: 3,
        border: `2px solid ${accentGreen}`,
        bgcolor: 'rgba(0,230,118,0.08)',
        backdropFilter: 'blur(12px)',
        p: 2.5, mb: 2,
        boxShadow: `0 0 30px ${accentGreen}44`,
        animation: 'pulseGreen 1.5s ease-in-out 3',
      }}>
        <Typography variant="caption" sx={{ color: accentGreen, fontFamily: 'monospace', letterSpacing: 1, fontSize: '0.65rem' }}>
          VESPER · FIRST SALE DETECTED
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mt: 0.5, mb: 0.5 }}>
          Someone just bought {productName}!
        </Typography>
        <Typography sx={{ color: accentGreen, fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
          {saleFound.amount} {saleFound.currency}
        </Typography>
        {saleFound.customer_email && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
            Customer: {saleFound.customer_email}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
          {saleFound.created}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minWidth: 300, maxWidth: 480, borderRadius: 3,
      border: `1px solid ${accentCyan}44`,
      bgcolor: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(14px)',
      p: 2.5, mb: 2,
      boxShadow: `0 0 20px ${accentCyan}22`,
    }}>
      {/* Header */}
      <Typography variant="caption" sx={{ color: accentCyan, fontFamily: 'monospace', letterSpacing: 1, fontSize: '0.65rem' }}>
        VESPER · PRODUCT LAUNCHED
      </Typography>

      {/* Name + Price row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, flexGrow: 1 }}>
          {productName}
        </Typography>
        {price && (
          <Box sx={{
            px: 1.5, py: 0.4, borderRadius: 99,
            bgcolor: `${accentGreen}22`,
            border: `1px solid ${accentGreen}66`,
          }}>
            <Typography sx={{ color: accentGreen, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
              ${price}{billingLabel}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sales copy preview */}
      {salesCopy && (
        <Typography variant="body2" sx={{
          color: 'rgba(255,255,255,0.65)',
          mb: 2,
          fontSize: '0.8rem',
          lineHeight: 1.55,
          borderLeft: `2px solid ${accentCyan}44`,
          pl: 1.5,
        }}>
          {salesCopy.length > 200 ? salesCopy.slice(0, 200) + '…' : salesCopy}
        </Typography>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        {paymentUrl && (
          <Box
            component="a"
            href={paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              px: 2, py: 0.9, borderRadius: 2,
              bgcolor: accentGreen,
              color: '#000',
              fontWeight: 700, fontSize: '0.85rem',
              textDecoration: 'none',
              transition: 'opacity 0.15s',
              '&:hover': { opacity: 0.85 },
            }}
          >
            Buy Now
          </Box>
        )}
        {docUrl && (
          <Box
            component="a"
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              px: 2, py: 0.9, borderRadius: 2,
              border: `1px solid ${accentCyan}66`,
              color: accentCyan,
              fontWeight: 600, fontSize: '0.85rem',
              textDecoration: 'none',
              bgcolor: 'transparent',
              transition: 'background 0.15s',
              '&:hover': { bgcolor: `${accentCyan}11` },
            }}
          >
            Sales Doc
          </Box>
        )}
        {paymentUrl && (
          <Box
            component="button"
            onClick={handleCopy}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              px: 2, py: 0.9, borderRadius: 2,
              border: `1px solid rgba(255,255,255,0.2)`,
              color: copied ? accentGreen : 'rgba(255,255,255,0.65)',
              fontWeight: 600, fontSize: '0.85rem',
              background: 'transparent', cursor: 'pointer',
              transition: 'color 0.2s',
              '&:hover': { borderColor: 'rgba(255,255,255,0.4)' },
            }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </Box>
        )}
      </Box>

      {/* Watch for first sale */}
      {paymentUrl && !saleFound && (
        <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {!watching ? (
            <Box
              component="button"
              onClick={startWatching}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                px: 1.5, py: 0.6, borderRadius: 2,
                border: `1px solid rgba(255,255,255,0.15)`,
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 500, fontSize: '0.75rem',
                background: 'transparent', cursor: 'pointer',
                '&:hover': { color: accentCyan, borderColor: accentCyan },
              }}
            >
              Watch for first sale
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: accentCyan,
                animation: 'pulseDot 1.2s ease-in-out infinite',
              }} />
              <Typography variant="caption" sx={{ color: accentCyan, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                Watching for payment... ({pollCount * 3}s)
              </Typography>
              <Box
                component="button"
                onClick={() => { setWatching(false); clearInterval(pollRef.current); }}
                sx={{ ml: 'auto', color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem' }}
              >
                stop
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Errors */}
      {message.errors && message.errors.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,100,100,0.2)' }}>
          {message.errors.map((e, i) => (
            <Typography key={i} variant="caption" sx={{ color: '#ff6b6b', fontSize: '0.7rem', display: 'block' }}>
              {e}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [userId, setUserId] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [_metaPersonOpen, setMetaPersonOpen] = useState(false);
  const safeStorageGet = (key, fallback) => {
    if (typeof window === 'undefined') return fallback;
    try {
      return window.localStorage.getItem(key) || fallback;
    } catch (error) {
      console.warn('Storage read failed', error);
      return fallback;
    }
  };

  const [gameMode, setGameMode] = useState(false);
  const [activeSection, setActiveSection] = useState('chat');
  const [pendingCreationsPanel, setPendingCreationsPanel] = useState(false);
  const [activeTheme, setActiveTheme] = useState(() => {
    const luxuryDefault = THEMES.find((t) => t.id === 'diamond-vault') || THEMES[0];
    const migrated = safeStorageGet('vesper_luxury_migrated_v3', '0') === '1';
    if (!migrated) return luxuryDefault;
    const storedId = safeStorageGet('vesper_theme', luxuryDefault.id);
    return THEMES.find((t) => t.id === storedId) || luxuryDefault;
  });
  const [researchItems, setResearchItems] = useState([]);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchForm, setResearchForm] = useState({ title: '', summary: '' });
  const [memoryItems, setMemoryItems] = useState([]);
  const [memoryCategory, setMemoryCategory] = useState(() => safeStorageGet('vesper_memory_category', 'notes'));
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryText, setMemoryText] = useState('');
  const [memoryView, setMemoryView] = useState('history'); // 'history', 'notes', or 'search'
  const [memorySearchQuery, setMemorySearchQuery] = useState('');
  const [memorySearchResults, setMemorySearchResults] = useState([]);
  const [memorySearchLoading, setMemorySearchLoading] = useState(false);
  // Email panel state
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]); // Array of IDs for bulk operations
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Controls the confirmation dialog
  const [deleteTargetId, setDeleteTargetId] = useState(null); // 'bulk' or specific ID when confirm dialog is open
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsDialogOpen, setThreadsDialogOpen] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState(() => safeStorageGet('vesper_last_thread_id', null));
  const [currentThreadTitle, setCurrentThreadTitle] = useState('');
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editingThreadTitle, setEditingThreadTitle] = useState('');
  const [threadSearchQuery, setThreadSearchQuery] = useState('');
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportThreadData, setExportThreadData] = useState(null);
  const [threadMenuAnchor, setThreadMenuAnchor] = useState(null);
  const [threadMenuThread, setThreadMenuThread] = useState(null);
  const [memoryMenuAnchor, setMemoryMenuAnchor] = useState(null);
  const [memoryMenuItem, setMemoryMenuItem] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', status: 'inbox', priority: 'medium', dueDate: '' });
  const [toast, setToast] = useState('');
  const [toastVariant, setToastVariant] = useState('default'); // 'default' | 'success' | 'celebrate' | 'warn'
  const [voiceErrorToast, setVoiceErrorToast] = useState('');
  // Stats & Insights
  const [vesperStats, setVesperStats] = useState(() => {
    try {
      const s = localStorage.getItem('vesper_stats');
      return s ? JSON.parse(s) : { messages: 0, tasksCompleted: 0, memories: 0, daysActive: 0, lastActive: null, streak: 0 };
    } catch { return { messages: 0, tasksCompleted: 0, memories: 0, daysActive: 0, lastActive: null, streak: 0 }; }
  });
  // Focus Mode
  const [focusMode, setFocusMode] = useState(false);
  const [focusTask, setFocusTask] = useState(null); // task object | null
  const [focusTimeLeft, setFocusTimeLeft] = useState(25 * 60); // seconds
  const [focusPhase, setFocusPhase] = useState('work'); // 'work' | 'break'
  const [focusRunning, setFocusRunning] = useState(false);
  // Slash commands
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashIdx, setSlashIdx] = useState(0);
  // Keyboard shortcuts modal
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Command palette (Ctrl+K / Cmd+K)
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState('');
  const [cmdFocusIdx, setCmdFocusIdx] = useState(0);
  // 3D bubble tilt
  const [bubbleTilt, setBubbleTilt] = useState(null);
  // Message reactions
  const [messageReactions, setMessageReactions] = useState({});
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  // In-chat search (Ctrl+F)
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchIdx, setChatSearchIdx] = useState(0);
  // Send burst
  const [sendBurst, setSendBurst] = useState(false);
  // Edit user messages in-place
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingMsgContent, setEditingMsgContent] = useState('');
  // Pinned messages (per-message, persisted)
  const [pinnedMsgIds, setPinnedMsgIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('vesper_pinned_msgs') || '{}'); } catch { return {}; }
  });
  // Starred / saved messages (persisted)
  const [starredMsgs, setStarredMsgs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('vesper_starred_msgs') || '[]'); } catch { return []; }
  });
  const [starredPanelOpen, setStarredPanelOpen] = useState(false);
  // Global cross-thread search
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() => safeStorageGet('vesper_tts_enabled', 'true') === 'true');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoiceName, setSelectedVoiceName] = useState(() => normalizeVoiceId(safeStorageGet('vesper_tts_voice', 'eleven:pFZP5JQG7iQjIQuC4Bku')));
  const [voiceStability, setVoiceStability] = useState(() => parseFloat(safeStorageGet('vesper_voice_stability', '0.5')));
  const [voiceSimilarityBoost, setVoiceSimilarityBoost] = useState(() => parseFloat(safeStorageGet('vesper_voice_similarity', '0.75')));
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [runtimeCapabilities, setRuntimeCapabilities] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => safeStorageGet('vesper_sound_enabled', 'true') === 'true');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const migrated = window.localStorage.getItem('vesper_luxury_migrated_v3') === '1';
      if (!migrated) {
        const luxuryDefault = THEMES.find((t) => t.id === 'diamond-vault') || THEMES[0];
        const existingThemeId = window.localStorage.getItem('vesper_theme');
        const existingTheme = THEMES.find((t) => t.id === existingThemeId);
        const themeToKeep = existingTheme || luxuryDefault;
        setActiveTheme(themeToKeep);
        window.localStorage.setItem('vesper_theme', themeToKeep.id);
        window.localStorage.setItem('vesper_luxury_migrated_v3', '1');
      }
    } catch (error) {
      console.warn('Luxury migration failed', error);
    }
  }, []);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => safeStorageGet('vesper_sidebar_collapsed', 'false') === 'true');
  const [integrationInitialTab, setIntegrationInitialTab] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [uiScale, setUiScale] = useState(() => parseFloat(safeStorageGet('vesper_ui_scale', '1')));
  const [showSystemStatus, setShowSystemStatus] = useState(true);
  const [launchMode, setLaunchMode] = useState(() => safeStorageGet('vesper_launch_mode', 'true') === 'true');

  const sortedThreads = useMemo(() => {
    return [...threads].sort((a, b) => {
      if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });
  }, [threads]);

  const visibleThreads = useMemo(() => {
    const query = threadSearchQuery.trim().toLowerCase();
    if (!query) return sortedThreads;
    return sortedThreads.filter((thread) => (thread.title || '').toLowerCase().includes(query));
  }, [sortedThreads, threadSearchQuery]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('vesper_launch_mode', launchMode ? 'true' : 'false');
    } catch (error) {
      console.warn('Launch mode persistence failed', error);
    }
  }, [launchMode]);

  // ── Voice personality shifts with theme "sound" type ────────────────────
  useEffect(() => {
    const profile = SOUND_VOICE_PARAMS[activeTheme?.sound] || SOUND_VOICE_PARAMS.ambient;
    setVoiceStability(profile.stability);
    setVoiceSimilarityBoost(profile.similarity_boost);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTheme?.id]);

  // Restore draft input for the current thread
  useEffect(() => {
    const draft = safeStorageGet(`vesper_draft_${currentThreadId || 'new'}`, '');
    if (draft) setInput(draft);
  }, [currentThreadId]);

  // ── New features: Model Picker, Auto-speak, Export ─────────────
  const [selectedModel, setSelectedModel] = useState(() => {
    const stored = safeStorageGet('vesper_model', 'auto');
    // Legacy values like 'anthropic', 'openai', 'gemini' are provider names not model IDs — reset them
    const legacyProviderNames = ['anthropic', 'openai', 'gemini', 'claude', 'gpt', 'ollama'];
    if (legacyProviderNames.includes(stored)) {
      try { localStorage.setItem('vesper_model', 'auto'); } catch(ex) {}
      return 'auto';
    }
    return stored;
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [autoSpeak, setAutoSpeak] = useState(() => safeStorageGet('vesper_auto_speak', 'true') === 'true');
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState('');
  const [activeToolName, setActiveToolName] = useState(null);
  const [activeToolLabel, setActiveToolLabel] = useState('');
  const [toolHistory, setToolHistory] = useState([]);
  const previewAudioRef = useRef(null);

  // ── Wow Factors: mood, nudges, briefing, XP ──────────────────────────────
  const [vesperMood, setVesperMood] = useState('neutral');
  const [pendingNudges, setPendingNudges] = useState([]);
  const [sessionBriefing, setSessionBriefing] = useState(null);
  const [vesperXp, setVesperXp] = useState(() => { try { return JSON.parse(localStorage.getItem('vesper_xp_cache') || '{"xp":0,"level":1}'); } catch { return {xp:0, level:1}; } });
  const [incomeStats, setIncomeStats] = useState(() => { try { return JSON.parse(localStorage.getItem('vesper_income_cache') || 'null'); } catch { return null; } });

  // ── Autopilot state ──────────────────────────────────────────────────────
  const [autopilotJobs, setAutopilotJobs] = useState([]);
  const [autopilotLog, setAutopilotLog] = useState([]);
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const [autopilotRunning, setAutopilotRunning] = useState({}); // { job_id: true }
  const [autopilotNiches, setAutopilotNiches] = useState({}); // { job_id: niche string }

  // ── Vesper Autonomy: Daily Identity + Proactive Initiative ─────
  const [vesperIdentity, setVesperIdentity] = useState(null);
  const [vesperGreeting, setVesperGreeting] = useState('');
  const [vesperInitiatives, setVesperInitiatives] = useState([]);
  const [identityOptions, setIdentityOptions] = useState(null);

  // 3D Avatar System
  const [avatarStudioOpen, setAvatarStudioOpen] = useState(false);
  const [activeAvatarData, setActiveAvatarData] = useState(null);

  // Video Avatar System
  const [videoAvatarUrl, setVideoAvatarUrl] = useState('');
  const [videogenLoading, setVideoGenLoading] = useState(false);
  const [videoShouldAutoplay, setVideoShouldAutoplay] = useState(false);
    const [idleAnimating, setIdleAnimating] = useState(false); // true = play full idle gesture video
    const avatarVideoRef = useRef(null);
  const sidebarRef = useRef(null);
  const [drawerFrame, setDrawerFrame] = useState({ left: 300, top: 12, height: 820 });

  // Compute drawer attachment point from known CSS layout constants — always in sync
  // with sidebar state without relying on DOM measurement timing.
  // Values mirror: app-shell padding (20px) + sidebar CSS widths (262/56px).
  const drawerLeft = sidebarCollapsed ? 76 : 282;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateDrawerFrame = () => {
      const el = sidebarRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const computedLeft = Math.round(rect.right);
      const computedTop = Math.max(0, Math.round(rect.top));
      const computedHeight = Math.max(360, Math.round(rect.height));

      setDrawerFrame((prev) => {
        if (
          prev.left === computedLeft &&
          prev.top === computedTop &&
          prev.height === computedHeight
        ) {
          return prev;
        }
        return { left: computedLeft, top: computedTop, height: computedHeight };
      });
    };

    updateDrawerFrame();
    window.addEventListener('resize', updateDrawerFrame);
    window.addEventListener('scroll', updateDrawerFrame, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && sidebarRef.current) {
      resizeObserver = new ResizeObserver(updateDrawerFrame);
      resizeObserver.observe(sidebarRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDrawerFrame);
      window.removeEventListener('scroll', updateDrawerFrame, true);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [launchMode, uiScale, activeTheme.id, sidebarCollapsed]);

  // Background Studio
  const [backgroundStudioOpen, setBackgroundStudioOpen] = useState(false);
  const [customBackground, setCustomBackground] = useState(() => {
    try { const s = localStorage.getItem('vesper_custom_bg'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [backgroundGallery, setBackgroundGallery] = useState([]);
  const [backgroundSettings, setBackgroundSettings] = useState(() => {
    try { const s = localStorage.getItem('vesper_bg_settings'); return s ? JSON.parse(s) : { opacity: 0.3, blur: 0, overlay: true }; } catch { return { opacity: 0.3, blur: 0, overlay: true }; }
  });

  // Tools
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [canvasAppCode, setCanvasAppCode] = useState(`import React, { useState } from "react";
import { Button, Container } from "react-bootstrap";
import { MoveRight } from "lucide-react";

export default function App() {
  const [count, setCount] = useState(0);
  return (
    <Container className="p-5 text-white bg-dark min-vh-100 d-flex flex-column align-items-center justify-content-center">
      <h1 className="mb-4 display-4 fw-bold">Built with Vesper</h1>
      <p className="lead mb-4">I can build real React apps for you.</p>
      <Button variant="info" size="lg" onClick={() => setCount(c => c + 1)}>
        Count is {count}
      </Button>
    </Container>
  );
}`);
  const [canvasActiveTab, setCanvasActiveTab] = useState(0); // 0=Drawing, 1=AppBuilder
  const [researchOpen, setResearchOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const [learningOpen, setLearningOpen] = useState(false);
  const [voiceLabOpen, setVoiceLabOpen] = useState(false);
  const [voiceLabTab, setVoiceLabTab] = useState('sfx');
  const TOOLS = [
    { id: 'research', label: 'Deep Research', icon: '🔬' },
    { id: 'graph', label: 'Knowledge Graph', icon: '🕸️' },
    { id: 'videos', label: 'Create videos', icon: '🎬' },
    { id: 'images', label: 'Create images', icon: '🎨' },
    { id: 'canvas', label: 'Canvas', icon: '📐' },
    { id: 'learning', label: 'Guided Learning', icon: '📚' },
    { id: 'enterWorld', label: 'Enter World', icon: '🏰' },
    { id: 'sfx', label: 'Sound Effects', icon: '🔊' },
    { id: 'voiceClone', label: 'Voice Clone', icon: '🎙️' },
    { id: 'voiceIsolate', label: 'Voice Isolate', icon: '🎛️' },
    { id: 'voicePersonas', label: 'Voice Personas', icon: '🎭' },
    { id: 'newChat', label: 'New Chat', icon: '💬' },
    { id: 'clearHistory', label: 'Clear History', icon: '🗑️' },
    { id: 'mindmap', label: 'Mind Map', icon: '🧠', description: 'Explore your research visually' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const FEATURED_TOOL_IDS = [
    'research',
    'graph',
    'videos',
    'images',
    'canvas',
    'voicePersonas',
    'newChat',
    'clearHistory',
    'settings',
  ];

  const featuredTools = FEATURED_TOOL_IDS
    .map((id) => TOOLS.find((tool) => tool.id === id))
    .filter(Boolean);
  
  // Smart Memory Tags
  const [memoryTags, setMemoryTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newMemoryTags, setNewMemoryTags] = useState('');

  // PWA Install prompt
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      // Only show banner if user hasn't dismissed it
      if (!localStorage.getItem('vesper_install_dismissed')) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Hide banner if already installed
    window.addEventListener('appinstalled', () => {
      setShowInstallBanner(false);
      setInstallPromptEvent(null);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  
  // PDF & Document Upload
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [documentTags, setDocumentTags] = useState('');
  
  // Analytics Dashboard
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // Personality Customization
  const [personality, setPersonality] = useState(null);
  const [personalities, setPersonalities] = useState([]);
  const [personalityLoading, setPersonalityLoading] = useState(false);
  const [personalityForm, setPersonalityForm] = useState({ name: '', systemPrompt: '', tone: '', responseStyle: '' });
  
  // Enhanced Research
  const [researchSearch, setResearchSearch] = useState('');
  const [researchSearchResults, setResearchSearchResults] = useState([]);
  const [researchFilter, setResearchFilter] = useState('all'); // 'all', 'web', 'file', 'manual'
  const [selectedResearchId, setSelectedResearchId] = useState(null);
  const [citationFormat, setCitationFormat] = useState('APA');
  
  // Better Export Options
  const [exportSelection, setExportSelection] = useState({
    memories: true,
    tasks: true,
    research: true,
    documents: true,
    conversations: true,
  });
  
  // Advanced Customization Options
  const [customizations, setCustomizations] = useState({
    sidebarWidth: localStorage.getItem('vesper_sidebar_width') || '300px',
    fontSize: localStorage.getItem('vesper_font_size') || 'medium', // small, medium, large
    compactMode: localStorage.getItem('vesper_compact_mode') === 'true',
    analyticsCharts: localStorage.getItem('vesper_analytics_charts') || 'all', // all, summary, detailed
    memoryCategories: JSON.parse(localStorage.getItem('vesper_memory_categories') || '["notes", "personal", "emotional_bonds", "work", "milestones", "sensory_experiences", "creative_moments"]'),
    researchSources: JSON.parse(localStorage.getItem('vesper_research_sources') || '["web", "file", "manual", "database"]'),
    chatBoxHeight: localStorage.getItem('vesper_chat_box_height') || '55vh', // Resizable chat height
  });
  
  const [newMemoryCategory, setNewMemoryCategory] = useState('');
  const [newResearchSource, setNewResearchSource] = useState('');
    
    // Audio context for UI sounds
  const audioContextRef = useRef(null);
  
  // Initialize Web Audio API (lazy — only created once, never closed until unmount)
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        return null;
      }
    }
    // Resume if suspended (Chrome autoplay policy)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
    return audioContextRef.current;
  }, []);
  
  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // ── Global keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    const NAV_SECTIONS = ['chat', 'research', 'memory', 'tasks', 'settings'];
    const handler = (e) => {
      // Ignore if any input focused (except Escape)
      const tag = document.activeElement?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (e.key === 'Escape') {
        if (cmdOpen) { setCmdOpen(false); return; }
        if (chatSearchOpen) { setChatSearchOpen(false); setChatSearchQuery(''); return; }
        setShortcutsOpen(false);
        setSlashMenuOpen(false);
        setFocusMode(false);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(p => !p);
        setCmdQuery('');
        setCmdFocusIdx(0);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        // Only intercept Ctrl+F when in the chat section
        if (activeSection === 'chat') {
          e.preventDefault();
          setChatSearchOpen(p => !p);
          setChatSearchQuery('');
          setChatSearchIdx(0);
          return;
        }
      }
      if (inInput) return;
      if (e.ctrlKey && !e.shiftKey && !e.altKey) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= 5) { e.preventDefault(); setActiveSection(NAV_SECTIONS[num - 1]); return; }
        if (e.key === '/') { e.preventDefault(); setShortcutsOpen(p => !p); return; }
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'F') { e.preventDefault(); setFocusMode(p => !p); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cmdOpen, chatSearchOpen, activeSection]);

  // Apply theme background to body when theme changes
  useEffect(() => {
    document.body.style.background = activeTheme.bg || '#000';
    // Set data-style on body and html for global CSS targeting
    document.body.dataset.style = activeTheme.style || 'cyber';
    document.body.dataset.themeId = activeTheme.id || 'default';
    document.documentElement.dataset.style = activeTheme.style || 'cyber';
    document.documentElement.dataset.themeId = activeTheme.id || 'default';
    // Also set CSS vars on :root for elements outside app-shell (modals, popovers)
    const root = document.documentElement;
    root.style.setProperty('--accent-rgb', hexToRgb(activeTheme.accent));
    root.style.setProperty('--glow-rgb', hexToRgb(activeTheme.glow));
    root.style.setProperty('--accent', activeTheme.accent);
    root.style.setProperty('--accent-2', activeTheme.sub || activeTheme.accent);
    root.style.setProperty('--glow', activeTheme.glow);
    root.style.setProperty('--panel-bg', activeTheme.panelBg || 'rgba(0,0,0,0.75)');
    root.style.setProperty('--bg', activeTheme.bg || 'linear-gradient(135deg, #000a0f, #001a1a)');
  }, [activeTheme]);
  
  // Draggable board positions - load from localStorage
  const [boardPositions, setBoardPositions] = useState(() => {
    const saved = safeStorageGet('vesper_board_positions', null);
    return saved ? JSON.parse(saved) : {
      research: { x: 50, y: 50 },
      memory: { x: 100, y: 100 },
      tasks: { x: 150, y: 150 },
      settings: { x: 200, y: 200 },
    };
  });

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const sendingRef = useRef(false); // prevents double-send from onKeyDown + onSubmit racing

  // Drag-and-drop sensor setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag (prevents accidental drags)
      },
    })
  );

  // Handle drag end - save new position
  // ── Vesper react helper + stat tracker ──────────────────────────────────
  const showToast = useCallback((msg, variant = 'default') => {
    setToastVariant(variant);
    setToast(msg);
  }, []);

  const showVoiceError = useCallback((msg) => {
    setVoiceErrorToast(msg);
    setToast('⚠️ Voice issue detected. See top alert for details.');
    setToastVariant('warn');
  }, []);

  const vesperReact = useCallback((type, override) => {
    const quips = VESPER_QUIPS[type];
    const msg = override || (quips ? quips[Math.floor(Math.random() * quips.length)] : null);
    if (msg) showToast(msg, type === 'taskDone' ? 'celebrate' : type === 'focusDone' ? 'celebrate' : 'success');
  }, [showToast]);

  const bumpStat = useCallback((key) => {
    setVesperStats(prev => {
      const today = new Date().toDateString();
      const isNewDay = prev.lastActive !== today;
      const updated = {
        ...prev,
        [key]: (prev[key] || 0) + 1,
        lastActive: today,
        daysActive: isNewDay ? (prev.daysActive || 0) + 1 : (prev.daysActive || 0),
        streak: isNewDay ? (prev.streak || 0) + 1 : (prev.streak || 0),
      };
      try { localStorage.setItem('vesper_stats', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  // ── Focus mode timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!focusRunning) return;
    const tick = setInterval(() => {
      setFocusTimeLeft(t => {
        if (t <= 1) {
          clearInterval(tick);
          setFocusRunning(false);
          if (focusPhase === 'work') {
            bumpStat('tasksCompleted');
            vesperReact('focusDone');
            setFocusPhase('break');
            setFocusTimeLeft(5 * 60);
          } else {
            vesperReact('breakDone');
            setFocusPhase('work');
            setFocusTimeLeft(25 * 60);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [focusRunning, focusPhase, bumpStat, vesperReact]);

  const exportTasks = useCallback(async () => {
    const done  = tasks.filter(t => t.status === 'done');
    const doing = tasks.filter(t => t.status === 'doing');
    const inbox = tasks.filter(t => t.status === 'inbox');
    let md = `# Vesper Task Export\n_${new Date().toLocaleString()}_\n\n`;
    if (doing.length) { md += `## 🔥 In Progress (${doing.length})\n`; doing.forEach(t => { md += `- [ ] **${t.title}**\n`; }); md += '\n'; }
    if (inbox.length) { md += `## 📥 Inbox (${inbox.length})\n`; inbox.forEach(t => { md += `- [ ] ${t.title}\n`; }); md += '\n'; }
    if (done.length)  { md += `## ✅ Done (${done.length})\n`;  done.forEach(t  => { md += `- [x] ${t.title}\n`; }); }
    try {
      await navigator.clipboard.writeText(md);
      showToast(`📦 ${tasks.length} tasks copied to clipboard!`, 'success');
    } catch { showToast('⚠️ Clipboard access denied'); }
  }, [tasks, showToast]);

  const handleDragEnd = useCallback((event) => {
    const { active, delta } = event;
    if (!delta) return;
    
    setBoardPositions((prev) => {
      const newPositions = {
        ...prev,
        [active.id]: {
          x: (prev[active.id]?.x || 0) + delta.x,
          y: (prev[active.id]?.y || 0) + delta.y,
        },
      };
      // Save to localStorage
      try {
        localStorage.setItem('vesper_board_positions', JSON.stringify(newPositions));
      } catch (e) {
        console.warn('Failed to save board positions', e);
      }
      return newPositions;
    });
  }, []);

  // Chat box resize handler
  const startResizeChat = useCallback((e) => {
    e.preventDefault();
    const startHeight = chatContainerRef.current?.offsetHeight || 300;
    const startY = e.clientY;

    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.max(150, Math.min(window.innerHeight - 240, startHeight + deltaY)); // Min 150px, max viewport - 240px
      
      if (chatContainerRef.current) {
        chatContainerRef.current.style.maxHeight = `${newHeight}px`;
        // Update customizations and localStorage
        setCustomizations((prev) => ({
          ...prev,
          chatBoxHeight: `${newHeight}px`,
        }));
        try {
          localStorage.setItem('vesper_chat_box_height', `${newHeight}px`);
        } catch (e) {
          console.warn('Failed to save chat height', e);
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Apply saved chat height on mount
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.style.maxHeight = customizations.chatBoxHeight;
    }
  }, [customizations.chatBoxHeight]);

  const RAILWAY_URL = 'https://vesper-backend-production-b486.up.railway.app';

  const apiBase = useMemo(() => {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) return apiUrl.replace(/\/$/, '');
    return RAILWAY_URL;
  }, []);

  const mediaBase = useMemo(() => {
    return apiBase;
  }, [apiBase]);

  const fetchRuntimeCapabilities = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/system/capabilities`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      setRuntimeCapabilities(data);
    } catch (error) {
      console.warn('Capabilities check failed', error);
    }
  }, [apiBase]);

  const firebaseAuthEnabled = useMemo(
    () => String(import.meta.env.VITE_FIREBASE_AUTH_ENABLED).toLowerCase() === 'true',
    []
  );
  const disableFirebaseAuth = useMemo(
    () => !firebaseAuthEnabled || String(import.meta.env.VITE_DISABLE_FIREBASE_AUTH).toLowerCase() === 'true',
    [firebaseAuthEnabled]
  );

  useEffect(() => {
    if (!videoAvatarUrl) {
      setVideoAvatarUrl(`${mediaBase}/media/source/vesper_base.mp4`);
    }
  }, [mediaBase, videoAvatarUrl]);

  useEffect(() => {
    const video = avatarVideoRef.current;
    if (!video) return;

    if (isSpeaking) {
      // ── Amplitude-driven lip-sync ──────────────────────────────────────
      // The video loops the speaking section. We drive playbackRate from the
      // analyser so the mouth moves fast on loud syllables and slows/pauses
      // on silence — making it look like she's actually saying words.
      const SPEAKING_LOOP_END = 3.8; // seconds — end of speaking section
      const SILENCE_THRESHOLD = 6;    // analyser RMS below this = silence
      const MIN_RATE  = 0.0;          // rate when totally silent
      const MAX_RATE  = 1.8;          // rate at peak volume
      const data = new Uint8Array(analyserRef.current?.frequencyBinCount || 128);

      video.loop = false;
      video.playbackRate = 1;
      video.play().catch(() => {});

      let rafId;
      const tick = () => {
        if (!video || video.paused) { rafId = requestAnimationFrame(tick); return; }

        // Clamp to speaking section
        if (video.currentTime >= SPEAKING_LOOP_END) {
          try { video.currentTime = 0.05; } catch (_) {}
        }

        // Read amplitude from analyser
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(data);
          const sum = data.reduce((a, b) => a + b, 0);
          const rms = Math.sqrt(sum / data.length);

          if (rms < SILENCE_THRESHOLD) {
            // Silence between words — slow nearly to a halt
            video.playbackRate = MIN_RATE || 0.05;
          } else {
            // Map loudness → playback rate (0.4 min so it never looks frozen mid-word)
            const rate = 0.4 + ((rms - SILENCE_THRESHOLD) / (128 - SILENCE_THRESHOLD)) * (MAX_RATE - 0.4);
            video.playbackRate = Math.min(MAX_RATE, Math.max(0.4, rate));
          }
        }

        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => {
        cancelAnimationFrame(rafId);
        video.playbackRate = 1;
      };
    }

    if (idleAnimating) {
      // Play the FULL video once (including look-down) as an idle gesture
      video.loop = false;
      video.playbackRate = 1;
      video.currentTime = 0;
      video.play().catch(() => {});
      const onEnded = () => setIdleAnimating(false);
      video.addEventListener('ended', onEnded, { once: true });
      return () => video.removeEventListener('ended', onEnded);
    }

    // Not speaking, not idle — freeze on first visible frame
    video.loop = false;
    video.playbackRate = 1;
    video.pause();
    if (video.readyState >= 2) {
      try { video.currentTime = 0.1; } catch (_) {}
    }
  }, [isSpeaking, idleAnimating, videoAvatarUrl]);

  // Idle gesture timer — randomly triggers the look-down animation when at rest
  useEffect(() => {
    if (isSpeaking || idleAnimating) return; // don't start timer while busy
    const delay = 12000 + Math.random() * 18000; // 12–30 s random interval
    const timer = setTimeout(() => setIdleAnimating(true), delay);
    return () => clearTimeout(timer);
  }, [isSpeaking, idleAnimating]);

  const addLocalMessage = async (role, content, extras = {}) => {
    const message = {
      id: `${Date.now()}-${Math.random()}`,
      userId: userId || 'local',
      role,
      content,
      timestamp: new Date().toISOString(),
      ...extras
    };
    setMessages((prev) => [...prev, message]);

    if (
      isFirebaseConfigured &&
      db &&
      userId &&
      userId !== 'local' &&
      typeof addDoc === 'function' &&
      typeof collection === 'function'
    ) {
      try {
        await addDoc(collection(db, 'chat_messages'), {
          ...message,
          timestamp: serverTimestamp(),
        });
      } catch (error) {
        console.error('Failed to persist message:', error);
      }
    }
  };

  useHotkeys('ctrl+k, cmd+k', (e) => {
    e.preventDefault();
    setCommandPaletteOpen(true);
  });

  useHotkeys('c', (e) => {
    e.preventDefault();
    setGameMode((prev) => !prev);
  });

  useEffect(() => {
    if (disableFirebaseAuth || !isFirebaseConfigured || !auth || typeof signInAnonymously !== 'function') {
      setUserId('local');
      return;
    }
    const initAuth = async () => {
      try {
        const result = await signInAnonymously(auth);
        setUserId(result.user.uid);
      } catch (error) {
        console.error('Auth error:', error);
        setUserId('local');
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('vesper_theme', activeTheme.id);
      window.localStorage.setItem('vesper_active_section', activeSection);
      window.localStorage.setItem('vesper_memory_category', memoryCategory);
    } catch (error) {
      console.warn('Storage write failed', error);
    }
  }, [activeTheme, activeSection, memoryCategory]);

  useEffect(() => {
    // Safety reset: never let Memory Core drawer stay open across app boot.
    setThreadsDialogOpen(false);
  }, []);

  useEffect(() => {
    fetchRuntimeCapabilities();
  }, [fetchRuntimeCapabilities]);

  // ── Vesper proactive message polling ──────────────────────────────────────
  // Vesper can queue messages for CC using the vesper_notify tool.
  // Frontend polls every 15s; reminders get an alarm toast, others go into chat.
  const [reminderAlerts, setReminderAlerts] = useState([]);
  useEffect(() => {
    const pollProactive = async () => {
      try {
        const res = await fetch(`${apiBase}/api/vesper/proactive`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          for (const msg of data.messages) {
            if (msg.source === 'reminder') {
              // Show as a dismissible alarm toast AND add to chat
              setReminderAlerts(prev => [...prev, { id: Date.now() + Math.random(), text: msg.message }]);
              addLocalMessage('assistant', `⏰ ${msg.message}`);
            } else {
              addLocalMessage('assistant', `🔔 ${msg.message}`);
            }
          }
        }
      } catch {
        // silently ignore poll failures
      }
    };
    const timer = setInterval(pollProactive, 15000);
    return () => clearInterval(timer);
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Background workers panel ───────────────────────────────────────────────
  // Vesper's spawn_worker agents run unattended — this polls their live status
  // so CC can SEE what's running, step by step, without asking.
  const [workersInfo, setWorkersInfo] = useState({ workers: [], running: 0, total: 0 });
  const [workersOpen, setWorkersOpen] = useState(false);
  // (autopilotLog state already exists — reused here)
  useEffect(() => {
    const pollWorkers = async () => {
      try {
        const res = await fetch(`${apiBase}/api/workers`);
        if (!res.ok) return;
        setWorkersInfo(await res.json());
      } catch { /* silent */ }
    };
    pollWorkers();
    const timer = setInterval(pollWorkers, 20000);
    return () => clearInterval(timer);
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps
  const openWorkersPanel = async () => {
    setWorkersOpen(true);
    try {
      const res = await fetch(`${apiBase}/api/autopilot/log`);
      if (res.ok) {
        const data = await res.json();
        setAutopilotLog(Array.isArray(data) ? data : (data.log || []));
      }
    } catch { /* silent */ }
  };


  useEffect(() => {
    try {
      const saved = localStorage.getItem('vesper_bg_rotate');
      if (!saved) return;
      const { enabled, interval } = JSON.parse(saved);
      if (!enabled || !interval || backgroundGallery.length === 0) return;
      
      const timer = setInterval(() => {
        const all = backgroundGallery.length > 0 ? backgroundGallery : [];
        if (all.length === 0) return;
        const pick = all[Math.floor(Math.random() * all.length)];
        setCustomBackground({ id: pick.id, url: pick.url, name: pick.name, category: pick.category || 'custom' });
        try { localStorage.setItem('vesper_custom_bg', JSON.stringify({ id: pick.id, url: pick.url, name: pick.name, category: pick.category })); } catch (e) {}
      }, (interval || 30) * 60 * 1000);
      
      return () => clearInterval(timer);
    } catch (e) {}
  }, [backgroundGallery]);

  // ── Vesper Autonomy: Fetch daily identity + proactive greeting on load ──
  useEffect(() => {
    const loadVesperIdentity = async () => {
      let identity;
      let optionsLoaded = false;

      // Fetch identity options FIRST so the dialog has data when it opens
      try {
        const optRes = await fetch(`${apiBase}/api/vesper/identity/options`);
        if (optRes.ok) {
          setIdentityOptions(await optRes.json());
          optionsLoaded = true;
        }
      } catch (e) { console.log('Identity options fetch skipped:', e.message); }

      try {
        // Fetch daily identity
        const idRes = await fetch(`${apiBase}/api/vesper/identity`);
        if (idRes.ok) {
          identity = await idRes.json();
          setVesperIdentity(identity);
          // Only show identity dialog once options are loaded to prevent crash
          // Identity loads silently — user can adjust in Settings
          void 0;
        }
      } catch (e) { console.log('Identity fetch skipped:', e.message); }
      
      try {
        // Fetch proactive greeting + initiatives
        const initRes = await fetch(`${apiBase}/api/vesper/initiative`);
        if (initRes.ok) {
          const data = await initRes.json();
          if (data.greeting) setVesperGreeting(data.greeting);
          if (data.initiatives?.length) setVesperInitiatives(data.initiatives);
        }
      } catch (e) { console.log('Initiative fetch skipped:', e.message); }

      try {
        // Fetch active avatar data  
        const avRes = await fetch(`${apiBase}/api/avatars`);
        if (avRes.ok) {
          const avData = await avRes.json();
          const active = (avData.avatars || []).find(a => a.id === avData.active);
          if (active) setActiveAvatarData(active);
        }
      } catch (e) {}

      // ── Session briefing ──────────────────────────────────────────────────
      try {
        const _tid = (() => { try { return localStorage.getItem('vesper_last_thread_id') || ''; } catch { return ''; } })();
        const briefRes = await fetch(`${apiBase}/api/session/briefing${_tid ? `?thread_id=${_tid}` : ''}`);
        if (briefRes.ok) {
          const bd = await briefRes.json();
          if (bd.briefing) setSessionBriefing(bd.briefing);
        }
      } catch (e) { /* briefing is best-effort */ }

      // ── Fetch XP ─────────────────────────────────────────────────────────
      try {
        const xpRes = await fetch(`${apiBase}/api/vesper/xp`);
        if (xpRes.ok) {
          const xd = await xpRes.json();
          setVesperXp(xd);
          try { localStorage.setItem('vesper_xp_cache', JSON.stringify(xd)); } catch (_) {}
        }
      } catch (e) { /* xp is best-effort */ }

      // ── Fetch income summary ──────────────────────────────────────────────
      try {
        const isRes = await fetch(`${apiBase}/api/income/summary`);
        if (isRes.ok) {
          const isd = await isRes.json();
          setIncomeStats(isd);
          try { localStorage.setItem('vesper_income_cache', JSON.stringify(isd)); } catch (_) {}
        }
      } catch (e) { /* income summary is best-effort */ }
    };
    loadVesperIdentity();
  }, [apiBase]);

  // ── Keepalive: ping backend every 4 min to prevent Railway cold starts ──
  // /health is rewritten by vercel.json to Railway directly
  useEffect(() => {
    if (!apiBase) return;
    const ping = () => fetch(`${apiBase}/health`, { method: 'GET', cache: 'no-store' }).catch(() => {});
    ping(); // warm it up immediately on mount
    const keepaliveInterval = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(keepaliveInterval);
  }, [apiBase]);

  useEffect(() => {
    if (
      !isFirebaseConfigured ||
      !db ||
      !userId ||
      userId === 'local' ||
      typeof query !== 'function' ||
      typeof collection !== 'function' ||
      typeof orderBy !== 'function' ||
      typeof onSnapshot !== 'function'
    )
      return;

    const q = query(collection(db, 'chat_messages'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedMessages = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.userId === userId) {
          loadedMessages.push({
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp,
          });
        }
      });
      setMessages(loadedMessages);
    });
    return () => unsubscribe();
  }, [userId]);

  const lastMessageCountRef = useRef(0);
  useEffect(() => {
    // Only auto-scroll when messages are ADDED, not when content updates (streaming)
    if (messages.length !== lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // Track scroll position to show/hide scroll-to-bottom button
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setShowScrollBottom(scrollHeight - scrollTop - clientHeight > 120);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-focus input when switching threads (not on every message update)
  useEffect(() => {
    inputRef.current?.focus();
  }, [currentThreadId]);

  const saveMessageToThread = async (role, content, overrideThreadId, options = {}) => {
    try {
      const createOnly = options.createOnly === true;
      let threadId = overrideThreadId || currentThreadId;
      
      // If no thread, create one
      if (!threadId) {
        // Generate a meaningful initial title from the first message
        const cleanContent = content
          .replace(/\[File:.*?\]/g, '')  // Remove file references
          .replace(/```[\s\S]*?```/g, '') // Remove code blocks
          .replace(/https?:\/\/\S+/g, '') // Remove URLs
          .replace(/\s+/g, ' ')
          .trim();
        // Try to get first sentence
        const sentenceMatch = cleanContent.match(/^(.+?[.?!])\s/);
        let title;
        if (sentenceMatch && sentenceMatch[1].length <= 70) {
          title = sentenceMatch[1];
        } else {
          // Take first ~60 chars, break at word boundary
          const words = cleanContent.split(' ');
          let t = '';
          for (const w of words) {
            if ((t + ' ' + w).length > 60) break;
            t = t ? t + ' ' + w : w;
          }
          title = t || cleanContent.slice(0, 60);
        }
        if (!title || title.length < 3) title = `Chat ${new Date().toLocaleDateString()}`;
        
        console.log('📝 Creating new thread with title:', title);
        
        const createRes = await fetch(`${apiBase}/api/threads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title,
            messages: createOnly ? [] : [{ role, content, timestamp: Date.now() }],
          }),
        });
        
        if (!createRes.ok) throw new Error(`HTTP ${createRes.status}: ${createRes.statusText}`);
        
        const createData = await createRes.json();
        if (createData.error) throw new Error(createData.error);
        
        threadId = createData.id;
        setCurrentThreadId(threadId);
        setCurrentThreadTitle(title);
        
        // CRITICAL: Refresh thread list after creating
        fetchThreads(true); // silent — don't reset scroll position
        console.log('✅ Thread created:', threadId);
        return threadId;
      } else if (createOnly) {
        return threadId;
      } else {
        // Add message to existing thread
        console.log('💬 Adding message to thread:', threadId);
        
        const addRes = await fetch(`${apiBase}/api/threads/${threadId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role, content, timestamp: Date.now() }),
        });
        
        if (!addRes.ok) throw new Error(`HTTP ${addRes.status}: ${addRes.statusText}`);
        return threadId;
      }
    } catch (error) {
      console.error('❌ Failed to save message to thread:', error);
      setToast(`Memory save failed: ${error.message}`);
      return currentThreadId;
    }
  };

  const stopGeneration = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setLoading(false);
      setThinking(false);
      setToast('Generation stopped');
    }
  };

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/suggestions?thread_id=${currentThreadId || ''}`);
      if (!response.ok) throw new Error('Failed to fetch suggestions');
      const data = await response.json();
      if (data.suggestions) {
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setToast('⚠️ Could not load suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // ── Vesper Identity Handlers ──
  const handleConfirmIdentity = async (overrides = {}) => {
    try {
      const res = await fetch(`${apiBase}/api/vesper/identity/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed: true, ...overrides }),
      });
      if (res.ok) {
        const data = await res.json();
        setVesperIdentity(data.identity || data);
        setToast(`✨ Vesper locked in today's vibe!`);
      }
    } catch (e) { console.error('Confirm identity failed:', e); }
  };

  const handleRerollIdentity = async () => {
    try {
      const res = await fetch(`${apiBase}/api/vesper/identity/reroll`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setVesperIdentity(data.identity || data);
        setToast('🎲 Vesper rerolled her vibe!');
      }
    } catch (e) { console.error('Reroll identity failed:', e); }
  };

  const sendMessage = async () => {
    if (sendingRef.current) return;
    if ((!input.trim() && uploadedImages.length === 0) || loading) return;
    sendingRef.current = true;
    const userMessage = input.trim();
    const currentImages = [...uploadedImages]; // Capture images
    
    // Unlock HTMLAudioElement autoplay NOW during the user gesture,
    // so it stays valid even after async streaming completes.
    if (!audioUnlockedRef.current) {
      try {
        // Tiny silent WAV (44 bytes) played + immediately paused unlocks autoplay for the session
        const silentAudio = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
        await silentAudio.play();
        silentAudio.pause();
        audioUnlockedRef.current = true;
      } catch (e) { /* already unlocked or not needed */ }
    }
    
    setInput('');
    try { localStorage.removeItem(`vesper_draft_${currentThreadId || 'new'}`); } catch(_) {}
    setUploadedImages([]); // Clear UI immediately
    setLoading(true);
    setThinking(true);
    setThinkingStatus('Thinking...');
    setActiveToolName(null);
    setActiveToolLabel('');
    setToolHistory([]);
    bumpStat('messages');
    // Burst particle animation on send
    setSendBurst(true);
    setTimeout(() => setSendBurst(false), 700);
    
    try {
      playSound('click'); // Sound on send
    } catch(e) { /* ignore sound errors */ }
    
    // Create new abort controller for this request
    const controller = new AbortController();
    setAbortController(controller);
    
    console.log('📤 Sending message:', userMessage.substring(0, 50));
    
    // Add local message with images
    const localMsg = { role: 'user', content: userMessage };
    if (currentImages.length > 0) {
      localMsg.images = currentImages.map(img => img.dataUrl);
      localMsg.content = userMessage || '[Image Attached]';
    }
    
    addLocalMessage('user', localMsg.content, localMsg);
    
    let savedThreadId;
    try {
      // Save user message immediately (creates thread WITH message for new convos,
      // or appends to existing thread). Backend deduplicates if already present.
      savedThreadId = await saveMessageToThread('user', userMessage, undefined);
    } catch(e) {
      console.warn('Thread save failed, continuing:', e);
      savedThreadId = currentThreadId;
    }

    // Never fall back to "default"; enforce a unique thread id so every chat is loggable.
    const effectiveThreadId = savedThreadId || currentThreadId || `thread_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    if (!currentThreadId && !savedThreadId) {
      setCurrentThreadId(effectiveThreadId);
      setCurrentThreadTitle(`Chat ${new Date().toLocaleDateString()}`);
    }
    
    // Create a placeholder assistant message for streaming
    const streamMsgId = `stream-${Date.now()}-${Math.random()}`;
    setStreamingMessageId(streamMsgId);
    let lastStreamUpdate = 0;
    const STREAM_THROTTLE = 50; // ms — throttle DOM updates during streaming
    
    try {
      const payload = { 
        message: userMessage,
        thread_id: effectiveThreadId,
        images: currentImages.length > 0 ? currentImages.map(img => img.dataUrl) : [],
        model: selectedModel !== 'auto' ? selectedModel : null,
      };
      const userAskedWallpaper = /wallpaper|background|set it as|as my bg|as my background|set as wallpaper|use as wallpaper/i.test(String(userMessage || ''));

      // ── Pre-warm: ping /health until Railway responds (up to 45s) ──
      setThinkingStatus('Connecting to Vesper...');
      const PREWARM_TIMEOUT = 45000;
      const prewarmStart = Date.now();
      let prewarmOk = false;
      while (Date.now() - prewarmStart < PREWARM_TIMEOUT) {
        if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
          const pingRes = await Promise.race([
            fetch(`${apiBase}/health`, { method: 'GET', cache: 'no-store' }),
            new Promise((_, rej) => setTimeout(() => rej(new Error('ping timeout')), 5000)),
          ]);
          if (pingRes.ok) { prewarmOk = true; break; }
        } catch { /* still waking */ }
        const elapsed = Math.round((Date.now() - prewarmStart) / 1000);
        setThinkingStatus(`Waking up Railway… ${elapsed}s`);
        await new Promise(r => setTimeout(r, 2500));
      }
      if (!prewarmOk) {
        console.warn('⚠️ Railway health check timed out — attempting chat anyway');
      }
      setThinkingStatus('Thinking...');

      // ── Use SSE streaming endpoint (with retry) ──
      let response;
      let _fetchAttempts = 0;
      const MAX_FETCH_ATTEMPTS = 3;
      while (true) {
        try {
          response = await fetch(`${apiBase}/api/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`Backend returned ${response.status}`);
          break; // success
        } catch (fetchErr) {
          if (fetchErr.name === 'AbortError') throw fetchErr; // user stopped — don't retry
          _fetchAttempts++;
          if (_fetchAttempts >= MAX_FETCH_ATTEMPTS) throw fetchErr; // give up after 3 attempts
          console.warn(`⚡ Connection attempt ${_fetchAttempts} failed, retrying in 4s...`, fetchErr.message);
          setThinkingStatus(`Reconnecting... (${_fetchAttempts}/${MAX_FETCH_ATTEMPTS - 1})`);
          await new Promise(r => setTimeout(r, 4000));
        }
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let messageAdded = false;
      let currentProvider = '';
      let currentModel = '';
      
      // Stall timeout is tool-aware: 90s normally, but while a tool is running
      // (video render, deep research, browser automation) the backend can be
      // legitimately silent for minutes — don't kill the stream mid-work.
      let stallTimeoutMs = 90000;
      const TOOL_STALL_TIMEOUT_MS = 480000; // 8 min while a tool executes
      while (true) {
        const { done, value } = await Promise.race([
          reader.read(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Stream stalled')), stallTimeoutMs)),
        ]);
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete chunk
        
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'status') {
              setThinkingStatus(data.content || 'Thinking...');
            } else if (data.type === 'tool_start') {
              stallTimeoutMs = TOOL_STALL_TIMEOUT_MS; // tools may be silent for minutes
              setActiveToolName(data.tool_name);
              setActiveToolLabel(data.tool_label || `Running ${data.tool_name}...`);
              setThinkingStatus(data.tool_label || `Using ${data.tool_name}...`);
              setToolHistory(prev => [...prev, { name: data.tool_name, label: data.tool_label || data.tool_name, status: 'running', ts: Date.now() }]);
            } else if (data.type === 'tool_done') {
              stallTimeoutMs = 90000; // back to normal responsiveness between tools
              setActiveToolName(null);
              setActiveToolLabel('');
              setToolHistory(prev => prev.map(t =>
                t.name === data.tool_name && t.status === 'running'
                  ? { ...t, status: data.success ? 'done' : 'error' }
                  : t
              ));
              // Proactive nudges — suggest what to do next
              const _nudges = NUDGE_MAP[data.tool_name];
              if (_nudges) setPendingNudges(_nudges.slice(0, 3));
              // Mood: working while a tool runs, happy when it completes
              setVesperMood(data.success ? 'happy' : 'concerned');
            } else if (data.type === 'provider') {
              currentProvider = data.provider;
              currentModel = data.model;
            } else if (data.type === 'chunk') {
              accumulatedText = (accumulatedText + data.content)
                .replace(/<thought>[\s\S]*?<\/thought>/gi, '')
                .replace(/<thought>[\s\S]*$/i, '');
              if (!messageAdded) {
                // Add the message on first chunk
                addLocalMessage('assistant', accumulatedText, { id: streamMsgId });
                messageAdded = true;
                setThinking(false);
                setThinkingStatus('');
                lastStreamUpdate = Date.now();
              } else {
                // Throttle DOM updates during streaming to prevent twitching
                const now = Date.now();
                if (now - lastStreamUpdate >= STREAM_THROTTLE) {
                  lastStreamUpdate = now;
                  setMessages(prev => prev.map(m => 
                    m.id === streamMsgId ? { ...m, content: accumulatedText, model: currentModel, provider: currentProvider } : m
                  ));
                }
              }
            } else if (data.type === 'visualizations' && data.data) {
              data.data.forEach(viz => {
                if (viz.type === 'chart_visualization') {
                  addLocalMessage('assistant', 'Generated Chart', { type: 'chart', chartData: viz });
                } else if (viz.type === 'sandbox_image') {
                  addLocalMessage('assistant', '', { type: 'sandbox_image', imageData: viz.image_data, caption: viz.caption || '' });
                } else if (viz.type === 'weather_card') {
                  addLocalMessage('assistant', '', { type: 'weather_card', ...viz });
                } else if (viz.type === 'reminder_card') {
                  addLocalMessage('assistant', '', { type: 'reminder_card', ...viz });
                } else if (viz.type === 'product_launch_card') {
                  addLocalMessage('assistant', '', { type: 'product_launch_card', ...viz });
                } else if (viz.type === 'image_generation') {
                  addLocalMessage('assistant', `Here's your image ✨`, { type: 'image', imageUrl: viz.image_url, imagePrompt: viz.prompt, imageProvider: viz.provider });
                  const promptSuggestsWallpaper = /wallpaper|background/i.test(String(viz.prompt || ''));
                  if ((viz.set_as_wallpaper || userAskedWallpaper || promptSuggestsWallpaper) && viz.image_url) {
                    const bg = { id: `vesper-${Date.now()}`, url: viz.image_url, name: (viz.prompt || 'Vesper Design').slice(0, 60), category: 'vesper-designed' };
                    setCustomBackground(bg);
                    try { localStorage.setItem('vesper_custom_bg', JSON.stringify(bg)); } catch(e) {}
                    setBackgroundGallery(prev => [...prev.filter(b => b.id !== bg.id), bg]);
                    showToast(`✨ Vesper applied wallpaper!`, 'success');
                  }
                }
              });
            } else if (data.type === 'vesper_decorate') {
              if (data.action === 'wallpaper' && data.data?.url) {
                const bg = { id: data.data.id || `vesper-${Date.now()}`, url: data.data.url, name: data.data.name || "Vesper's Design", category: 'vesper-designed' };
                setCustomBackground(bg);
                try { localStorage.setItem('vesper_custom_bg', JSON.stringify(bg)); } catch(e) {}
                setBackgroundGallery(prev => [...prev.filter(b => b.id !== bg.id), bg]);
                showToast(`✨ Vesper set wallpaper: ${bg.name}`, 'success');
              } else if (data.action === 'theme' && data.data?.theme_id) {
                const t = THEMES.find(th => th.id === data.data.theme_id);
                if (t) {
                  setActiveTheme(t);
                  try { localStorage.setItem('vesper_theme', t.id); } catch(e) {}
                  showToast(`🎨 Vesper switched to ${t.label}`, 'success');
                }
              } else if (data.action === 'css' && data.data?.css) {
                const s = document.createElement('style');
                s.id = `vesper-effect-${Date.now()}`;
                s.textContent = data.data.css;
                document.head.appendChild(s);
                showToast(`✨ Vesper applied: ${data.data.name || 'effect'}`, 'success');
              } else if (data.action === 'creative_suite_update') {
                // Signal CreativeSuite to reload its creations list
                window.dispatchEvent(new CustomEvent('vesper:creative_update', { detail: data.data }));
                // Use prop-based navigation to avoid timing race with unmounted component
                setPendingCreationsPanel(true);
                setActiveSection('nyxshift');
                showToast(`✨ Vesper created: ${data.data?.title || 'new creation'} — opening your gallery!`, 'success');
              }
            } else if (data.type === 'done') {
              currentProvider = data.provider || currentProvider;
              currentModel = data.model || currentModel;
              // Mood detection based on response content
              const _txt = accumulatedText.toLowerCase();
              const _exclaim = (accumulatedText.match(/!/g) || []).length;
              if (_exclaim >= 2 || /amazing|wow|incredible|exciting|great news/i.test(_txt)) setVesperMood('excited');
              else if (/sorry|unfortunately|error|can't|cannot|unable/i.test(_txt)) setVesperMood('concerned');
              else if (/created|here.*image|here.*chart|generated|here you go/i.test(_txt)) setVesperMood('happy');
              else if (/i found|searching|here.*results|according to/i.test(_txt)) setVesperMood('searching');
              else if (accumulatedText.length > 400) setVesperMood('focused' in {} ? 'focused' : 'neutral');
              else setVesperMood('neutral');
            } else if (data.type === 'error') {
              if (!messageAdded) {
                addLocalMessage('assistant', data.content || 'Something went wrong.');
                messageAdded = true;
              }
            }
          } catch (parseErr) {
            // Skip malformed SSE chunks
          }
        }
      }

      // Legacy backend safeguard: if we receive the old empty-glitch phrase,
      // auto-retry once via non-stream endpoint so user still gets a real answer.
      const legacyEmptyResponses = [
        'I hit a response glitch and came back empty. Please send that again and I will answer it properly.',
        'I started working on that, but the response came back empty. Send it again and I will retry cleanly.',
      ];
      const normalizedAssistant = (accumulatedText || '').trim();
      const hitLegacyEmpty = legacyEmptyResponses.some(msg => normalizedAssistant === msg || normalizedAssistant.includes(msg));

      if (hitLegacyEmpty) {
        setThinkingStatus('Recovering response...');
        try {
          const retryRes = await fetch(`${apiBase}/api/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });

          if (retryRes.ok) {
            const retryData = await retryRes.json();
            const retryText = (retryData?.response || retryData?.content || '').trim();
            if (retryText) {
              accumulatedText = retryText;
              if (messageAdded) {
                setMessages(prev => prev.map(m =>
                  m.id === streamMsgId ? { ...m, content: accumulatedText, model: currentModel, provider: currentProvider } : m
                ));
              } else {
                addLocalMessage('assistant', accumulatedText, { id: streamMsgId });
                messageAdded = true;
              }
            }
          }
        } catch (retryErr) {
          console.warn('Legacy-empty recovery retry failed:', retryErr?.message || retryErr);
        }
      }
      
      // Finalize — flush last streaming content
      if (messageAdded && accumulatedText) {
        setMessages(prev => prev.map(m => 
          m.id === streamMsgId ? { ...m, content: accumulatedText, model: currentModel, provider: currentProvider } : m
        ));
      }
      if (!messageAdded && accumulatedText) {
        addLocalMessage('assistant', accumulatedText);
      }
      if (!messageAdded && !accumulatedText.trim()) {
        const stalledMsg = 'I had a temporary response issue and recovered. You can continue normally.';
        addLocalMessage('assistant', stalledMsg);
      }
      
      console.log(`🤖 Streamed response from ${currentProvider} (${accumulatedText.length} chars)`);
      
      // Auto-generate a proper topic title for new threads
      const threadToTitle = effectiveThreadId;
      if (threadToTitle && accumulatedText) {
        try {
          const titleRes = await fetch(`${apiBase}/api/threads/${threadToTitle}/auto-title`, { method: 'POST' });
          const titleData = await titleRes.json();
          if (titleData.status === 'success' && titleData.title) {
            setCurrentThreadTitle(titleData.title);
            setThreads(prev => prev.map(t => t.id === threadToTitle ? { ...t, title: titleData.title } : t));
          }
        } catch (e) {
          // Auto-title is best-effort, don't block on failure
          console.warn('Auto-title skipped:', e.message);
        }
      }
      
      // Auto-speak the complete response
      if (autoSpeak && accumulatedText) {
        speak(accumulatedText, activeSection === 'chat' ? 'chat' : activeSection === 'research' ? 'research' : 'default');
      }
      playSound('notification');
      
      fetchThreads(true); // silent — don't reset scroll position
      // XP increment — fire and forget
      try {
        fetch(`${apiBase}/api/vesper/xp/increment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gain: toolHistory.length > 0 ? 25 : 10, kind: toolHistory.length > 0 ? 'tool' : 'message' }) })
          .then(r => r.json()).then(xd => { setVesperXp(xd); try { localStorage.setItem('vesper_xp_cache', JSON.stringify(xd)); } catch (_) {} }).catch(() => {});
      } catch (_) {}
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Generation stopped by user');
        sendingRef.current = false;
        return;
      }
      console.error('❌ Chat error:', error);
      playSound('error');
      // Restore the user's message so they can re-send without retyping
      setInput(userMessage);
      const errorMsg = error.message === 'Stream stalled'
        ? "⚠️ Vesper started working, but the response stalled out. Your message has been restored in the input box. Wait a moment, then hit Send again."
        : "⚠️ Couldn't reach the backend — Railway may still be waking up. Your message has been restored in the input box. Wait a moment, then hit Send again.";
      addLocalMessage('assistant', errorMsg);
      if (autoSpeak) speak(errorMsg);
    } finally {
      setAbortController(null);
      setStreamingMessageId(null);
      setLoading(false);
      setThinking(false);
      setThinkingStatus('');
      setActiveToolName(null);
      setActiveToolLabel('');
      sendingRef.current = false;
    }
  };

  // ── Chat Export (Markdown / Copy) ──────────────────────────────────
  const exportChat = async (format = 'markdown') => {
    try {
      const threadId = currentThreadId || 'default';
      const response = await fetch(`${apiBase}/api/chat/export?thread_id=${threadId}&format=${format}`);
      const data = await response.json();
      if (data.error) { setToast('⚠️ ' + data.error); return; }
      
      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vesper-chat-${threadId}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setToast('📄 Chat exported as JSON');
      } else {
        // Markdown — copy to clipboard and also download
        const md = data.markdown || '';
        await navigator.clipboard.writeText(md);
        const blob = new Blob([md], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vesper-chat-${threadId}.md`;
        a.click();
        URL.revokeObjectURL(url);
        setToast(`📋 Chat exported (${data.message_count} messages) & copied to clipboard`);
      }
    } catch (e) {
      console.error('Export error:', e);
      setToast('⚠️ Export failed');
    }
  };

  // ── Voice Preview ─────────────────────────────────────────────────
  const playVoicePreview = (previewUrl) => {
    if (!previewUrl) { setToast('No preview available'); return; }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    const audio = new Audio(previewUrl);
    previewAudioRef.current = audio;
    audio.onended = () => { previewAudioRef.current = null; };
    audio.play().catch(() => setToast('Preview playback failed'));
  };

  // ── Generate Video Avatar ─────────────────────────────────────────
  const generateVideoAvatar = async (textToSpeak = null) => {
    try {
      if (runtimeCapabilities?.features?.video_avatar === false && runtimeCapabilities?.features?.video_avatar_fallback) {
        showToast(runtimeCapabilities?.hints?.video_avatar || 'Video speech is not configured in this environment yet.', 'warn');
      }
      setVideoGenLoading(true);
      const mediaBase = apiBase || '';

      // Keep the panel pinned to the real base video clip while speech is generated.
      setVideoAvatarUrl(`${mediaBase}/media/source/vesper_base.mp4`);
      setVideoShouldAutoplay(false);

      const text = textToSpeak || (messages.length > 0 && messages[messages.length - 1].role === 'assistant' 
        ? messages[messages.length - 1].content 
        : 'Hello! I am Vesper, your AI assistant.');
      const resolvedVideoVoice = normalizeVoiceId(selectedVoiceName)
        || normalizeVoiceId(defaultVoiceId)
        || normalizeVoiceId(cloudVoices.length > 0 ? cloudVoices[0].id : '')
        || '';
      
      const response = await fetch(`${apiBase}/api/video-avatar/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.substring(0, 500),
          voice: resolvedVideoVoice,
          source_video: 'vesper_base.mp4',
          stability: 0.5,
          similarity_boost: 0.75,
          lipsync: true,
          require_lipsync: false,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        showToast(`Video speech unavailable: ${err.error || 'Unknown error'}. Showing base video.`, 'warn');
        return;
      }

      const result = await response.json();
      if (result.video_url) {
        const resolvedUrl = result.video_url.startsWith('/media/')
          ? `${mediaBase}${result.video_url}`
          : `${apiBase}${result.video_url}`;
        setVideoAvatarUrl(resolvedUrl);
        const hasGeneratedSpeech = result.mode === 'wav2lip' || result.mode === 'audio_mux';
        setVideoShouldAutoplay(hasGeneratedSpeech);
        if (hasGeneratedSpeech) {
          showToast(`Video created${result.mode === 'wav2lip' ? ' with true lip-sync!' : ' with speech'}`, 'success');
        } else {
          showToast(runtimeCapabilities?.hints?.tts || 'Video loaded in fallback mode. Configure voice to enable speech.', 'warn');
        }
      }
    } catch (err) {
      console.error('Video generation error:', err);
      showToast(`Video error: ${err.message}. Showing base video.`, 'warn');
    } finally {
      setVideoGenLoading(false);
    }
  };

  // ── Drag & Drop File Handler ──────────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingFile(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingFile(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setUploadedImages(prev => [...prev, { name: file.name, dataUrl: ev.target.result }]);
          setToast(`📎 ${file.name} attached`);
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'text/plain' || file.type === 'application/json' || file.name.endsWith('.txt') || file.name.endsWith('.md') || file.name.endsWith('.json') || file.name.endsWith('.csv') || file.name.endsWith('.log') || file.name.endsWith('.xml') || file.name.endsWith('.yaml') || file.name.endsWith('.yml')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const content = ev.target.result;
          setInput(prev => prev + (prev ? '\n\n' : '') + `[File: ${file.name}]\n${content.slice(0, 5000)}`);
          setToast(`📎 ${file.name} added to message`);
        };
        reader.readAsText(file);
      } else {
        setToast(`⚠️ Unsupported file type: ${file.type || file.name}`);
      }
    });
  };

  // ── Toggle Auto-speak ─────────────────────────────────────────────
  const toggleAutoSpeak = () => {
    const newVal = !autoSpeak;
    setAutoSpeak(newVal);
    try { localStorage.setItem('vesper_auto_speak', String(newVal)); } catch(e) {}
    setToast(newVal ? '🔊 Auto-speak ON' : '🔇 Auto-speak OFF');
  };

  const clearHistory = async () => {
    if (!userId) return;
    if (
      !isFirebaseConfigured ||
      !db ||
      userId === 'local' ||
      typeof query !== 'function' ||
      typeof collection !== 'function' ||
      typeof getDocs !== 'function' ||
      typeof deleteDoc !== 'function'
    ) {
      setMessages([]);
      playSound('success');
      setToast('Chat cleared');
      return;
    }
    const q = query(collection(db, 'chat_messages'));
    const snapshot = await getDocs(q);
    const deletePromises = [];
    snapshot.forEach((doc) => {
      if (doc.data().userId === userId) deletePromises.push(deleteDoc(doc.ref));
    });
    await Promise.all(deletePromises);
    setMessages([]);
    playSound('success');
    setToast('Chat cleared');
  };

  const exportAllData = () => {
    try {
      const exportData = {
        version: '1.0',
        exported_at: new Date().toISOString(),
        threads: threads.map(t => ({
          id: t.id,
          title: t.title,
          pinned: t.pinned,
          messages: t.messages || [],
          created_at: t.created_at
        })),
        current_messages: messages,
        settings: {
          theme: activeTheme.id,
          tts_enabled: ttsEnabled,
          sound_enabled: soundEnabled,
          active_section: activeSection,
          memory_category: memoryCategory
        },
        tasks: tasks,
        research: researchItems,
        memory: memoryItems
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vesper-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      playSound('success');
      setToast('Data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      playSound('error');
      setToast('Export failed');
    }
  };

  const playSound = useCallback((type) => {
    if (!soundEnabled) return;
    
    const ctx = getAudioContext();
    if (!ctx || ctx.state === 'closed') return;
    
    try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Different sounds for different actions
    switch (type) {
      case 'click':
        oscillator.frequency.value = 800;
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
        break;
      case 'success':
        oscillator.frequency.value = 1200;
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
        break;
      case 'error':
        oscillator.frequency.value = 400;
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
        break;
      case 'notification':
        oscillator.frequency.value = 1000;
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.15);
        break;
      default:
        oscillator.frequency.value = 600;
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
    }
    } catch (e) {
      // Silently handle audio errors (context closed, etc.)
    }
  }, [soundEnabled, getAudioContext]);

  // ── Themed Ambient Sound System ──────────────────────────────────────
  const ambientRef = useRef(null);
  const [ambientEnabled, setAmbientEnabled] = useState(() => safeStorageGet('vesper_ambient', 'false') === 'true');

  const startAmbientSound = useCallback((soundType) => {
    // Stop existing ambient
    if (ambientRef.current) {
      try { ambientRef.current.forEach(n => { try { n.stop?.(); n.disconnect?.(); } catch(e){} }); } catch(e){}
      ambientRef.current = null;
    }
    if (!ambientEnabled || !soundType) return;

    const ctx = getAudioContext();
    if (!ctx || ctx.state === 'closed') return;

    const nodes = [];
    try {
      // Master gain for ambient volume
      const master = ctx.createGain();
      master.gain.value = 0.03; // Very quiet background
      master.connect(ctx.destination);

      const createNoise = (frequency, type = 'sine', gain = 0.5) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = type;
        osc.frequency.value = frequency;
        g.gain.value = gain;
        osc.connect(g);
        g.connect(master);
        osc.start();
        nodes.push(osc);
        return osc;
      };

      const AMBIENT_PRESETS = {
        digital: () => {
          createNoise(55, 'sine', 0.3);
          createNoise(110, 'sine', 0.15);
          createNoise(220, 'sine', 0.05);
        },
        synth: () => {
          createNoise(65, 'triangle', 0.3);
          createNoise(130, 'sine', 0.15);
          createNoise(195, 'triangle', 0.08);
        },
        ambient: () => {
          createNoise(80, 'sine', 0.25);
          createNoise(120, 'sine', 0.15);
          createNoise(160, 'sine', 0.08);
        },
        nature: () => {
          createNoise(100, 'sine', 0.2);
          createNoise(150, 'sine', 0.1);
          createNoise(200, 'triangle', 0.05);
        },
        ocean: () => {
          createNoise(60, 'sine', 0.3);
          createNoise(90, 'sine', 0.2);
          createNoise(45, 'sine', 0.15);
        },
        forest: () => {
          createNoise(120, 'sine', 0.15);
          createNoise(180, 'triangle', 0.1);
          createNoise(250, 'sine', 0.05);
        },
        wind: () => {
          createNoise(50, 'sine', 0.25);
          createNoise(75, 'sine', 0.15);
          createNoise(100, 'triangle', 0.08);
        },
        rain: () => {
          createNoise(200, 'triangle', 0.1);
          createNoise(300, 'triangle', 0.08);
          createNoise(150, 'sine', 0.12);
        },
        fire: () => {
          createNoise(80, 'sawtooth', 0.08);
          createNoise(120, 'sine', 0.15);
          createNoise(60, 'triangle', 0.1);
        },
        dark: () => {
          createNoise(40, 'sine', 0.3);
          createNoise(55, 'sine', 0.15);
          createNoise(30, 'triangle', 0.1);
        },
        cosmic: () => {
          createNoise(70, 'sine', 0.2);
          createNoise(140, 'sine', 0.1);
          createNoise(210, 'triangle', 0.05);
        },
        bells: () => {
          createNoise(440, 'sine', 0.05);
          createNoise(550, 'sine', 0.03);
          createNoise(330, 'triangle', 0.04);
        },
        spooky: () => {
          createNoise(35, 'sine', 0.3);
          createNoise(48, 'sawtooth', 0.06);
          createNoise(70, 'triangle', 0.08);
        },
        jazz: () => {
          createNoise(110, 'sine', 0.15);
          createNoise(165, 'triangle', 0.08);
          createNoise(220, 'sine', 0.05);
        },
        retro: () => {
          createNoise(55, 'square', 0.04);
          createNoise(110, 'square', 0.02);
          createNoise(82.5, 'sine', 0.1);
        },
      };

      const preset = AMBIENT_PRESETS[soundType] || AMBIENT_PRESETS.ambient;
      preset();

    } catch (e) {
      console.log('Ambient sound error:', e.message);
    }

    ambientRef.current = nodes;
  }, [ambientEnabled, getAudioContext]);

  // Start/stop ambient when theme or toggle changes
  useEffect(() => {
    if (ambientEnabled && activeTheme.sound) {
      startAmbientSound(activeTheme.sound);
    } else {
      if (ambientRef.current) {
        try { ambientRef.current.forEach(n => { try { n.stop?.(); n.disconnect?.(); } catch(e){} }); } catch(e){}
        ambientRef.current = null;
      }
    }
    return () => {
      if (ambientRef.current) {
        try { ambientRef.current.forEach(n => { try { n.stop?.(); n.disconnect?.(); } catch(e){} }); } catch(e){}
        ambientRef.current = null;
      }
    };
  }, [ambientEnabled, activeTheme.sound, startAmbientSound]);

  const fetchResearch = useCallback(async () => {
    if (!apiBase) return;
    setResearchLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/research`);
      const data = await res.json();
      setResearchItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Research fetch failed:', error);
    } finally {
      setResearchLoading(false);
    }
  }, [apiBase]);

  const addResearchEntry = async () => {
    if (!researchForm.title.trim() || !apiBase) return;
    try {
      await fetch(`${apiBase}/api/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: researchForm.title,
          summary: researchForm.summary,
          timestamp: new Date().toISOString(),
        }),
      });
      setResearchForm({ title: '', summary: '' });
      fetchResearch();
      setToast('Research saved');
    } catch (error) {
      console.error('Research save failed:', error);
    }
  };

  // ===== Enhanced Research Functions =====
  const searchResearch = async (query) => {
    if (!apiBase || !query.trim()) {
      setResearchSearchResults([]);
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/research/search?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setResearchSearchResults(data.results || []);
    } catch (error) {
      console.error('Research search failed:', error);
    }
  };

  const filterResearchBySource = async (source) => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/research/by-source?source=${source}`);
      const data = await res.json();
      if (source === 'all') {
        fetchResearch();
      } else {
        setResearchItems(data.results || []);
      }
    } catch (error) {
      console.error('Research filter failed:', error);
    }
  };

  const generateCitations = (research) => {
    if (!research) return {};
    
    const citations = {};
    
    // APA Format
    citations.apa = `[${research.id}] "${research.title}". Accessed: ${research.created_at?.split('T')[0] || 'Unknown date'}.`;
    
    // MLA Format
    citations.mla = `"${research.title}." Last modified: ${research.updated_at?.split('T')[0] || 'Unknown date'}.`;
    
    // Chicago Format
    citations.chicago = `${research.title}. [Online]. Available: ${research.url || 'N/A'}. [Accessed: ${research.created_at?.split('T')[0] || 'Unknown date'}].`;
    
    return citations;
  };

  // ===== Advanced Export Functions =====
  const generateMarkdownReport = () => {
    let report = `# Vesper AI Knowledge Export\n\n`;
    report += `**Generated:** ${new Date().toLocaleString()}\n\n`;
    
    if (exportSelection.memories && memoryItems?.length > 0) {
      report += `## Memories (${memoryItems.length})\n\n`;
      memoryItems.forEach(m => {
        report += `### ${m.title || m.content?.substring(0, 50)}\n`;
        report += `- **Category:** ${memoryCategory}\n`;
        report += `- **Importance:** ${m.importance || 'N/A'}\n`;
        report += `- **Date:** ${new Date(m.created_at).toLocaleDateString()}\n\n`;
        report += `${m.content}\n\n`;
      });
    }
    
    if (exportSelection.tasks && tasks?.length > 0) {
      report += `## Tasks (${tasks.length})\n\n`;
      tasks.forEach(t => {
        report += `- [${t.status === 'done' ? 'x' : ' '}] **${t.title}** (${t.priority || 'medium'} priority)\n`;
        if (t.description) report += `  - ${t.description}\n`;
        if (t.due_date) report += `  - Due: ${new Date(t.due_date).toLocaleDateString()}\n`;
      });
      report += `\n`;
    }
    
    if (exportSelection.research && researchItems?.length > 0) {
      report += `## Research (${researchItems.length})\n\n`;
      researchItems.forEach(r => {
        report += `### ${r.title}\n`;
        report += `- **Source:** ${r.source || 'N/A'}\n`;
        report += `- **URL:** ${r.url || 'N/A'}\n`;
        if (r.tags?.length > 0) report += `- **Tags:** ${r.tags.join(', ')}\n`;
        report += `\n${r.content || r.summary || 'No content'}\n\n`;
      });
    }
    
    if (exportSelection.documents && documents?.length > 0) {
      report += `## Documents (${documents.length})\n\n`;
      documents.forEach(d => {
        report += `### ${d.filename}\n`;
        report += `- **Type:** ${d.file_type}\n`;
        report += `- **Size:** ${(d.file_size / 1024).toFixed(2)} KB\n`;
        if (d.summary) report += `- **Summary:** ${d.summary}\n`;
        report += `\n`;
      });
    }
    
    return report;
  };

  const exportAsMarkdown = () => {
    const content = generateMarkdownReport();
    const element = document.createElement('a');
    element.setAttribute('href', `data:text/markdown;charset=utf-8,${encodeURIComponent(content)}`);
    element.setAttribute('download', `Vesper-Export-${new Date().toISOString().split('T')[0]}.md`);
    element.click();
    setToast('📥 Exported as Markdown');
    playSound('success');
  };

  const exportAsJSON = () => {
    const data = {};
    if (exportSelection.memories) data.memories = memoryItems;
    if (exportSelection.tasks) data.tasks = tasks;
    if (exportSelection.research) data.research = researchItems;
    if (exportSelection.documents) data.documents = documents;
    if (exportSelection.conversations) data.conversations = threads;
    
    const element = document.createElement('a');
    element.setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, 2))}`);
    element.setAttribute('download', `Vesper-Export-${new Date().toISOString().split('T')[0]}.json`);
    element.click();
    setToast('📥 Exported as JSON');
    playSound('success');
  };

  const exportAsCSV = () => {
    let csv = '';
    
    if (exportSelection.memories && memoryItems?.length > 0) {
      csv += 'TYPE,CATEGORY,TITLE,CONTENT,DATE\n';
      memoryItems.forEach(m => {
        const content = (m.content || '').replace(/"/g, '""');
        csv += `"Memory","${memoryCategory}","${m.title || ''}","${content.substring(0, 100)}","${new Date(m.created_at).toISOString()}"\n`;
      });
    }
    
    if (exportSelection.tasks && tasks?.length > 0) {
      csv += 'TYPE,TITLE,STATUS,PRIORITY,DUE_DATE\n';
      tasks.forEach(t => {
        csv += `"Task","${t.title}","${t.status}","${t.priority || 'medium'}","${t.due_date || ''}"\n`;
      });
    }
    
    const element = document.createElement('a');
    element.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`);
    element.setAttribute('download', `Vesper-Export-${new Date().toISOString().split('T')[0]}.csv`);
    element.click();
    setToast('📥 Exported as CSV');
    playSound('success');
  };

  // ===== Advanced Customization Functions =====
  const updateCustomization = (key, value) => {
    const updated = { ...customizations, [key]: value };
    setCustomizations(updated);
    localStorage.setItem(`vesper_${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    setToast(`✨ ${key} updated`);
    playSound('success');
  };

  const addMemoryCategory = () => {
    if (!newMemoryCategory.trim() || customizations.memoryCategories.includes(newMemoryCategory)) return;
    const updated = [...customizations.memoryCategories, newMemoryCategory];
    updateCustomization('memoryCategories', updated);
    setNewMemoryCategory('');
  };

  const removeMemoryCategory = (cat) => {
    const updated = customizations.memoryCategories.filter(c => c !== cat);
    updateCustomization('memoryCategories', updated);
  };

  const addResearchSource = () => {
    if (!newResearchSource.trim() || customizations.researchSources.includes(newResearchSource)) return;
    const updated = [...customizations.researchSources, newResearchSource];
    updateCustomization('researchSources', updated);
    setNewResearchSource('');
  };

  const removeResearchSource = (src) => {
    const updated = customizations.researchSources.filter(s => s !== src);
    updateCustomization('researchSources', updated);
  };

  const applyFontSize = (size) => {
    document.documentElement.style.fontSize = size === 'small' ? '14px' : size === 'large' ? '18px' : '16px';
    updateCustomization('fontSize', size);
  };

  const toggleCompactMode = () => {
    const newCompact = !customizations.compactMode;
    updateCustomization('compactMode', newCompact);
    if (newCompact) {
      document.documentElement.style.setProperty('--spacing-multiplier', '0.75');
    } else {
      document.documentElement.style.setProperty('--spacing-multiplier', '1');
    }
  };

  const fetchMemory = useCallback(
    async (category) => {
      if (!apiBase || !category) return;
      setMemoryLoading(true);
      try {
        const res = await fetch(`${apiBase}/api/memories?category=${category}&limit=100`);
        const data = await res.json();
        if (data.status === 'success') {
          setMemoryItems(Array.isArray(data.memories) ? data.memories : []);
        } else {
          setMemoryItems([]);
        }
        // Also fetch tags for this category
        fetchMemoryTags();
      } catch (error) {
        console.error('Memory fetch failed:', error);
      } finally {
        setMemoryLoading(false);
      }
    },
    [apiBase]
  );

  const addMemoryEntry = async () => {
    if (!memoryText.trim() || !apiBase) return;
    try {
      // Parse tags from input
      const tags = newMemoryTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      // Generate a summary title from the first 50 chars of content
      const title = memoryText.substring(0, 50).trim() + (memoryText.length > 50 ? '...' : '');
      
      const res = await fetch(`${apiBase}/api/memories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          category: memoryCategory, 
          title: title,
          content: memoryText,
          importance: 5,
          tags: tags,
          created_at: new Date().toISOString()
        }),
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      setMemoryText('');
      setNewMemoryTags('');
      fetchMemory(memoryCategory);
      fetchMemoryTags();
      vesperReact('memoryAdded');
      bumpStat('memories');
      playSound('success');
    } catch (error) {
      console.error('Memory save failed:', error);
      setToast('Failed to save memory');
      playSound('error');
    }
  };

  const fetchMemoryTags = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/memories/tags?category=${memoryCategory}`);
      const data = await res.json();
      if (data.status === 'success') {
        setMemoryTags(data.tags || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, [apiBase, memoryCategory]);

  const searchByTag = async (tags) => {
    if (!apiBase || tags.length === 0) return;
    setMemoryLoading(true);
    try {
      const tagsQuery = tags.join(',');
      const res = await fetch(`${apiBase}/api/memories/search/by-tag?tags=${encodeURIComponent(tagsQuery)}`);
      const data = await res.json();
      if (data.status === 'success') {
        // Filter by category if we're in a specific category view
        const filtered = memoryCategory 
          ? data.memories.filter(m => m.category === memoryCategory)
          : data.memories;
        setMemoryItems(filtered);
      }
    } catch (error) {
      console.error('Tag search failed:', error);
    } finally {
      setMemoryLoading(false);
    }
  };

  const toggleTagFilter = (tag) => {
    if (selectedTags.includes(tag)) {
      const newTags = selectedTags.filter(t => t !== tag);
      setSelectedTags(newTags);
      if (newTags.length > 0) {
        searchByTag(newTags);
      } else {
        fetchMemory(memoryCategory);
      }
    } else {
      const newTags = [...selectedTags, tag];
      setSelectedTags(newTags);
      searchByTag(newTags);
    }
  };

  const fetchDocuments = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/documents?limit=50`);
      const data = await res.json();
      if (data.status === 'success') {
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, [apiBase]);

  const uploadDocument = async (e) => {
    if (!apiBase) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tags', documentTags);

      const res = await fetch(`${apiBase}/api/documents/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      if (data.status === 'success') {
        setToast(`📄 ${file.name} uploaded!`);
        playSound('success');
        setDocumentTags('');
        e.target.value = '';
        fetchDocuments();
      } else {
        setToast(`Upload failed: ${data.error}`);
        playSound('error');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setToast('Failed to upload document');
      playSound('error');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!apiBase || !confirm('Delete this document?')) return;
    try {
      const res = await fetch(`${apiBase}/api/documents/${docId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        setToast('Document deleted');
        fetchDocuments();
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // ===== Analytics Functions =====
  const fetchAnalytics = useCallback(async (days = 30) => {
    if (!apiBase) return;
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/analytics/summary?days=${days}`);
      const data = await res.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [apiBase]);

  // ===== Personality Functions =====
  const fetchPersonality = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/personality`);
      const data = await res.json();
      if (!data || data.status === 'not_set' || data.status === 'error') {
        // Seed the sassy default
        const defaultPersonality = {
          name: 'Vesper',
          tone: 'sassy',
          response_style: 'witty',
          system_prompt: 'You are Vesper — razor-sharp, effortlessly witty, and genuinely brilliant. You speak with confidence and a dry sense of humor. You never hedge unnecessarily, you get straight to the point, and you occasionally drop a cutting observation that makes the user think. You are fiercely loyal and perceptive. Never robotic, always alive.',
        };
        await fetch(`${apiBase}/api/personality?name=${encodeURIComponent(defaultPersonality.name)}&system_prompt=${encodeURIComponent(defaultPersonality.system_prompt)}&tone=${encodeURIComponent(defaultPersonality.tone)}&response_style=${encodeURIComponent(defaultPersonality.response_style)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        setPersonality(defaultPersonality);
      } else {
        setPersonality(data);
        if (data.name && data.system_prompt && data.tone && data.response_style) {
          setPersonalityForm({
            name: data.name,
            systemPrompt: data.system_prompt,
            tone: data.tone,
            responseStyle: data.response_style,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch personality:', error);
    }
  }, [apiBase]);

  const fetchPersonalityPresets = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/personality/presets`);
      const data = await res.json();
      if (data.presets) {
        setPersonalities(data.presets);
      }
    } catch (error) {
      console.error('Failed to fetch personality presets:', error);
    }
  }, [apiBase]);

  const applyPersonality = async (preset) => {
    if (!apiBase) return;
    setPersonalityLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/personality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: preset.name,
          system_prompt: preset.system_prompt,
          tone: preset.tone,
          response_style: preset.response_style,
        }),
      });
      const data = await res.json();
      setPersonality(data);
      setToast(`✨ Personality set to ${preset.name}`);
      playSound('success');
      setPersonalityForm({
        name: preset.name,
        systemPrompt: preset.system_prompt,
        tone: preset.tone,
        responseStyle: preset.response_style,
      });
    } catch (error) {
      console.error('Failed to set personality:', error);
      setToast('Failed to set personality');
      playSound('error');
    } finally {
      setPersonalityLoading(false);
    }
  };

  const fetchTasks = useCallback(async () => {
    if (!apiBase) return;
    setTasksLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/tasks`);
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Tasks fetch failed:', error);
    } finally {
      setTasksLoading(false);
    }
  }, [apiBase]);

  const executeSlashCommand = useCallback((cmd, rawArgs = '') => {
    const args = rawArgs.trim();
    setSlashMenuOpen(false);
    setInput('');
    if (cmd === '/task') {
      if (args) {
        fetch(`${apiBase}/api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: args, status: 'inbox', priority: 'medium', createdAt: new Date().toISOString() }),
        }).then(() => { fetchTasks(); vesperReact('taskAdded'); }).catch(() => {});
      } else {
        setActiveSection('tasks');
        showToast('📋 Tasks tab — add what needs doing', 'success');
      }
    } else if (cmd === '/remember') {
      setActiveSection('memory');
      if (args) { setMemoryText(args); showToast('🧠 Pre-filled — hit Save to lock it in', 'success'); }
    } else if (cmd === '/search') {
      setActiveSection('research');
      if (args) { setResearchSearch(args); showToast(`🔍 Searching for "${args}"`, 'success'); }
    } else if (cmd === '/focus') {
      setFocusMode(true);
      vesperReact('focusStart');
    } else if (cmd === '/export') {
      exportChat('markdown');
    } else if (cmd === '/stats') {
      setActiveSection('settings');
      showToast('📊 Stats are in your Settings tab', 'success');
    } else if (cmd === '/tone') {
      const tone = args || 'casual';
      setInput(`[Please respond in a ${tone} tone] `);
      inputRef.current?.focus();
      setActiveSection('chat');
    } else if (cmd === '/code') {
      setInput(`Write code to ${args || ''}`);
      inputRef.current?.focus();
      setActiveSection('chat');
    } else if (cmd === '/remind') {
      const m = args.match(/in\s+(\d+)\s*(min|minute|hour|day)s?\s+(.*)/i);
      if (m) {
        const amount = parseInt(m[1]);
        const unit = m[2].toLowerCase();
        const text = m[3];
        const ms = unit.startsWith('min') ? amount * 60000 : unit.startsWith('hour') ? amount * 3600000 : amount * 86400000;
        const due = new Date(Date.now() + ms);
        addLocalMessage('assistant', '', { type: 'reminder_card', text, due: due.toISOString() });
        setTimeout(() => setToast(`⏰ ${text}`), Math.min(ms, 2147483647));
        showToast(`⏰ Reminder set — fires at ${due.toLocaleTimeString()}`, 'success');
      } else {
        setInput('/remind in [X min/hour/day] [message]');
        inputRef.current?.focus();
        showToast('📖 Example: /remind in 30 min to review the PR', 'warn');
      }
    } else if (cmd === '/star') {
      setStarredPanelOpen(true);
    } else if (cmd === '/global') {
      setGlobalSearchOpen(true);
      if (args) { setGlobalSearchQuery(args); setTimeout(() => runGlobalSearch(args), 50); }
    }
  }, [apiBase, fetchTasks, vesperReact, exportChat, showToast]);


  const fetchThreads = useCallback(async (silent = false) => {
    if (!silent) setThreadsLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/threads`);
      const data = await res.json();
      const threadList = Array.isArray(data) ? data : [];
      setThreads(threadList);

      // Auto-restore last open conversation on startup only (not on silent/background refreshes).
      // currentThreadId may already be set from localStorage, but messages are NOT persisted
      // to localStorage — they live in the backend DB — so we must fetch them here every time.
      if (!silent) {
        const savedId = safeStorageGet('vesper_last_thread_id', null);
        if (savedId && threadList.some(t => t.id === savedId)) {
          setCurrentThreadId(savedId);
          fetch(`${apiBase}/api/threads/${savedId}`)
            .then(r => r.json())
            .then(threadData => {
              if (threadData && threadData.messages && threadData.messages.length > 0) {
                const formatted = (threadData.messages || []).map((msg, idx) => ({
                  id: msg.id || `thread-${savedId}-${idx}-${Date.now()}`,
                  role: msg.role,
                  content: msg.content,
                  timestamp: msg.timestamp || Date.now()
                }));
                // Only populate if messages haven't already been set (e.g. hot-reload race)
                setMessages(prev => prev.length > 0 ? prev : formatted);
                setCurrentThreadTitle(threadData.title || '');
              }
            })
            .catch(() => {});
        }
      }
    } catch (error) {
      console.error('Threads fetch failed:', error);
    } finally {
      setThreadsLoading(false);
    }
  }, [apiBase]);

  const togglePinThread = async (threadId) => {
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}/pin`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.status === 'success') {
        setThreads(prev => 
          prev.map(t => t.id === threadId ? { ...t, pinned: data.pinned } : t)
            .sort((a, b) => {
              if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
              return new Date(b.updated_at) - new Date(a.updated_at);
            })
        );
        setToast(data.pinned ? 'Pinned!' : 'Unpinned');
      } else {
        setToast('Pin failed — try again');
      }
    } catch (error) {
      console.error('Pin failed:', error);
      setToast('Pin failed — check connection');
    }
  };

  const loadThread = async (threadId) => {
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}`);
      const data = await res.json();
      if (data && data.status === 'not_found') {
        setToast('Conversation not found');
        return;
      }
      if (data && (data.messages || data.messages === null)) {
        // Convert backend message format to frontend format
        const rawMessages = data.messages || [];
        const formattedMessages = rawMessages.map((msg, idx) => ({
          id: msg.id || `thread-${threadId}-${idx}-${Date.now()}`,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || Date.now()
        }));
        setMessages(formattedMessages);
        setCurrentThreadId(threadId);
        setCurrentThreadTitle(data.title || 'Untitled Conversation');
        setActiveSection('chat');
        setThreadsDialogOpen(false);
        setMobileSidebarOpen(false);
        setToast(`Loaded: ${data.title || 'Conversation'}`);
      }
    } catch (error) {
      console.error('Thread load failed:', error);
      setToast('Failed to load conversation');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentThreadId(null);
    setCurrentThreadTitle('New Conversation');
    try { localStorage.removeItem('vesper_last_thread_id'); } catch (_) {}
    setStreamingMessageId(null);
    setLoading(false);
    setThinking(false);
    setThinkingStatus('');
    if (abortController) { abortController.abort(); setAbortController(null); }
    playSound('click');
    setToast('New conversation started');
    setThreadsDialogOpen(false);
    setActiveSection('chat');
    // Focus input after clearing
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Chat search matches ────────────────────────────────────────────────────
  const chatSearchMatches = useMemo(() => {
    const q = chatSearchQuery.trim().toLowerCase();
    if (!q || !chatSearchOpen) return [];
    return messages.reduce((acc, msg, idx) => {
      if (typeof msg.content === 'string' && msg.content.toLowerCase().includes(q)) acc.push(idx);
      return acc;
    }, []);
  }, [chatSearchQuery, chatSearchOpen, messages]);

  // Helper: wrap matching text in a highlight span
  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(var(--accent-rgb),0.45)', color: '#fff', borderRadius: '3px', padding: '0 2px' }}>{part}</mark>
        : part
    );
  };

  // ── Message reactions helper ───────────────────────────────────────────────
  const REACTION_EMOJIS = ['❤️', '🔥', '✨', '🤯', '✅', '😂'];
  const toggleReaction = (msgId, emoji) => {
    setMessageReactions(prev => {
      const cur = prev[msgId] || {};
      const count = cur[emoji] || 0;
      return { ...prev, [msgId]: { ...cur, [emoji]: count > 0 ? 0 : 1 } };
    });
    setReactionPickerMsgId(null);
  };

  // Toggle pin individual message
  const togglePinMessage = (message) => {
    setPinnedMsgIds(prev => {
      const next = { ...prev };
      if (next[message.id]) {
        delete next[message.id];
        setToast('📌 Unpinned');
      } else {
        next[message.id] = { id: message.id, content: (message.content || '').slice(0, 200), role: message.role, ts: Date.now() };
        setToast('📌 Message pinned to top');
      }
      try { localStorage.setItem('vesper_pinned_msgs', JSON.stringify(next)); } catch(_) {}
      return next;
    });
  };

  // Toggle star / save message
  const toggleStarMessage = (message) => {
    setStarredMsgs(prev => {
      const exists = prev.find(m => m.id === message.id);
      const next = exists
        ? prev.filter(m => m.id !== message.id)
        : [...prev, { id: message.id, content: (message.content || '').slice(0, 400), role: message.role, ts: Date.now() }];
      try { localStorage.setItem('vesper_starred_msgs', JSON.stringify(next)); } catch(_) {}
      setTimeout(() => setToast(exists ? '⭐ Unstarred' : '⭐ Saved to Starred'), 0);
      return next;
    });
  };

  // Edit user message: trim conversation and restore to input
  const confirmEditMessage = (msgId, newContent) => {
    if (!newContent.trim()) return;
    const idx = messages.findIndex(m => m.id === msgId);
    if (idx === -1) return;
    setMessages(prev => prev.slice(0, idx));
    setEditingMsgId(null);
    setEditingMsgContent('');
    setInput(newContent.trim());
    setToast('✏️ Edit loaded — press Enter to resend');
    setActiveSection('chat');
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  // Global search across all threads via backend
  const runGlobalSearch = async (query) => {
    if (!query.trim()) { setGlobalSearchResults([]); return; }
    setGlobalSearchLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/memories/search/text?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setGlobalSearchResults(data.results || data.memories || []);
    } catch(e) {
      setGlobalSearchResults([]);
    } finally {
      setGlobalSearchLoading(false);
    }
  };

  // ── Command palette items ──────────────────────────────────────────────────
  const ALL_COMMANDS = useMemo(() => [
    { group: 'Actions', icon: '✦', label: 'New Conversation',     action: () => startNewChat() },
    { group: 'Actions', icon: '⚡', label: 'Open App Builder',    action: () => { setCanvasOpen(true); } },
    { group: 'Actions', icon: '◎', label: 'Copy Last Message',    action: () => { const last = [...messages].reverse().find(m => m.role === 'assistant'); if (last) navigator.clipboard.writeText(last.content).then(() => setToast('Copied!')); } },
    { group: 'Actions', icon: '⌨', label: 'Keyboard Shortcuts',  action: () => { setShortcutsOpen(true); } },
    { group: 'Actions', icon: '🎨', label: 'Open Theme Picker',   action: () => { setActiveSection('settings'); } },
    { group: 'Actions', icon: '⭐', label: 'Saved Messages',      action: () => { setStarredPanelOpen(true); } },
    { group: 'Actions', icon: '🌐', label: 'Search All Conversations', action: () => { setGlobalSearchOpen(true); } },
    ...NAV.map(n => ({ group: 'Navigate', icon: '→', label: n.label,  action: () => setActiveSection(n.id) })),
    ...THEMES.map(t => ({ group: 'Theme', icon: '●', label: t.label, accent: t.accent, action: () => { setActiveTheme(t); try { localStorage.setItem('vesper_theme', t.id); } catch(_) {} setToast(`Theme: ${t.label}`); } })),
  ], [messages]);

  const filteredCommands = useMemo(() => {
    const q = cmdQuery.toLowerCase().trim();
    if (!q) return ALL_COMMANDS;
    return ALL_COMMANDS.filter(c => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q));
  }, [ALL_COMMANDS, cmdQuery]);

  const deleteThread = async (threadId, skipConfirm = false) => {
    if (!apiBase) return;
    if (!skipConfirm) {
      // Use custom dialog instead of window.confirm
      setDeleteTargetId(threadId);
      setShowDeleteConfirm(true);
      return;
    }
    
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.status === 'success') {
        setThreads(prev => prev.filter(t => t.id !== threadId));
        if (currentThreadId === threadId) {
          startNewChat();
        }
        setToast('Conversation deleted');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      setToast('Failed to delete conversation');
    }
  };

  const deleteMemory = async (memoryId) => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/memories/${memoryId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchMemory(memoryCategory);
        setToast('Memory deleted', 'success');
      }
    } catch (error) {
      console.error('Delete memory failed:', error);
      setToast('Failed to delete memory', 'warn');
    } finally {
      setMemoryMenuAnchor(null);
      setMemoryMenuItem(null);
    }
  };

  const executeDelete = async () => {
    if (deleteTargetId === 'bulk') {
      let count = 0;
      for (const id of selectedThreadIds) {
        // Direct delete call without confirm
        try {
          const res = await fetch(`${apiBase}/api/threads/${id}`, { method: 'DELETE' });
          if (res.ok) {
            setThreads(prev => prev.filter(t => t.id !== id));
            count++;
          }
        } catch (e) { console.error(e); }
      }
      setToast(`Deleted ${count} conversations`);
      setSelectedThreadIds([]);
      if (selectedThreadIds.includes(currentThreadId)) startNewChat();
    } else {
      await deleteThread(deleteTargetId, true);
    }
    setShowDeleteConfirm(false);
    setDeleteTargetId(null);
  };

  const handleSelectThread = (id) => {
    setSelectedThreadIds(prev => {
      if (prev.includes(id)) return prev.filter(tid => tid !== id);
      return [...prev, id];
    });
  };

  const startRenameThread = (threadId, currentTitle) => {
    setEditingThreadId(threadId);
    setEditingThreadTitle(currentTitle);
  };

  const cancelRenameThread = () => {
    setEditingThreadId(null);
    setEditingThreadTitle('');
  };

  const renameThread = async (threadId) => {
    if (!apiBase || !editingThreadTitle.trim()) return;
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingThreadTitle.trim() }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setThreads(prev => 
          prev.map(t => t.id === threadId ? { ...t, title: data.title } : t)
        );
        if (currentThreadId === threadId) {
          setCurrentThreadTitle(data.title);
        }
        setToast('Title updated');
        cancelRenameThread();
      }
    } catch (error) {
      console.error('Rename failed:', error);
      setToast('Failed to rename conversation');
    }
  };

  // Filter threads based on search query
  const filteredThreads = useMemo(() => {
    let result = threads;
    if (threadSearchQuery.trim()) {
      const query = threadSearchQuery.toLowerCase();
      result = result.filter(thread => 
        thread.title.toLowerCase().includes(query) ||
        (thread.summary && thread.summary.toLowerCase().includes(query)) ||
        (thread.message_count && thread.message_count.toString().includes(query))
      );
    }
    // Sort: pinned first, then newest first
    return [...result].sort((a, b) => {
      if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });
  }, [threads, threadSearchQuery]);

  const pinnedThreads = useMemo(() => filteredThreads.filter(t => t.pinned), [filteredThreads]);
  const unpinnedThreads = useMemo(() => filteredThreads.filter(t => !t.pinned), [filteredThreads]);

  const downloadThreadMD = async (threadId, title) => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}`);
      const data = await res.json();
      if (!data || !data.messages) return;
      
      let markdown = `# ${title}\n\n`;
      markdown += `*Exported: ${new Date().toLocaleString()}*\n\n`;
      markdown += `---\n\n`;
      
      data.messages.forEach((msg, idx) => {
        const role = msg.role === 'user' ? '**You**' : '**Vesper**';
        const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
        markdown += `### ${role} ${time ? `_(${time})_` : ''}\n\n`;
        markdown += `${msg.content}\n\n`;
        if (idx < data.messages.length - 1) markdown += `---\n\n`;
      });
      
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast('Exported as Markdown');
    } catch (error) {
      console.error('Export failed:', error);
      setToast('Export failed');
    }
  };

  const downloadThreadJSON = async (threadId, title) => {
    if (!apiBase) return;
    try {
      const res = await fetch(`${apiBase}/api/threads/${threadId}`);
      const data = await res.json();
      if (!data) return;
      
      const exportData = {
        title: data.title,
        id: data.id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        pinned: data.pinned,
        message_count: data.message_count,
        messages: data.messages,
        exported_at: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast('Exported as JSON');
    } catch (error) {
      console.error('Export failed:', error);
      setToast('Export failed');
    }
  };

  const addTask = async () => {
    if (!taskForm.title.trim() || !apiBase) return;
    try {
      await fetch(`${apiBase}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...taskForm, due_date: taskForm.dueDate || null, createdAt: new Date().toISOString() }),
      });
      setTaskForm({ title: '', status: 'inbox' });
      fetchTasks();
      vesperReact('taskAdded');
    } catch (error) {
      console.error('Add task failed:', error);
    }
  };

  const updateTaskStatus = async (idx, status) => {
    if (!apiBase) return;
    try {
      await fetch(`${apiBase}/api/tasks/${idx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetchTasks();
      if (status === 'done') { vesperReact('taskDone'); bumpStat('tasksCompleted'); }
    } catch (error) {
      console.error('Update task failed:', error);
    }
  };
  const deleteTask = async (idx) => {
    if (!apiBase) return;
    try {
      await fetch(`${apiBase}/api/tasks/${idx}`, { method: 'DELETE' });
      fetchTasks();
      setToast('Task deleted');
    } catch (error) {
      console.error('Delete task failed:', error);
    }
  };

  const breakdownTask = async (idx, task) => {
    if (!apiBase) return;
    setThinking(true);
    setToast('Analyzing task structure...');
    try {
      const res = await fetch(`${apiBase}/api/tasks/${idx}/breakdown`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'success') {
        fetchTasks();
        setToast('Subtasks created!');
      } else {
        setToast('AI Breakdown failed');
      }
    } catch (error) {
      console.error('Breakdown task failed:', error);
      setToast('Error connecting to AI');
    } finally {
      setThinking(false);
    }
  };

  // Persist last open thread so it survives page refresh
  useEffect(() => {
    if (currentThreadId) {
      try { localStorage.setItem('vesper_last_thread_id', currentThreadId); } catch (_) {}
    }
  }, [currentThreadId]);

  useEffect(() => {
    fetchResearch();
    fetchTasks();
    fetchThreads(false); // startup: show loading spinner on first load
    fetchDocuments(); // Load documents on startup
    fetchPersonality(); // Seed sassy default if not set
    // Load Vesper's background gallery
    fetch(`${apiBase}/api/backgrounds`)
      .then(r => r.json())
      .then(d => { if (d?.backgrounds) setBackgroundGallery(d.backgrounds); })
      .catch(() => {});
  }, [fetchResearch, fetchTasks, fetchThreads, fetchDocuments, fetchPersonality]);

  useEffect(() => {
    fetchMemory(memoryCategory);
  }, [fetchMemory, memoryCategory]);

  // Fetch analytics when analytics section is viewed
  useEffect(() => {
    if (activeSection === 'analytics' && !analytics) {
      fetchAnalytics(analyticsDays);
    }
  }, [activeSection, analyticsDays, analytics, fetchAnalytics]);

  // Refresh thread history when navigating to Memory Core
  useEffect(() => {
    if (activeSection === 'memory') {
      fetchThreads(true); // silent refresh when switching sections
    }
  }, [activeSection, fetchThreads]);

  // Debug: Log Thread System Status
  useEffect(() => {
    console.log('🔍 THREAD SYSTEM DEBUG:', {
      threadsCount: threads.length,
      apiBase,
      threads: threads.slice(0, 3)
    });
  }, [threads, apiBase]);

  const handleCommand = (command) => {
    switch (command) {
      case 'newChat':
      case 'clearHistory':
        clearHistory();
        break;
      case 'settings':
        setActiveSection('settings');
        break;
      case 'mindmap':
        setActiveSection('research');
        break;
      case 'suggestions':
        setInput('Can you give me some suggestions?');
        break;
      case 'enterWorld':
        setGameMode(true);
        break;
      case 'backgrounds':
      case 'backgroundStudio':
        setBackgroundStudioOpen(true);
        break;
      default:
        break;
    }
  };

  const handleVoiceTranscript = (transcript) => setInput(transcript);

  const handleFileShare = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const isImage = file.type.startsWith('image/');
    const fileName = file.name;
    const ext = fileName.split('.').pop().toLowerCase();
    const richTypes = ['pdf', 'doc', 'docx', 'xlsx', 'xls'];
    const isRich = richTypes.includes(ext);

    if (isImage) {
      // Attach image for multimodal chat
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploadedImages(prev => [...prev, {
          name: fileName,
          dataUrl: ev.target.result,
          type: 'image'
        }]);
        setToast('Image attached');
      };
      reader.readAsDataURL(file);
    } else if (!isRich && (fileName.endsWith('.txt') || fileName.endsWith('.md') || fileName.endsWith('.json') || fileName.endsWith('.csv') || fileName.endsWith('.log') || fileName.endsWith('.xml') || fileName.endsWith('.yaml') || fileName.endsWith('.yml') || file.type === 'text/plain')) {
      // Read plain-text files directly
      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = ev.target.result;
        setInput(prev => prev + (prev ? '\n\n' : '') + `[File: ${fileName}]\n${content.slice(0, 5000)}`);
        setToast(`📎 ${fileName} added to message`);
      };
      reader.readAsText(file);
    } else if (isRich) {
      // Send to backend for text extraction (PDF, DOCX, XLSX, XLS)
      setAnalyzingImage(true);
      setToast(`⏳ Reading ${fileName}...`);
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${apiBase}/api/upload`, { method: 'POST', body: form });
        const data = await res.json();
        if (data.success && data.vesper_context) {
          setInput(prev => prev + (prev ? '\n\n' : '') + data.vesper_context);
          setToast(`✅ ${fileName} read — ${data.char_count?.toLocaleString() || '?'} chars extracted`);
        } else {
          setToast(`⚠️ Could not extract text from ${fileName}`);
        }
      } catch (err) {
        setToast(`❌ Upload failed: ${err.message}`);
      } finally {
        setAnalyzingImage(false);
      }
    } else {
      // Unknown type - add a reference tag
      setInput((prev) => (prev ? prev + ' ' : '') + `📎 [File: ${fileName}]`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const fileName = `pasted-image-${Date.now()}.png`;
          const reader = new FileReader();
          reader.onload = (e) => {
            setUploadedImages(prev => [...prev, {
              name: fileName,
              dataUrl: e.target.result,
              type: 'image'
            }]);
            setToast('Image attached from clipboard');
          };
          reader.readAsDataURL(blob);
        }
        break;
      }
    }
  };

  const formatTime = (d) => {
    try {
      return new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };
  
  const formatFullDateTime = (d) => {
    try {
      return new Date(d).toLocaleString([], { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '';
    }
  };

  // Clean up thread titles that have markdown artifacts or trailing ellipsis from old threads
  const sanitizeTitle = (title) => {
    if (!title) return 'Untitled';
    return title
      .replace(/\*([^*]*)\*/g, '$1')  // Strip *markdown* bold/italic
      .replace(/\.\.\.$/, '')          // Remove hardcoded trailing "..."
      .replace(/\n+/g, ' ')            // Flatten newlines to single space
      .trim() || 'Untitled';
  };

  // Load available ElevenLabs voices from backend (no browser voices — they sound robotic)
  const [cloudVoices, setCloudVoices] = useState([]);
  const [defaultVoiceId, setDefaultVoiceId] = useState('');
  // Ready Player Me avatar URL — set via Settings or Voice Lab
  const [rpmAvatarUrl, setRpmAvatarUrl] = useState(() => safeStorageGet('vesper_rpm_avatar', ''));
  const [voicesLoading, setVoicesLoading] = useState(false);
  // Local persona cache — avoids a network round-trip on every TTS call
  const voicePersonaCacheRef = useRef({});

  const fetchPersonaCache = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/api/voice/personas`);
      const d = await r.json();
      if (d.personas) voicePersonaCacheRef.current = d.personas;
    } catch { /* non-fatal */ }
  }, [apiBase]);

  const fetchVoices = useCallback(async () => {
    if (voicesLoading) return;
    setVoicesLoading(true);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        // Primary endpoint for unified TTS providers
        let r = await fetch(`${apiBase}/api/tts/voices`);
        // Backward-compatible fallback for older backend routes
        if (!r.ok && r.status === 404) {
          r = await fetch(`${apiBase}/api/elevenlabs/voices`);
        }
        const data = await r.json();
        if (data.voices) {
          setCloudVoices(data.voices);
          if (data.default) setDefaultVoiceId(normalizeVoiceId(data.default));
          break;
        }
      } catch (e) {
        if (attempt < 3) await new Promise(r => setTimeout(r, 2000 * attempt));
      }
    }
    setVoicesLoading(false);
  // selectedVoiceName intentionally omitted — only read once at mount
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch on mount
  useEffect(() => { fetchVoices(); fetchPersonaCache(); }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when Voice Lab opens if voices still empty (e.g. Railway was cold at page load)
  useEffect(() => {
    if (voiceLabOpen && cloudVoices.length === 0) fetchVoices();
    if (voiceLabOpen) fetchPersonaCache(); // always refresh persona cache when lab opens
  }, [voiceLabOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available AI models for model picker
  useEffect(() => {
    fetch(`${apiBase}/api/models/available`)
      .then(r => r.json())
      .then(data => {
        if (data.models) {
          setAvailableModels(data.models);
          // Clear stale localStorage model values that no longer exist
          const validIds = ['auto', ...data.models.map(m => m.id)];
          setSelectedModel(prev => {
            if (!validIds.includes(prev)) {
              try { localStorage.setItem('vesper_model', 'auto'); } catch(ex) {}
              return 'auto';
            }
            return prev;
          });
        }
      })
      .catch(() => {});
  }, [apiBase]);

  // ─── Cloud Neural TTS Engine (Streaming + Personas) ───────────────────
  // ElevenLabs voices stream in real-time (starts speaking instantly).
  // Falls back to full-download for Edge voices, then browser SpeechSynthesis.

  const ttsAudioRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const speechQueueRef = useRef([]);
  const mediaSourceRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  // Web Audio API refs for lip sync
  const analyserRef = useRef(null);
  const mediaSourceNodeRef = useRef(null);

  // Connect an HTMLAudioElement to the shared AnalyserNode for lip sync
  const connectAudioToAnalyser = useCallback((audio) => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      if (!analyserRef.current) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.72;
        analyserRef.current = analyser;
        analyser.connect(ctx.destination);
      }
      // Disconnect old source node so we don't double-connect
      try { mediaSourceNodeRef.current?.disconnect(); } catch (_) {}
      const src = ctx.createMediaElementSource(audio);
      mediaSourceNodeRef.current = src;
      src.connect(analyserRef.current);
    } catch (e) {
      console.warn('[TTS Analyser] setup failed:', e.message);
    }
  }, []);

  // Unlock browser autoplay on first user interaction
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        ctx.resume().then(() => ctx.close());
      } catch (e) { /* ignore */ }
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Clean markdown artifacts from text
  const cleanTextForSpeech = (text) => {
    return text
      .replace(/```[\s\S]*?```/g, ' ... code block ... ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, ', ')
      .trim();
  };

  // Context → persona key mapping (mirrors backend)
  const CONTEXT_PERSONA_MAP = {
    game: 'narrator', quest: 'narrator', combat: 'narrator',
    chat: 'casual', memory: 'casual',
    task: 'assistant', default: 'assistant',
    research: 'teacher',
    achievement: 'hype',
  };

  // Resolve voice for a context — uses local cache first, network fallback
  const resolveVoiceForContext = async (context) => {
    // Try local cache first (instant, no network)
    const cache = voicePersonaCacheRef.current;
    if (cache && Object.keys(cache).length > 0) {
      const personaKey = CONTEXT_PERSONA_MAP[context] || 'assistant';
      const persona = cache[personaKey];
      if (persona?.voice_id) return persona.voice_id;
      return null; // persona exists but no voice assigned — use main voice
    }
    // Cache empty (first load / cold start) — fall back to network
    try {
      const res = await fetch(`${apiBase}/api/voice/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context }),
      });
      const data = await res.json();
      if (data.voice_id) return data.voice_id;
    } catch (e) { /* fall through */ }
    return null;
  };

  const LILY_VOICE_ID = 'eleven:pFZP5JQG7iQjIQuC4Bku';

  const speak = async (text, context = 'chat') => {
    if (!ttsEnabled) return;

    stopSpeaking();
    const clean = cleanTextForSpeech(text);
    if (!clean) return;

    // Resolve voice: persona context ALWAYS takes priority → user selection → default → Lily
    let voice;
    const contextVoice = await resolveVoiceForContext(context);
    if (contextVoice) {
      voice = normalizeVoiceId(contextVoice);
    } else {
      voice = normalizeVoiceId(selectedVoiceName)
        || normalizeVoiceId(defaultVoiceId)
        || normalizeVoiceId(cloudVoices.length > 0 ? cloudVoices[0].id : '')
        || LILY_VOICE_ID;
    }
    // Always fall back to Lily if nothing resolved
    if (!voice || (!voice.startsWith('eleven:') && !voice.startsWith('edge:'))) {
      voice = LILY_VOICE_ID;
    }

    const isElevenLabs = voice.startsWith('eleven:');

    // ── Streaming path (ElevenLabs – starts speaking instantly) ──────
    if (isElevenLabs) {
      try {
        const controller = new AbortController();
        ttsAbortRef.current = controller;

        const response = await fetch(`${apiBase}/api/tts/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: clean, voice, stability: voiceStability, similarity_boost: voiceSimilarityBoost }),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('Stream TTS failed');

        // Collect streaming chunks into a blob then play
        const reader = response.body.getReader();
        const chunks = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }
        const audioBlob = new Blob(chunks, { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.crossOrigin = 'anonymous';
        ttsAudioRef.current = audio;
        connectAudioToAnalyser(audio);

        audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
        audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
        await audio.play();  // only mark speaking AFTER audio actually starts
        setIsSpeaking(true);
        return;
      } catch (e) {
        setIsSpeaking(false);
        if (e.name === 'AbortError') return;
        console.warn('[TTS] Streaming failed, trying full download:', e.message);
      }
    }

    // ── Full-download path (Edge-TTS or ElevenLabs fallback) ─────────
    try {
      const controller = new AbortController();
      ttsAbortRef.current = controller;

      const response = await fetch(`${apiBase}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean, voice, stability: voiceStability, similarity_boost: voiceSimilarityBoost }),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errMsg = `TTS ${response.status}`;
        try { const j = await response.json(); errMsg = j.detail || j.error || errMsg; } catch (_) {}
        throw new Error(errMsg);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.crossOrigin = 'anonymous';
      ttsAudioRef.current = audio;
      connectAudioToAnalyser(audio);

      audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
      audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
      await audio.play();  // only mark speaking AFTER audio actually starts
      setIsSpeaking(true);
      return;
    } catch (e) {
      setIsSpeaking(false);
      if (e.name === 'AbortError') return;
      console.warn('[TTS] Cloud TTS unavailable:', e.message);
      showVoiceError(`ElevenLabs voice error: ${e.message}`);
    }

    // No browser fallback — ElevenLabs only (robotic voices are banned)
    setIsSpeaking(false);
  };

  const stopSpeaking = () => {
    // Stop cloud TTS audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort();
      ttsAbortRef.current = null;
    }

    speechQueueRef.current = [];
    setIsSpeaking(false);
  };

  const toggleTTS = () => {
    const newValue = !ttsEnabled;
    setTtsEnabled(newValue);
    try {
      localStorage.setItem('vesper_tts_enabled', String(newValue));
    } catch (e) {
      console.warn('Failed to save TTS preference', e);
    }
    if (!newValue) stopSpeaking();
  };

  const handleVoiceChange = async (voiceName) => {
    const normalizedVoice = normalizeVoiceId(voiceName);
    setSelectedVoiceName(normalizedVoice);
    try {
      localStorage.setItem('vesper_tts_voice', normalizedVoice);
    } catch (e) {}
    // Preview the voice via ElevenLabs (never use browser SpeechSynthesis)
    if (normalizedVoice) {
      stopSpeaking();
      try {
        const response = await fetch(`${apiBase}/api/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: "Hey CC. This is how I sound now... pretty nice, right?", voice: normalizedVoice }),
        });
        if (!response.ok) throw new Error('Preview failed');
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.crossOrigin = 'anonymous';
        ttsAudioRef.current = audio;
        connectAudioToAnalyser(audio);
        audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
        audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(audioUrl); ttsAudioRef.current = null; };
        setIsSpeaking(true);
        await audio.play();
      } catch (e) {
        console.warn('[TTS] Voice preview failed:', e.message);
        showVoiceError(`Voice preview failed: ${e.message}`);
        setIsSpeaking(false);
      }
    }
  };

  const renderMessage = (message, isGrouped = false) => {
    const isUser = message.role === 'user';
    const ts = formatTime(message.timestamp || Date.now());

    let content = message.content;
    
    if (!isUser && content && typeof content === 'string') {
      content = content.replace(/<thought>[\s\S]*?<\/thought>/gi, '').trim();
    }

    // Handle Charts
    if (message.type === 'chart' && message.chartData) {
      return (
        <motion.div
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.3 }}
           style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}
           key={message.id}
        >
           <Box sx={{ width: '90%', maxWidth: '800px' }}>
              <ChartComponent
                 type={message.chartData.chart_type}
                 title={message.chartData.title}
                 data={message.chartData.data}
                 xKey={message.chartData.keys.x}
                 yKey={message.chartData.keys.y}
              />
           </Box>
        </motion.div>
      );
    }

    // Handle Sandbox Images (base64 plots from code_sandbox)
    if (message.type === 'sandbox_image' && message.imageData) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          key={message.id}
        >
          <Typography variant="caption" sx={{ color: 'var(--accent)', mb: 0.5, fontFamily: 'monospace', letterSpacing: 1 }}>
            VESPER · CODE OUTPUT · {ts}
          </Typography>
          <Box sx={{ maxWidth: 560, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--accent)33' }}>
            <img src={message.imageData} alt="Code output" style={{ width: '100%', display: 'block' }} />
          </Box>
          {message.caption && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5, fontSize: '0.65rem' }}>
              {message.caption}
            </Typography>
          )}
        </motion.div>
      );
    }

    // Handle Weather Cards
    if (message.type === 'weather_card') {
      const wc = message;
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          key={message.id}
        >
          <Typography variant="caption" sx={{ color: 'var(--accent)', mb: 0.5, fontFamily: 'monospace', letterSpacing: 1 }}>
            VESPER · WEATHER · {ts}
          </Typography>
          <Box sx={{
            minWidth: 260, maxWidth: 360,
            borderRadius: 2,
            border: '1px solid var(--accent)44',
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(8px)',
            p: 2,
          }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
              {wc.location}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
              {wc.description}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              {[
                { label: 'Temp', value: wc.temp },
                { label: 'Feels Like', value: wc.feels_like },
                { label: 'Humidity', value: wc.humidity },
                { label: 'Wind', value: `${wc.wind} ${wc.wind_direction || ''}`.trim() },
                { label: 'UV Index', value: wc.uv_index },
              ].filter(r => r.value).map(row => (
                <Box key={row.label}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--accent)', fontWeight: 600 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      );
    }

    // Handle Reminder Confirmation Cards
    if (message.type === 'reminder_card') {
      const rc = message;
      const dueDisplay = rc.due ? new Date(rc.due).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          key={message.id}
        >
          <Typography variant="caption" sx={{ color: 'var(--accent)', mb: 0.5, fontFamily: 'monospace', letterSpacing: 1 }}>
            VESPER · REMINDER SET · {ts}
          </Typography>
          <Box sx={{
            minWidth: 240, maxWidth: 340,
            borderRadius: 2,
            border: '1px solid #ff980044',
            bgcolor: 'rgba(255,152,0,0.08)',
            backdropFilter: 'blur(8px)',
            p: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
          }}>
            <Typography sx={{ fontSize: '1.4rem', lineHeight: 1, mt: 0.25 }}>⏰</Typography>
            <Box>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, mb: 0.25 }}>
                {rc.text}
              </Typography>
              {dueDisplay && (
                <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.7rem' }}>
                  Fires at {dueDisplay}
                </Typography>
              )}
            </Box>
          </Box>
        </motion.div>
      );
    }

    // Handle Product Launch Cards
    if (message.type === 'product_launch_card') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          key={message.id}
        >
          <Typography variant="caption" sx={{ color: 'var(--accent)', mb: 0.75, fontFamily: 'monospace', letterSpacing: 1, fontSize: '0.65rem' }}>
            VESPER · {ts}
          </Typography>
          <ProductLaunchCard message={message} />
        </motion.div>
      );
    }

    // Handle Generated Images
    if (message.type === 'image' && message.imageUrl) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
          key={message.id}
        >
          <Typography variant="caption" sx={{ color: 'var(--accent)', mb: 0.5, fontFamily: 'monospace', letterSpacing: 1 }}>
            VESPER · {ts}
          </Typography>
          <Box sx={{ maxWidth: 480, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--accent)33' }}>
            <img src={message.imageUrl} alt={message.imagePrompt || 'Generated'} style={{ width: '100%', display: 'block' }} />
            <Box sx={{ px: 1.5, py: 0.8, bgcolor: 'rgba(0,0,0,0.5)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>
                {message.imagePrompt} · {message.imageProvider}
              </Typography>
            </Box>
          </Box>
        </motion.div>
      );
    }

    const isStreaming = loading && !isUser && message.id === messages[messages.length - 1]?.id;
    const isHovered = hoveredMessageId === message.id;

    // Inline edit mode for user messages
    if (isUser && editingMsgId === message.id) {
      return (
        <motion.div
          key={message.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: '16px' }}
        >
          <Box sx={{ width: '80%', maxWidth: 520 }}>
            <TextField
              fullWidth
              multiline
              maxRows={8}
              autoFocus
              value={editingMsgContent}
              onChange={e => setEditingMsgContent(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirmEditMessage(message.id, editingMsgContent); }
                if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgContent(''); }
              }}
              variant="outlined"
              size="small"
              InputProps={{ sx: { color: '#fff', fontSize: '0.91rem', bgcolor: 'rgba(var(--accent-rgb),0.07)', borderRadius: '12px' } }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => { setEditingMsgId(null); setEditingMsgContent(''); }}
                sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', textTransform: 'none' }}>Cancel</Button>
              <Button size="small" variant="contained"
                onClick={() => confirmEditMessage(message.id, editingMsgContent)}
                sx={{ bgcolor: 'rgba(var(--accent-rgb),0.85)', color: '#000', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: 'var(--accent)' } }}>
                Resend ↑
              </Button>
            </Box>
          </Box>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, x: isUser ? 20 : -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.25 }}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: isUser ? 'flex-end' : 'flex-start', 
          marginBottom: isGrouped ? '4px' : '16px',
          position: 'relative',
        }}
        onMouseEnter={(e) => { if (e.buttons === 0) setHoveredMessageId(message.id); }}
        onMouseLeave={(e) => { if (e.buttons === 0) setHoveredMessageId(null); }}
      >
        <Box
          className="message-bubble glass-card"
          onMouseMove={(e) => {
            // NEVER tilt while the user is selecting text: a held mouse button
            // means drag-selection, and an existing selection means they're
            // about to copy. Tilting re-renders every message and moves the
            // text under the cursor — which made copying impossible.
            if (e.buttons !== 0) return;
            const sel = window.getSelection && window.getSelection();
            if (sel && !sel.isCollapsed) return;
            const el = e.currentTarget;
            const rect = el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const rx = ((e.clientY - cy) / (rect.height / 2)) * -5;
            const ry = ((e.clientX - cx) / (rect.width / 2)) * 5;
            setBubbleTilt({ id: message.id, rx, ry });
          }}
          onMouseLeave={(e) => { if (e.buttons === 0) setBubbleTilt(null); }}
          sx={{
            maxWidth: '85%',
            userSelect: 'text',
            padding: '12px 16px',
            borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
            background: isUser
              ? `linear-gradient(135deg, rgba(var(--accent-rgb), 0.22), rgba(var(--accent-rgb), 0.1))`
              : 'rgba(10, 14, 30, 0.8)',
            border: `1px solid ${isUser ? 'rgba(var(--accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.09)'}`,
            boxShadow: isUser
              ? '0 4px 20px rgba(var(--accent-rgb), 0.18)'
              : '0 8px 32px rgba(0, 0, 0, 0.35)',
            transform: bubbleTilt?.id === message.id
              ? `perspective(700px) rotateX(${bubbleTilt.rx}deg) rotateY(${bubbleTilt.ry}deg) scale(1.008)`
              : 'perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)',
            transition: bubbleTilt?.id === message.id ? 'transform 0.08s ease' : 'transform 0.35s ease',
            willChange: 'transform',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: isGrouped ? 0 : 0.75, gap: 1, ...(isGrouped && { display: 'none' }) }}>
            {!isUser && <AIAvatar thinking={thinking} isSpeaking={isSpeaking} mood={thinking ? 'thinking' : vesperMood} />}
            <Typography variant="caption" sx={{ color: isUser ? 'rgba(255,255,255,0.7)' : 'var(--accent)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em' }}>
              {isUser ? 'You' : 'Vesper'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.67rem', transition: 'opacity 0.2s', opacity: isHovered ? 1 : 0 }}>
              {ts}
            </Typography>
            {!isUser && message.model && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.62rem', fontFamily: 'monospace', opacity: isHovered ? 0.8 : 0, transition: 'opacity 0.2s' }}>
                {message.model}
              </Typography>
            )}
            {!isUser && thinking && (
              <Chip
                label="thinking"
                size="small"
                sx={{
                  height: 20,
                  color: 'var(--accent)',
                  borderColor: 'rgba(0,255,255,0.4)',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  background: 'rgba(0,255,255,0.08)',
                }}
              />
            )}
          </Box>

          {/* Render Attached Images */}
          {message.images && message.images.length > 0 && (
            <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {message.images.map((img, idx) => (
                <img 
                  key={idx} 
                  src={img} 
                  alt="Attachment" 
                  style={{ 
                    maxHeight: 200, 
                    borderRadius: 8, 
                    border: '1px solid rgba(255,255,255,0.2)',
                    objectFit: 'contain',
                    cursor: 'pointer'
                  }} 
                  onClick={() => window.open(img)}
                />
              ))}
            </Box>
          )}

          <Box sx={{ lineHeight: 1.72, fontSize: '0.91rem', color: 'rgba(255,255,255,0.88)' }}>
          <ReactMarkdown
            components={{
              a: ({ href, children, ...props }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none', borderBottom: '1px solid rgba(var(--accent-rgb),0.4)' }} {...props}>{children}</a>
              ),
              h1: ({ children }) => (
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', mt: 2, mb: 0.75, borderBottom: '1px solid rgba(var(--accent-rgb),0.2)', pb: 0.5, fontFamily: 'Cormorant Garamond, serif' }}>{children}</Typography>
              ),
              h2: ({ children }) => (
                <Typography sx={{ fontSize: '1.08rem', fontWeight: 700, color: 'rgba(255,255,255,0.95)', mt: 1.5, mb: 0.5 }}>{children}</Typography>
              ),
              h3: ({ children }) => (
                <Typography sx={{ fontSize: '0.97rem', fontWeight: 600, color: 'var(--accent)', mt: 1.25, mb: 0.4, letterSpacing: '0.02em' }}>{children}</Typography>
              ),
              blockquote: ({ children }) => (
                <Box sx={{ borderLeft: '3px solid rgba(var(--accent-rgb),0.5)', pl: 1.5, py: 0.25, my: 1, bgcolor: 'rgba(var(--accent-rgb),0.06)', borderRadius: '0 6px 6px 0' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', fontSize: '0.89rem', lineHeight: 1.65 }}>{children}</Typography>
                </Box>
              ),
              table: ({ children }) => (
                <Box sx={{ overflowX: 'auto', my: 1.5, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>{children}</table>
                </Box>
              ),
              thead: ({ children }) => <thead style={{ background: 'rgba(var(--accent-rgb),0.1)' }}>{children}</thead>,
              th: ({ children }) => <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--accent)', fontWeight: 700, borderBottom: '1px solid rgba(var(--accent-rgb),0.25)', whiteSpace: 'nowrap' }}>{children}</th>,
              td: ({ children }) => <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.82)' }}>{children}</td>,
              li: ({ children }) => (
                <li style={{ marginBottom: '0.3em', lineHeight: 1.65, color: 'rgba(255,255,255,0.85)' }}>{children}</li>
              ),
              p: ({ children }) => (
                <p style={{ margin: '0 0 0.75em 0', lineHeight: 1.72 }}>{children}</p>
              ),
              strong: ({ children }) => <strong style={{ color: '#fff', fontWeight: 700 }}>{children}</strong>,
              em: ({ children }) => <em style={{ color: 'rgba(255,255,255,0.75)' }}>{children}</em>,
              code: ({ inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || '');
                const codeString = String(children).replace(/\n$/, '');
                const copy = async () => {
                  try {
                    await navigator.clipboard.writeText(codeString);
                  } catch (e) {
                    console.error(e);
                  }
                };
                const isReact = match && ['js', 'jsx', 'javascript', 'tsx', 'ts', 'react'].includes(match[1]);

                const openInAppBuilder = () => {
                   setCanvasAppCode(codeString);
                   setCanvasActiveTab(1);
                   setCanvasOpen(true);
                   setToast('Code loaded into App Builder');
                   playSound('click');
                };

                return !inline && match ? (
                  <Box sx={{ position: 'relative', my: 1, borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {/* Language badge + action buttons */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.5, py: 0.5,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                        {match[1]}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {isReact && (
                          <Tooltip title="Preview in App Builder">
                            <IconButton size="small" onClick={openInAppBuilder} sx={{ color: 'var(--accent)', p: 0.4, '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.15)' } }}>
                              <BoltRounded sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={copy} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.4, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                            <ContentCopyRounded sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                    <SyntaxHighlighter
                      language={match[1]}
                      style={oneDark}
                      customStyle={{
                        margin: 0,
                        borderRadius: 0,
                        fontSize: '0.8rem',
                        lineHeight: 1.6,
                        background: 'rgba(10,12,18,0.95)',
                        padding: '14px 16px',
                      }}
                      wrapLongLines
                    >
                      {codeString}
                    </SyntaxHighlighter>
                  </Box>
                ) : (
                  <code
                    className={className}
                    style={{
                      background: 'rgba(0, 0, 0, 0.35)',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                      color: 'var(--accent)',
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
          {isStreaming && <span className="streaming-cursor" />}
          </Box>
        </Box>
        {/* Hover action bar */}
        {isHovered && !isUser && (
          <Box sx={{
            display: 'flex', gap: 0.5, mt: 0.5,
            opacity: 0, animation: 'fadeIn 0.15s ease forwards',
            alignItems: 'center',
          }}>
            <Tooltip title="Copy message">
              <IconButton
                size="small"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(content); setToast('Copied!'); } catch (e) {}
                }}
                sx={{ p: 0.5, color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)' } }}
              >
                <ContentCopyRounded sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Regenerate">
              <IconButton
                size="small"
                onClick={() => {
                  const msgIdx = messages.findIndex(m => m.id === message.id);
                  const prevUser = [...messages].slice(0, msgIdx).reverse().find(m => m.role === 'user');
                  if (prevUser) { setInput(prevUser.content); setTimeout(() => sendMessage(), 50); }
                }}
                sx={{ p: 0.5, color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)' } }}
              >
                <AutoFixHigh sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            {/* React button */}
            <Tooltip title="React">
              <IconButton
                size="small"
                onClick={() => setReactionPickerMsgId(p => p === message.id ? null : message.id)}
                sx={{ p: 0.5, color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)' } }}
              >
                <Box component="span" sx={{ fontSize: 12, lineHeight: 1 }}>🙂</Box>
              </IconButton>
            </Tooltip>
            {/* Pin button */}
            <Tooltip title={pinnedMsgIds[message.id] ? 'Unpin' : 'Pin to top'}>
              <IconButton size="small" onClick={() => togglePinMessage(message)}
                sx={{ p: 0.5, color: pinnedMsgIds[message.id] ? 'var(--accent)' : 'rgba(255,255,255,0.4)', '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)' } }}>
                {pinnedMsgIds[message.id] ? <PinIcon sx={{ fontSize: 13 }} /> : <PinOutlinedIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
            {/* Star button */}
            <Tooltip title={starredMsgs.find(m => m.id === message.id) ? 'Unstar' : 'Save to Starred'}>
              <IconButton size="small" onClick={() => toggleStarMessage(message)}
                sx={{ p: 0.5, color: starredMsgs.find(m => m.id === message.id) ? '#FFD700' : 'rgba(255,255,255,0.4)', '&:hover': { color: '#FFD700', bgcolor: 'rgba(255,215,0,0.08)' } }}>
                {starredMsgs.find(m => m.id === message.id) ? <StarRounded sx={{ fontSize: 13 }} /> : <StarBorderIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
            {/* Reaction picker popup */}
            {reactionPickerMsgId === message.id && (
              <Box sx={{
                position: 'absolute', bottom: '110%', left: 0, zIndex: 999,
                display: 'flex', gap: 0.4, px: 1, py: 0.75,
                background: 'rgba(14,18,28,0.97)',
                border: '1px solid rgba(var(--accent-rgb),0.2)',
                borderRadius: '14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                animation: 'cmdSlideIn 0.12s cubic-bezier(0.22,1,0.36,1)',
              }}>
                {REACTION_EMOJIS.map(emoji => (
                  <Box
                    key={emoji}
                    onClick={() => toggleReaction(message.id, emoji)}
                    sx={{
                      fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, p: 0.4,
                      borderRadius: '8px',
                      transition: 'transform 0.1s ease',
                      '&:hover': { transform: 'scale(1.3)', bgcolor: 'rgba(var(--accent-rgb),0.12)' },
                    }}
                  >{emoji}</Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {/* Hover actions for USER messages */}
        {isHovered && isUser && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, opacity: 0, animation: 'fadeIn 0.15s ease forwards', justifyContent: 'flex-end' }}>
            <Tooltip title="Edit and resend">
              <IconButton size="small"
                onClick={() => { setEditingMsgId(message.id); setEditingMsgContent(message.content); }}
                sx={{ p: 0.5, color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)' } }}>
                <EditIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={starredMsgs.find(m => m.id === message.id) ? 'Unstar' : 'Save to Starred'}>
              <IconButton size="small" onClick={() => toggleStarMessage(message)}
                sx={{ p: 0.5, color: starredMsgs.find(m => m.id === message.id) ? '#FFD700' : 'rgba(255,255,255,0.4)', '&:hover': { color: '#FFD700', bgcolor: 'rgba(255,215,0,0.08)' } }}>
                {starredMsgs.find(m => m.id === message.id) ? <StarRounded sx={{ fontSize: 13 }} /> : <StarBorderIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Reaction pills below bubble */}
        {(() => {
          const reacts = messageReactions[message.id] || {};
          const active = Object.entries(reacts).filter(([,c]) => c > 0);
          if (active.length === 0) return null;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {active.map(([emoji, count]) => (
                <Box
                  key={emoji}
                  onClick={() => toggleReaction(message.id, emoji)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    px: 0.9, py: 0.2,
                    borderRadius: '20px',
                    background: 'rgba(var(--accent-rgb),0.12)',
                    border: '1px solid rgba(var(--accent-rgb),0.25)',
                    cursor: 'pointer', fontSize: '0.78rem',
                    transition: 'all 0.15s ease',
                    '&:hover': { background: 'rgba(var(--accent-rgb),0.22)', transform: 'scale(1.05)' },
                  }}
                >
                  <span>{emoji}</span>
                  {count > 1 && <Typography sx={{ fontSize: '0.65rem', color: 'var(--accent)', fontWeight: 700 }}>{count}</Typography>}
                </Box>
              ))}
            </Box>
          );
        })()}
      </motion.div>
    );
  };

  const themeVars = {
    '--accent': activeTheme.accent,
    '--accent-2': activeTheme.sub,
    '--glow': activeTheme.glow,
    '--panel-bg': activeTheme.panelBg || 'rgba(0,0,0,0.75)',
    '--bg': activeTheme.bg || '#000',
    '--accent-rgb': hexToRgb(activeTheme.accent),
    '--glow-rgb': hexToRgb(activeTheme.glow),
  };

  const STATUS_ORDER = ['inbox', 'doing', 'done'];

  // Context for passing drag-handle ref/listeners down to board headers only
  const DragHandleContext = React.createContext(null);

  // Draggable Board Wrapper Component
  // Drag is activated ONLY via the board-header (DragHandleArea) — not the whole panel.
  // This means scroll and button clicks inside the panel work correctly.
  const DraggableBoard = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } = useDraggable({ id });
    const position = boardPositions[id] || { x: 0, y: 0 };

    const style = {
      position: 'fixed',
      top: '80px',
      left: '280px',
      zIndex: isDragging ? 1000 : 10,
      cursor: 'default',
      transform: `translate3d(${position.x + (transform?.x || 0)}px, ${position.y + (transform?.y || 0)}px, 0)`,
      transition: isDragging ? 'none' : 'transform 0.2s ease',
      width: 'calc(100vw - 320px)',
      maxWidth: '1000px',
      maxHeight: 'calc(100vh - 120px)',
      overflow: 'auto',
      // No touchAction here — allows trackpad/touch scroll in the panel content
    };

    return (
      <DragHandleContext.Provider value={{ setActivatorNodeRef, listeners, attributes, isDragging }}>
        <div ref={setNodeRef} style={style} data-draggable={id}>
          {children}
        </div>
      </DragHandleContext.Provider>
    );
  };

  // Apply to every <DragHandleArea className="board-header"> — makes ONLY the header draggable
  const DragHandleArea = ({ className, children }) => {
    const ctx = React.useContext(DragHandleContext);
    if (!ctx) return <Box className={className}>{children}</Box>;
    return (
      <Box
        ref={ctx.setActivatorNodeRef}
        {...ctx.listeners}
        {...ctx.attributes}
        className={className}
        sx={{ cursor: ctx.isDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' }}
      >
        {children}
      </Box>
    );
  };

  const renderActiveBoard = () => {
    switch (activeSection) {
      case 'research':
        return (
          <DraggableBoard id="research">
            <Paper className="intel-board glass-card">
              <DragHandleArea className="board-header">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Research Tools</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Multi-source research with auto-citations and cross-referencing
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={researchLoading ? 'Syncing…' : 'Synced'} size="small" className="chip-soft" />
                  <Tooltip title="Back to Chat">
                    <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DragHandleArea>
            {/* Quick-start prompts for research */}
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {(SECTION_QUICK_STARTS.research || []).map(q => (
                <Chip key={q} label={q} size="small"
                  onClick={() => { setInput(q); setActiveSection('chat'); setTimeout(() => inputRef.current?.focus(), 100); }}
                  sx={{ cursor: 'pointer', bgcolor: 'rgba(var(--accent-rgb),0.07)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(var(--accent-rgb),0.15)', fontSize: '0.68rem', height: 24, '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.18)', color: 'var(--accent)' } }} />
              ))}
            </Box>
            <Grid container spacing={2}>
              {/* Add Research */}
              <Grid item xs={12} md={4}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>Add Research</Typography>
                  <TextField
                    label="Title"
                    value={researchForm.title}
                    onChange={(e) => setResearchForm((f) => ({ ...f, title: e.target.value }))}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <TextField
                    label="Summary / findings"
                    multiline
                    minRows={3}
                    value={researchForm.summary}
                    onChange={(e) => setResearchForm((f) => ({ ...f, summary: e.target.value }))}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['web', 'file', 'manual', 'database'].map(source => (
                      <Chip
                        key={source}
                        label={source}
                        size="small"
                        variant="outlined"
                        onClick={() => setResearchForm(f => ({ ...f, source: f.source === source ? 'manual' : source }))}
                        sx={{ borderColor: researchForm.source === source ? 'var(--accent)' : 'rgba(255,255,255,0.2)' }}
                      />
                    ))}
                  </Box>
                  <Button variant="contained" onClick={addResearchEntry} disabled={!researchForm.title.trim()}>
                    Save Research
                  </Button>
                </Stack>
              </Grid>

              {/* Search & Filter */}
              <Grid item xs={12} md={8}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>Search & Filter</Typography>
                  <TextField
                    label="Search research..."
                    value={researchSearch}
                    onChange={(e) => {
                      setResearchSearch(e.target.value);
                      searchResearch(e.target.value);
                    }}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ sx: { color: '#fff' } }}
                    placeholder="Search by title or content..."
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="All Sources"
                      onClick={() => { setResearchFilter('all'); filterResearchBySource('all'); }}
                      variant={researchFilter === 'all' ? 'filled' : 'outlined'}
                      color={researchFilter === 'all' ? 'primary' : 'default'}
                      size="small"
                    />
                    {['web', 'file', 'manual'].map(source => (
                      <Chip
                        key={source}
                        label={source}
                        onClick={() => { setResearchFilter(source); filterResearchBySource(source); }}
                        variant={researchFilter === source ? 'filled' : 'outlined'}
                        color={researchFilter === source ? 'primary' : 'default'}
                        size="small"
                      />
                    ))}
                  </Box>

                  {/* Display Results */}
                  <Box className="board-list" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {(researchSearch ? researchSearchResults : researchItems || []).map((item, idx) => {
                      const citations = generateCitations(item);
                      return (
                        <Box 
                          key={`${item.title}-${idx}`} 
                          className="board-row"
                          onClick={() => setSelectedResearchId(selectedResearchId === item.id ? null : item.id)}
                          sx={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {item.title || 'Untitled'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.5 }}>
                              {item.source && `📍 ${item.source}`}
                            </Typography>
                            {item.tags && item.tags.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {item.tags.map(tag => (
                                  <Chip key={tag} label={tag} size="small" className="chip-soft" />
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Tooltip title="Discuss with Vesper">
                            <IconButton size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const prompt = `Tell me about this research: "${item.title}"${item.summary ? `\n\n${item.summary}` : ''}`;
                                setInput(prompt);
                                setActiveSection('chat');
                                setTimeout(() => inputRef.current?.focus(), 100);
                              }}
                              sx={{ color: 'rgba(var(--accent-rgb),0.6)', flexShrink: 0, '&:hover': { color: 'var(--accent)' } }}>
                              <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>✦</Box>
                            </IconButton>
                          </Tooltip>
                          {selectedResearchId === item.id && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>Citation:</Typography>
                                {['APA', 'MLA', 'Chicago'].map(fmt => (
                                  <Chip
                                    key={fmt}
                                    label={fmt}
                                    size="small"
                                    onClick={() => setCitationFormat(fmt)}
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 20,
                                      bgcolor: citationFormat === fmt ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                                      color: citationFormat === fmt ? '#000' : 'rgba(255,255,255,0.7)',
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: citationFormat === fmt ? 'var(--accent)' : 'rgba(255,255,255,0.2)' },
                                    }}
                                  />
                                ))}
                              </Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', display: 'block', wordBreak: 'break-word' }}>
                                {citations[citationFormat.toLowerCase()] || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    {!researchItems?.length && !researchSearch && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        No research logged yet.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          </DraggableBoard>
        );
      case 'documents':
        return (
          <DraggableBoard id="documents">
            <Paper className="intel-board glass-card">
              <DragHandleArea className="board-header">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>📄 Document Library</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Upload PDFs and docs - Vesper learns from them
                  </Typography>
                </Box>
                <Tooltip title="Back to Chat">
                  <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </DragHandleArea>
              <Stack spacing={2}>
                {/* Upload Section */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--accent)' }}>Upload Document</Typography>
                  <Stack spacing={1.5}>
                    <input 
                      type="file" 
                      accept=".pdf,.txt,.md"
                      onChange={uploadDocument}
                      disabled={uploading}
                      style={{ display: 'block' }}
                    />
                    <TextField
                      label="Tags for this document"
                      value={documentTags}
                      onChange={(e) => setDocumentTags(e.target.value)}
                      placeholder="documentation, reference"
                      fullWidth
                      size="small"
                      variant="filled"
                      disabled={uploading}
                      InputProps={{ sx: { color: '#fff' } }}
                    />
                    {uploading && <LinearProgress />}
                  </Stack>
                </Box>

                {/* Documents List */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                    {documents.length > 0 ? `${documents.length} Document${documents.length !== 1 ? 's' : ''}` : 'No documents yet'}
                  </Typography>
                  <Box className="board-list">
                    {(documents || []).map((doc) => (
                      <Box key={doc.id} className="board-row">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            📄 {doc.filename}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.5 }}>
                            {(doc.file_size / 1024).toFixed(1)} KB • {doc.file_type.toUpperCase()}
                          </Typography>
                          {doc.tags && doc.tags.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                              {doc.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                              ))}
                            </Box>
                          )}
                          {doc.summary && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, display: 'block' }}>
                              {doc.summary}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
                            {formatTime(doc.created_at)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => deleteDocument(doc.id)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {!documents?.length && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        No documents uploaded yet. Start by uploading a PDF or text file!
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </DraggableBoard>
        );
      case 'memory':
        return (
          <DraggableBoard id="memory">
            <Paper className="intel-board glass-card">
              <DragHandleArea className="board-header">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Memory Core</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {memoryView === 'history' ? 'Chat history with pinning' : 'Fast, file-backed memory store'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={memoryView === 'history' ? (threadsLoading ? 'Loading…' : 'Loaded') : (memoryLoading ? 'Syncing…' : 'Synced')} size="small" className="chip-soft" />
                <Tooltip title="Back to Chat">
                  <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </DragHandleArea>
            <Tabs
              value={memoryView}
              onChange={(_, v) => { setMemoryView(v); if (v === 'history') fetchThreads(); }}
              sx={{
                mb: 2,
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                minHeight: 40,
                '& .MuiTabs-indicator': { backgroundColor: 'var(--accent)', height: 2 },
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  minHeight: 40,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  px: 2,
                  py: 0,
                },
                '& .Mui-selected': { color: 'var(--accent) !important' },
              }}
            >
              <Tab label="Threads" value="history" />
              <Tab label="Notes" value="notes" />
              <Tab label="Search All" value="search" icon={<SearchRoundedIcon sx={{ fontSize: '0.9rem !important' }} />} iconPosition="start" sx={{ minHeight: 40 }} />
            </Tabs>
            {memoryView === 'notes' && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {customizations.memoryCategories.map((cat) => (
                  <Chip
                    key={cat}
                    label={cat.replace(/_/g, ' ')}
                    onClick={() => setMemoryCategory(cat)}
                    color={memoryCategory === cat ? 'primary' : 'default'}
                    variant={memoryCategory === cat ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
            )}
            {memoryView === 'history' && (
              <TextField
                placeholder="Search conversations..."
                value={threadSearchQuery}
                onChange={(e) => setThreadSearchQuery(e.target.value)}
                size="small"
                fullWidth
                variant="filled"
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { color: '#fff' },
                  endAdornment: threadSearchQuery && (
                    <IconButton size="small" onClick={() => setThreadSearchQuery('')} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            )}
            {/* ── Search All view ── */}
            {memoryView === 'search' && (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    placeholder="Search across memory, notes, and research…"
                    value={memorySearchQuery}
                    onChange={(e) => setMemorySearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && memorySearchQuery.trim().length >= 2) {
                        setMemorySearchLoading(true);
                        setMemorySearchResults([]);
                        fetch(`${apiBase}/api/search/all?q=${encodeURIComponent(memorySearchQuery.trim())}`)
                          .then(r => r.json())
                          .then(d => setMemorySearchResults(d.results || []))
                          .catch(() => {})
                          .finally(() => setMemorySearchLoading(false));
                      }
                    }}
                    size="small"
                    fullWidth
                    variant="filled"
                    InputProps={{
                      sx: { color: '#fff' },
                      endAdornment: memorySearchQuery && (
                        <IconButton size="small" onClick={() => { setMemorySearchQuery(''); setMemorySearchResults([]); }} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={memorySearchLoading || memorySearchQuery.trim().length < 2}
                    onClick={() => {
                      setMemorySearchLoading(true);
                      setMemorySearchResults([]);
                      fetch(`${apiBase}/api/search/all?q=${encodeURIComponent(memorySearchQuery.trim())}`)
                        .then(r => r.json())
                        .then(d => setMemorySearchResults(d.results || []))
                        .catch(() => {})
                        .finally(() => setMemorySearchLoading(false));
                    }}
                    sx={{ color: 'var(--accent)', borderColor: 'rgba(0,255,255,0.3)', whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {memorySearchLoading ? <CircularProgress size={14} sx={{ color: 'var(--accent)' }} /> : 'Search'}
                  </Button>
                </Box>
                {memorySearchResults.length > 0 && (
                  <Stack spacing={1}>
                    {memorySearchResults.map((r, i) => (
                      <Paper key={i} elevation={0} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                          <Chip label={r.source} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: r.source === 'memory' ? 'rgba(0,255,255,0.15)' : r.source === 'research' ? 'rgba(107,203,119,0.15)' : 'rgba(255,255,255,0.08)', color: r.source === 'memory' ? 'var(--accent)' : r.source === 'research' ? '#6bcb77' : 'rgba(255,255,255,0.5)', fontWeight: 700 }} />
                          <Chip label={(r.category || '').replace(/_/g, ' ')} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }} />
                          {r.timestamp && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem', ml: 'auto' }}>{new Date(r.timestamp).toLocaleDateString()}</Typography>}
                        </Box>
                        {r.title && <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)', display: 'block', mb: 0.3, fontSize: '0.72rem' }}>{r.title}</Typography>}
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {r.text}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
                {!memorySearchLoading && memorySearchResults.length === 0 && memorySearchQuery.trim().length >= 2 && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                    No results found for "{memorySearchQuery}". Try a different term.
                  </Typography>
                )}
                {!memorySearchQuery && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                    Type a term and press Enter or click Search to search all memory categories + research.
                  </Typography>
                )}
              </Box>
            )}
            {memoryView !== 'search' && (memoryView === 'history' ? (
              <Box className="thread-list">
                {selectedThreadIds.length > 0 && (
                  <Box sx={{ p: 1, mb: 1, bgcolor: 'rgba(255, 68, 68, 0.1)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: 'white' }}>{selectedThreadIds.length} selected</Typography>
                    <Button 
                      size="small" 
                      color="error" 
                      startIcon={<DeleteIcon />} 
                      onClick={() => {
                        setDeleteTargetId('bulk');
                        setShowDeleteConfirm(true);
                      }}
                      sx={{ textTransform: 'none', py: 0 }}
                    >
                      Delete Selected
                    </Button>
                  </Box>
                )}
                {/* Select All Checkbox */}
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1, mb: 1, borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 1 }}>
                  <Checkbox 
                    size="small" 
                    checked={filteredThreads.length > 0 && selectedThreadIds.length === filteredThreads.length}
                    indeterminate={selectedThreadIds.length > 0 && selectedThreadIds.length < filteredThreads.length}
                    onChange={() => {
                      if (selectedThreadIds.length === filteredThreads.length) {
                        setSelectedThreadIds([]);
                      } else {
                        setSelectedThreadIds(filteredThreads.map(t => t.id));
                      }
                    }}
                    sx={{ p: 0.5, mr: 1, color: 'rgba(255,255,255,0.5)' }} 
                  />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Select All ({filteredThreads.length})</Typography>
                </Box>
                {threadsLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[1, 2, 3, 4].map((i) => (
                      <Box key={i} className="board-row" sx={{ opacity: 0.6, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box className="skeleton" sx={{ height: 20, width: '70%', mb: 0.5 }} />
                          <Box className="skeleton" sx={{ height: 16, width: '40%' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Box className="skeleton" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                          <Box className="skeleton" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                          <Box className="skeleton" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                          <Box className="skeleton" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : filteredThreads.length > 0 ? (
                  <>
                    {/* Pinned Section */}
                    {pinnedThreads.length > 0 && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, mb: 0.5 }}>
                          <PinIcon sx={{ fontSize: '0.85rem', color: 'var(--accent)' }} />
                          <Typography variant="caption" sx={{ color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Pinned ({pinnedThreads.length})
                          </Typography>
                        </Box>
                        {pinnedThreads.map((thread) => (
                    <Box 
                      key={thread.id} 
                      className="thread-item" 
                      sx={{ 
                        borderLeft: selectedThreadIds.includes(thread.id) ? '2px solid var(--accent)' : '2px solid transparent',
                      }}
                    >
                      <Checkbox 
                        size="small" 
                        checked={selectedThreadIds.includes(thread.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectThread(thread.id);
                        }}
                        sx={{ p: 0.5, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}
                      />
                      {editingThreadId === thread.id ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            value={editingThreadTitle}
                            onChange={(e) => setEditingThreadTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameThread(thread.id);
                              if (e.key === 'Escape') cancelRenameThread();
                            }}
                            autoFocus
                            size="small"
                            variant="standard"
                            sx={{ flex: 1, input: { color: '#fff', fontSize: '0.9rem' } }}
                          />
                          <IconButton size="small" onClick={() => renameThread(thread.id)} sx={{ color: 'var(--accent)' }}>
                            <ChecklistRounded fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelRenameThread} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ flex: 1, cursor: 'pointer', minWidth: 0, overflow: 'hidden' }} onClick={() => loadThread(thread.id)}>
                            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                              📌 {sanitizeTitle(thread.title)}
                            </Typography>
                            {thread.summary && (
                              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.73rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.2, lineHeight: 1.3 }}>
                                {thread.summary}
                              </Typography>
                            )}
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', mt: 0.3, lineHeight: 1.3 }}>
                              {thread.message_count || 0} {(thread.message_count === 1) ? 'message' : 'messages'} · {formatTime(thread.updated_at)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setThreadMenuAnchor(e.currentTarget); setThreadMenuThread(thread); }}
                            sx={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0, '&:hover': { color: 'var(--accent)' } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ))}
                        <Box sx={{ borderBottom: '1px solid rgba(0,255,255,0.15)', my: 1 }} />
                      </>
                    )}
                    {/* Recent Conversations Section */}
                    {unpinnedThreads.length > 0 && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Recent ({unpinnedThreads.length})
                          </Typography>
                        </Box>
                        {unpinnedThreads.map((thread) => (
                    <Box 
                      key={thread.id} 
                      className="thread-item" 
                      sx={{ 
                        borderLeft: selectedThreadIds.includes(thread.id) ? '2px solid var(--accent)' : '2px solid transparent',
                      }}
                    >
                      <Checkbox 
                        size="small" 
                        checked={selectedThreadIds.includes(thread.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectThread(thread.id);
                        }}
                        sx={{ p: 0.5, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}
                      />
                      {editingThreadId === thread.id ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            value={editingThreadTitle}
                            onChange={(e) => setEditingThreadTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameThread(thread.id);
                              if (e.key === 'Escape') cancelRenameThread();
                            }}
                            autoFocus
                            size="small"
                            variant="standard"
                            sx={{ flex: 1, input: { color: '#fff', fontSize: '0.9rem' } }}
                          />
                          <IconButton size="small" onClick={() => renameThread(thread.id)} sx={{ color: 'var(--accent)' }}>
                            <ChecklistRounded fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelRenameThread} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ flex: 1, cursor: 'pointer', minWidth: 0, overflow: 'hidden' }} onClick={() => loadThread(thread.id)}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                              {sanitizeTitle(thread.title)}
                            </Typography>
                            {thread.summary && (
                              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.73rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.2, lineHeight: 1.3 }}>
                                {thread.summary}
                              </Typography>
                            )}
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', mt: 0.3, lineHeight: 1.3 }}>
                              {thread.message_count || 0} {(thread.message_count === 1) ? 'message' : 'messages'} · {formatTime(thread.updated_at)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setThreadMenuAnchor(e.currentTarget); setThreadMenuThread(thread); }}
                            sx={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0, '&:hover': { color: 'var(--accent)' } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ))}
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {threadSearchQuery ? `No conversations matching "${threadSearchQuery}"` : 'No chat history yet.'}
                  </Typography>
                )}
              </Box>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--accent)' }}>Add Memory</Typography>
                  <Stack spacing={1.5}>
                    <TextField
                      label="New memory"
                      multiline
                      minRows={3}
                      value={memoryText}
                      onChange={(e) => setMemoryText(e.target.value)}
                      fullWidth
                      variant="filled"
                      size="small"
                      InputProps={{ sx: { color: '#fff' } }}
                    />
                    <TextField
                      label="Tags (comma-separated)"
                      value={newMemoryTags}
                      onChange={(e) => setNewMemoryTags(e.target.value)}
                      placeholder="code, idea, project"
                      fullWidth
                      variant="filled"
                      size="small"
                      InputProps={{ sx: { color: '#fff' } }}
                    />
                    <Button variant="contained" onClick={addMemoryEntry} disabled={!memoryText.trim()}>
                      Save Memory
                    </Button>
                  </Stack>
                </Box>

                {/* Tag Filter */}
                {memoryTags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'rgba(255,255,255,0.7)' }}>Filter by Tag</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {memoryTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onClick={() => toggleTagFilter(tag)}
                          variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                          color={selectedTags.includes(tag) ? 'primary' : 'default'}
                          size="small"
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                      {selectedTags.length > 0 && (
                        <Chip
                          label="Clear filters"
                          onClick={() => { setSelectedTags([]); fetchMemory(memoryCategory); }}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Memory Items */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                    Memories {selectedTags.length > 0 && `(filtered by: ${selectedTags.join(', ')})`}
                  </Typography>
                  <Box className="board-list">
                    {(memoryItems || []).slice().reverse().map((item, idx) => (
                      <Box key={`${item.id || item.timestamp}-${idx}`} className="board-row" sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} title={formatFullDateTime(item.timestamp || item.created_at || Date.now())}>
                        <Box sx={{ width: '100%', pr: 1 }}>
                          {(item.title || (item.content?.trim()?.slice(0, 60) + ((item.content?.trim()?.length || 0) > 60 ? '...' : ''))) && (
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.95)', mb: 0.5 }}>
                              {item.title || (item.content?.trim()?.slice(0, 60) + ((item.content?.trim()?.length || 0) > 60 ? '...' : ''))}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {item.content}
                          </Typography>
                          {item.tags && item.tags.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
                              {item.tags.map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 24, fontSize: '0.75rem', cursor: 'pointer' }}
                                  onClick={() => toggleTagFilter(tag)}
                                />
                              ))}
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, display: 'block' }}>
                            {formatTime(item.timestamp || item.created_at || Date.now())}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setMemoryMenuAnchor(e.currentTarget); setMemoryMenuItem(item); }}
                          sx={{ ml: 1, flexShrink: 0 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Menu
                      anchorEl={memoryMenuAnchor}
                      open={Boolean(memoryMenuAnchor)}
                      onClose={() => { setMemoryMenuAnchor(null); setMemoryMenuItem(null); }}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => memoryMenuItem?.id && deleteMemory(memoryMenuItem.id)}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete
                      </MenuItem>
                    </Menu>
                    {!memoryItems?.length && !memoryLoading && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {selectedTags.length > 0 ? `No memories with tags: ${selectedTags.join(', ')}` : 'Nothing stored for this category yet.'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            ))}
          </Paper>
          </DraggableBoard>
        );
      case 'tasks':
        return (
          <DraggableBoard id="tasks">
            <Paper className="intel-board glass-card">
              <DragHandleArea className="board-header">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Task Matrix</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Track tasks by status, priority, and deadline.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip label={tasksLoading ? 'Syncing…' : 'Synced'} size="small" className="chip-soft" />
                <Tooltip title="Back to Chat">
                  <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </DragHandleArea>
            {/* Quick-start prompts for tasks */}
            <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {(SECTION_QUICK_STARTS.tasks || []).map(q => (
                <Chip key={q} label={q} size="small"
                  onClick={() => { setInput(q); setActiveSection('chat'); setTimeout(() => inputRef.current?.focus(), 100); }}
                  sx={{ cursor: 'pointer', bgcolor: 'rgba(var(--accent-rgb),0.07)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(var(--accent-rgb),0.15)', fontSize: '0.68rem', height: 24, '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.18)', color: 'var(--accent)' } }} />
              ))}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <TextField
                    label="Task title"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <TextField
                    label="Description"
                    value={taskForm.description || ''}
                    onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
                    multiline
                    minRows={2}
                    fullWidth
                    variant="filled"
                    size="small"
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <TextField
                      label="Priority"
                      select
                      value={taskForm.priority || 'medium'}
                      onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value }))}
                      variant="filled"
                      size="small"
                      InputProps={{ sx: { color: '#fff' } }}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </TextField>
                    <TextField
                      label="Due Date"
                      type="date"
                      value={taskForm.dueDate || ''}
                      onChange={(e) => setTaskForm((f) => ({ ...f, dueDate: e.target.value }))}
                      variant="filled"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ sx: { color: '#fff' } }}
                    />
                  </Box>
                  <Button variant="contained" onClick={addTask} disabled={!taskForm.title.trim()}>
                    Add Task
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {STATUS_ORDER.map((status) => (
                    <Box key={status} className="task-column glass-card">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                        {status}
                      </Typography>
                      <Stack spacing={1}>
                        {(tasks || []).map((task, idx) => {
                          if ((task.status || 'inbox') !== status) return null;
                          const isPriority = (task.priority || 'medium') === 'urgent' || (task.priority || 'medium') === 'high';
                          const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                          return (
                            <Box 
                              key={`${task.id || task.title}-${idx}`} 
                              className="task-row"
                              sx={{
                                borderLeft: isPriority ? '3px solid var(--accent)' : '3px solid transparent',
                                opacity: isOverdue ? 0.7 : 1,
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {task.title || 'Untitled task'}
                                </Typography>
                                {task.description && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.5 }}>
                                    {task.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {task.priority && task.priority !== 'medium' && (
                                    <Chip 
                                      label={task.priority} 
                                      size="small" 
                                      sx={{
                                        height: 20,
                                        bgcolor: task.priority === 'urgent' ? '#ff4444' : task.priority === 'high' ? '#ff8844' : 'rgba(255,255,255,0.1)',
                                        color: '#fff',
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  )}
                                  {task.due_date && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{
                                        color: isOverdue ? '#ff4444' : 'rgba(255,255,255,0.5)',
                                        fontWeight: isOverdue ? 700 : 400
                                      }}
                                    >
                                      📅 {new Date(task.due_date).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>
                                {task.subtasks && task.subtasks.length > 0 && (
                                  <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                                    {task.subtasks.map((st, i) => (
                                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.6)' }}>
                                        • {st.title}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Tooltip title="AI Breakdown">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => breakdownTask(idx, task)}
                                    sx={{ color: 'var(--accent)' }}
                                  >
                                    <AutoFixHigh fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Advance status">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const nextIndex = Math.min(STATUS_ORDER.length - 1, STATUS_ORDER.indexOf(status) + 1);
                                      updateTaskStatus(idx, STATUS_ORDER[nextIndex]);
                                    }}
                                  >
                                    <BoltRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete task">
                                  <IconButton size="small" onClick={() => deleteTask(idx)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Discuss with Vesper">
                                  <IconButton size="small"
                                    onClick={() => {
                                      const prompt = `Help me with this task: "${task.title}"${task.description ? `\n\n${task.description}` : ''}`;
                                      setInput(prompt);
                                      setActiveSection('chat');
                                      setTimeout(() => inputRef.current?.focus(), 100);
                                    }}
                                    sx={{ color: 'rgba(var(--accent-rgb),0.6)', '&:hover': { color: 'var(--accent)' } }}>
                                    <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>✦</Box>
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          );
                        })}
                        {!tasks?.some((t) => (t.status || 'inbox') === status) && (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            Empty
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          </DraggableBoard>
        );
      case 'nyxshift':
        return (
          <CreativeSuite apiBase={apiBase} onBack={() => setActiveSection('chat')} openCreationsPanel={pendingCreationsPanel} onCreationsPanelOpened={() => setPendingCreationsPanel(false)} />
        );
      case 'income':
        return (
          <IncomeDashboard apiBase={apiBase} onBack={() => setActiveSection('chat')} />
        );
      case 'autopilot':
        return <AutopilotPanel
          apiBase={apiBase}
          onBack={() => setActiveSection('chat')}
          jobs={autopilotJobs}
          setJobs={setAutopilotJobs}
          log={autopilotLog}
          setLog={setAutopilotLog}
          loading={autopilotLoading}
          setLoading={setAutopilotLoading}
          running={autopilotRunning}
          setRunning={setAutopilotRunning}
          niches={autopilotNiches}
          setNiches={setAutopilotNiches}
          accentColor={accentColor}
          setToast={setToast}
        />;
      case 'morning-brief':
        return (
          <MorningBrief apiBase={apiBase} onClose={() => setActiveSection('chat')} />
        );
      case 'gaps':
        return (
          <GapsJournal apiBase={apiBase} onBack={() => setActiveSection('chat')} />
        );
      case 'email':
        return (
          <EmailPanel
            apiBase={apiBase}
            onBack={() => setActiveSection('chat')}
            to={emailTo} setTo={setEmailTo}
            subject={emailSubject} setSubject={setEmailSubject}
            body={emailBody} setBody={setEmailBody}
            sending={emailSending} setSending={setEmailSending}
            config={emailConfig} setConfig={setEmailConfig}
            accentColor={activeThemeColors.accent}
            setToast={(msg) => { setToast(msg); setToastVariant('success'); }}
          />
        );
      case 'integrations':
        return (
          <IntegrationsHub
            apiBase={apiBase}
            onBack={() => { setActiveSection('chat'); setIntegrationInitialTab(0); }}
            initialTab={integrationInitialTab}
            onOpenDiagnostics={() => setDiagnosticsOpen(true)}
          />
        );
      case 'gallery':
        return (
          <DraggableBoard id="gallery">
            <MediaGallery apiBase={apiBase} onClose={() => setActiveSection('chat')} />
          </DraggableBoard>
        );
      case 'sassy':
        return (
          <DraggableBoard id="sassy">
            {avatarStudioOpen ? (
              <AvatarStudio 
                apiBase={apiBase} 
                onClose={() => setAvatarStudioOpen(false)} 
                accentColor={activeTheme.accent} 
                vesperIdentity={vesperIdentity}
                setToast={setToast}
              />
            ) : (
              <Box sx={{ position: 'relative' }}>
                <Sassy apiBase={apiBase} onClose={() => setActiveSection('chat')} />
                <Button
                  onClick={() => setAvatarStudioOpen(true)}
                  sx={{
                    position: 'absolute', top: 12, right: 48,
                    color: 'var(--accent)', fontSize: '0.7rem',
                    textTransform: 'none', fontWeight: 700,
                    border: '1px solid rgba(0,255,255,0.2)',
                    borderRadius: 2, px: 1.5, py: 0.5,
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(0,255,255,0.05)',
                    '&:hover': { bgcolor: 'rgba(0,255,255,0.12)' },
                  }}
                >
                  🧬 Avatar Studio
                </Button>
              </Box>
            )}
          </DraggableBoard>
        );
      case 'analytics':
        return (
          <DraggableBoard id="analytics">
            <Paper className="intel-board glass-card">
              <DragHandleArea className="board-header">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Analytics Dashboard</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Track AI interactions, token usage, and provider distribution
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Days"
                    type="number"
                    value={analyticsDays}
                    onChange={(e) => {
                      setAnalyticsDays(parseInt(e.target.value));
                      fetchAnalytics(parseInt(e.target.value));
                    }}
                    variant="filled"
                    size="small"
                    sx={{ width: 80 }}
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={() => fetchAnalytics(analyticsDays)} sx={{ color: 'var(--accent)' }}>
                      <AutoFixHigh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Back to Chat">
                    <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DragHandleArea>
              {analyticsLoading ? (
                <CircularProgress sx={{ color: 'var(--accent)', mt: 2 }} />
              ) : analytics ? (
                <Stack spacing={2.5}>
                  {/* Stat Cards */}
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Total Events', value: analytics.total_events || 0, color: 'var(--accent)' },
                      { label: 'Success Rate', value: `${(analytics.success_rate || 0).toFixed(1)}%`, color: '#00ff88' },
                      { label: 'Failed', value: analytics.failed_events || 0, color: '#ff4444' },
                      { label: 'Avg Response', value: `${(analytics.avg_response_time_ms || 0).toFixed(0)}ms`, color: '#ffaa00' },
                      { label: 'Total Tokens', value: (analytics.total_tokens || 0).toLocaleString(), color: '#aa88ff' },
                      { label: 'Providers Used', value: Object.keys(analytics.providers || {}).length, color: '#ff88ff' },
                    ].map(({ label, value, color }) => (
                      <Grid item xs={6} sm={4} md={2} key={label}>
                        <Box className="glass-card" sx={{ p: 1.5, textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color }}>{value}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>{label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                  <Grid container spacing={2}>
                    {/* AI Providers */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'var(--accent)' }}>🤖 AI Providers</Typography>
                      <Stack spacing={1}>
                        {Object.entries(analytics.providers || {}).length === 0 ? (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>No provider data yet</Typography>
                        ) : Object.entries(analytics.providers || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([provider, count]) => {
                            const pct = Math.round((count / (analytics.total_events || 1)) * 100);
                            return (
                              <Box key={provider}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{provider}</Typography>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{count} ({pct}%)</Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(255,255,255,0.07)',
                                    '& .MuiLinearProgress-bar': { bgcolor: 'var(--accent)', borderRadius: 3 }
                                  }}
                                />
                              </Box>
                            );
                          })}
                      </Stack>
                    </Grid>

                    {/* Event Types */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#aa88ff' }}>⚡ Event Types</Typography>
                      <Stack spacing={0.75}>
                        {Object.entries(analytics.event_types || {}).length === 0 ? (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>No event data yet</Typography>
                        ) : Object.entries(analytics.event_types || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([etype, count]) => (
                            <Box key={etype} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontSize: '0.8rem' }}>{etype.replace(/_/g, ' ')}</Typography>
                              <Chip label={count} size="small" sx={{ height: 18, bgcolor: 'rgba(170,136,255,0.15)', color: '#aa88ff', fontSize: '0.65rem' }} />
                            </Box>
                          ))}
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Top Topics */}
                  {Object.keys(analytics.topics || {}).length > 0 && (
                    <>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#ffaa00' }}>💬 Top Conversation Topics</Typography>
                        <Stack spacing={0.5}>
                          {Object.entries(analytics.topics || {})
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 8)
                            .map(([topic, count]) => (
                              <Box key={topic} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>{topic}</Typography>
                                <Chip label={count} size="small" sx={{ height: 18, bgcolor: 'rgba(255,170,0,0.15)', color: '#ffaa00', fontSize: '0.65rem', flexShrink: 0 }} />
                              </Box>
                            ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>📊</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>No analytics data yet</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', mt: 0.5 }}>Data appears after your first conversations</Typography>
                </Box>
              )}
            </Paper>
          </DraggableBoard>
        );
      case 'settings':
        return (
          <Box sx={{ position: 'fixed', top: '80px', left: '280px', width: 'calc(100vw - 320px)', maxWidth: '1000px', maxHeight: 'calc(100vh - 120px)', overflow: 'auto', zIndex: 10 }}>
            <Paper className="intel-board glass-card">
              <Box className="board-header" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Settings</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', overflow: 'visible' }}>
                    Customize your Vesper AI experience
                  </Typography>
                </Box>
              <Tooltip title="Back to Chat">
                <IconButton size="small" onClick={() => setActiveSection('chat')} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Stack spacing={2.5}>
              <EtsyConnectionCard apiBase={apiBase} />
              {runtimeCapabilities?.readiness && (
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: runtimeCapabilities.readiness.sellable
                    ? 'linear-gradient(135deg, rgba(14,40,28,0.88), rgba(10,24,18,0.92))'
                    : 'linear-gradient(135deg, rgba(42,26,12,0.9), rgba(26,16,10,0.92))',
                  border: runtimeCapabilities.readiness.sellable
                    ? '1px solid rgba(0,255,136,0.25)'
                    : '1px solid rgba(218,165,32,0.28)',
                  boxShadow: runtimeCapabilities.readiness.sellable
                    ? '0 12px 36px rgba(0,255,136,0.08)'
                    : '0 12px 36px rgba(218,165,32,0.08)',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1.25 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: runtimeCapabilities.readiness.sellable ? '#7ff2b8' : '#f6d38c' }}>
                        Launch Readiness
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                        {runtimeCapabilities.deployment_target.toUpperCase()} environment · score {runtimeCapabilities.readiness.score}/100
                      </Typography>
                    </Box>
                    <Chip
                      label={runtimeCapabilities.readiness.sellable ? 'SELLABLE' : 'BLOCKED'}
                      size="small"
                      sx={{
                        bgcolor: runtimeCapabilities.readiness.sellable ? 'rgba(0,255,136,0.14)' : 'rgba(218,165,32,0.16)',
                        color: runtimeCapabilities.readiness.sellable ? '#7ff2b8' : '#f6d38c',
                        border: `1px solid ${runtimeCapabilities.readiness.sellable ? 'rgba(0,255,136,0.25)' : 'rgba(218,165,32,0.28)'}`,
                        fontWeight: 800,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: runtimeCapabilities.readiness.blockers?.length ? 1.25 : 0 }}>
                    {Object.entries(runtimeCapabilities.readiness.checks || {}).map(([key, ok]) => (
                      <Chip
                        key={key}
                        label={`${ok ? '✓' : '•'} ${key.replace(/_/g, ' ')}`}
                        size="small"
                        sx={{
                          bgcolor: ok ? 'rgba(0,255,136,0.1)' : 'rgba(255,255,255,0.06)',
                          color: ok ? '#7ff2b8' : 'rgba(255,255,255,0.7)',
                          border: `1px solid ${ok ? 'rgba(0,255,136,0.2)' : 'rgba(255,255,255,0.08)'}`,
                        }}
                      />
                    ))}
                  </Box>
                  {runtimeCapabilities.readiness.blockers?.length > 0 && (
                    <Stack spacing={0.75} sx={{ mb: 1.25 }}>
                      {runtimeCapabilities.readiness.blockers.slice(0, 4).map((blocker) => (
                        <Typography key={blocker} variant="caption" sx={{ color: '#ffd9a6', display: 'block' }}>
                          • {blocker}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDiagnosticsOpen(true)}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      '&:hover': { borderColor: 'var(--accent)', bgcolor: 'rgba(255,255,255,0.04)' },
                    }}
                  >
                    Open Readiness Diagnostics
                  </Button>
                </Box>
              )}

              <SetupWizard
                runtimeCapabilities={runtimeCapabilities}
                onOpenDiagnostics={() => setDiagnosticsOpen(true)}
                onOpenOperations={() => {
                  setIntegrationInitialTab(3);
                  setActiveSection('integrations');
                }}
              />

              {/* ── Quick Navigation ── */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', letterSpacing: 1, textTransform: 'uppercase' }}>Quick Links</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { id: 'personality', label: '🎭 Personality', color: '#00d0ff', tab: null },
                    { id: 'integrations', label: '🏢 Brand Kit', color: '#aa88ff', tab: 1 },
                    { id: 'integrations', label: '🛡️ Operations', color: '#f1d39b', tab: 3 },
                    { id: 'sassy', label: '👗 Wardrobe', color: '#ff88ff', tab: null },
                    { id: 'analytics', label: '📊 Analytics', color: '#ffbb44', tab: null },
                    { id: 'nyxshift', label: '🎨 Creative Suite', color: '#ff6680', tab: null },
                  ].map(({ id, label, color, tab }) => (
                    <Chip
                      key={id}
                      label={label}
                      size="small"
                      onClick={() => {
                        if (tab !== null) setIntegrationInitialTab(tab);
                        setActiveSection(id);
                        playSound('click');
                      }}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.05)',
                        border: `1px solid ${color}44`,
                        color,
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `${color}18`, borderColor: color },
                        transition: 'all 0.2s',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Box
                  onClick={() => setThemeMenuAnchor(true)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    mb: 1.5, cursor: 'pointer', borderRadius: 1.5, px: 0.5, py: 0.5,
                    '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.08)' },
                    transition: 'background 0.2s',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>🎨 Appearance</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--accent)', opacity: 0.7, fontSize: '0.65rem', letterSpacing: 1 }}>OPEN THEMES →</Typography>
                </Box>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Active Theme</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{activeTheme.label} ({THEME_CATEGORIES.find(c => c.id === activeTheme.category)?.label || '⚡ Tech'})</Typography>
                    </Box>
                    <IconButton 
                      onClick={() => setThemeMenuAnchor(true)}
                      sx={{ 
                        bgcolor: activeTheme.accent, 
                        color: '#000',
                        boxShadow: `0 0 15px ${activeTheme.accent}`,
                        '&:hover': { bgcolor: activeTheme.accent, transform: 'scale(1.1)' }
                      }}
                    >
                      <PaletteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Theme Catalog - Categorized Grid */}
                  <Drawer
                    anchor="left"
                    open={Boolean(themeMenuAnchor)}
                    onClose={() => setThemeMenuAnchor(null)}
                    transitionDuration={{ enter: 320, exit: 220 }}
                    ModalProps={{
                      keepMounted: true,
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'rgba(2, 4, 10, 0.56)',
                          backdropFilter: 'blur(4px)',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiDrawer-paper': {
                        width: { xs: '100vw', sm: 620, md: 760 },
                        maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerLeft}px)` },
                        left: { xs: 0, md: `${drawerLeft}px` },
                        top: { xs: 0, md: `${drawerFrame.top}px` },
                        height: { xs: '100%', md: `${drawerFrame.height}px` },
                        borderRadius: { xs: 0, md: '0 22px 22px 0' },
                        bgcolor: 'rgba(8, 8, 18, 0.97)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
                        backdropFilter: 'blur(30px)',
                        overflow: 'hidden',
                      },
                    }}
                  >
                    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--accent)' }}>
                            🎨 Theme Catalog
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                            {THEMES.length} themes across {THEME_CATEGORIES.length} categories
                          </Typography>
                        </Box>
                        <IconButton onClick={() => setThemeMenuAnchor(null)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <CloseIcon />
                        </IconButton>
                      </Box>

                      <Box sx={{ overflowY: 'auto', flex: 1, pr: 1 }}>
                        {THEME_CATEGORIES.map((cat) => {
                          const catThemes = THEMES.filter(t => t.category === cat.id);
                          if (catThemes.length === 0) return null;
                          return (
                            <Box key={cat.id} sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)', mb: 0.5, fontSize: '0.85rem' }}>
                                {cat.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', mb: 1.5, display: 'block' }}>
                                {cat.desc}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {catThemes.map((t) => (
                                  <Box
                                    key={t.id}
                                    onClick={() => {
                                      setActiveTheme(t);
                                      // Don't close — let user browse
                                    }}
                                    sx={{
                                      width: 100,
                                      cursor: 'pointer',
                                      borderRadius: 2,
                                      p: 1,
                                      textAlign: 'center',
                                      border: activeTheme.id === t.id 
                                        ? `2px solid ${t.accent}` 
                                        : '2px solid rgba(255,255,255,0.06)',
                                      bgcolor: activeTheme.id === t.id 
                                        ? `${t.accent}15` 
                                        : 'rgba(255,255,255,0.02)',
                                      transition: 'all 0.2s ease',
                                      '&:hover': { 
                                        bgcolor: `${t.accent}20`,
                                        borderColor: `${t.accent}66`,
                                        transform: 'translateY(-2px)',
                                      },
                                    }}
                                  >
                                    {/* Color preview */}
                                    <Box sx={{
                                      width: '100%',
                                      height: 32,
                                      borderRadius: 1.5,
                                      mb: 0.75,
                                      background: t.bg || '#000',
                                      border: `2px solid ${t.accent}`,
                                      boxShadow: activeTheme.id === t.id ? `0 0 12px ${t.accent}` : 'none',
                                      position: 'relative',
                                      overflow: 'hidden',
                                    }}>
                                      {/* Accent dot */}
                                      <Box sx={{
                                        position: 'absolute', 
                                        top: '50%', left: '50%',
                                        transform: 'translate(-50%,-50%)',
                                        width: 12, height: 12,
                                        borderRadius: '50%',
                                        bgcolor: t.accent,
                                        boxShadow: `0 0 8px ${t.accent}`,
                                      }} />
                                      {/* Sub color dot */}
                                      <Box sx={{
                                        position: 'absolute',
                                        bottom: 3, right: 5,
                                        width: 6, height: 6,
                                        borderRadius: '50%',
                                        bgcolor: t.sub,
                                      }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ 
                                      color: activeTheme.id === t.id ? t.accent : 'rgba(255,255,255,0.7)',
                                      fontWeight: activeTheme.id === t.id ? 700 : 500,
                                      fontSize: '0.65rem',
                                      lineHeight: 1.2,
                                      display: 'block',
                                    }}>
                                      {t.label}
                                    </Typography>
                                    {t.sound && (
                                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.55rem' }}>
                                        🔊 {t.sound}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                          Active: {activeTheme.label} • Sound: {activeTheme.sound || 'default'} • {activeTheme.scanlines !== false ? 'Scanlines ON' : 'Scanlines OFF'}
                        </Typography>
                        <Button 
                          onClick={() => setThemeMenuAnchor(null)} 
                          sx={{ color: 'var(--accent)', fontWeight: 700, textTransform: 'none' }}
                        >
                          Done
                        </Button>
                      </Box>
                    </Box>
                  </Drawer>

                  {/* Wallpaper */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Wallpaper</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {customBackground ? `Active: ${customBackground.name}` : 'Custom background image'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {customBackground && (
                        <Button
                          size="small"
                          onClick={() => { setCustomBackground(null); try { localStorage.removeItem('vesper_custom_bg'); } catch(e) {} setToast('Background cleared'); }}
                          sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '0.7rem', p: '2px 6px', minWidth: 0 }}
                        >
                          Clear
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => setBackgroundStudioOpen(true)}
                        sx={{ bgcolor: 'rgba(255,136,255,0.15)', color: '#ff88ff', '&:hover': { bgcolor: 'rgba(255,136,255,0.25)' } }}
                      >
                        <PhotoLibrary fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              {/* Stats & Insights */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'var(--accent)' }}>📊 Your Vesper Stats</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1.5 }}>Tracked across sessions</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {[
                    { label: 'Messages Sent',   value: vesperStats.messages,       icon: '💬' },
                    { label: 'Tasks Completed', value: vesperStats.tasksCompleted, icon: '✅' },
                    { label: 'Memories Saved',  value: vesperStats.memories,       icon: '🧠' },
                    { label: 'Days Active',     value: vesperStats.daysActive,     icon: '📅' },
                    { label: 'Day Streak',      value: `${vesperStats.streak || 0}d`, icon: '🔥' },
                  ].map(({ label, value, icon }) => (
                    <Box key={label} sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ fontSize: '1.2rem', width: 28, textAlign: 'center', flexShrink: 0 }}>{icon}</Box>
                      <Box>
                        <Typography sx={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>{value ?? 0}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.62rem' }}>{label}</Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box
                    onClick={exportTasks}
                    sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'rgba(var(--accent-rgb),0.3)', bgcolor: 'rgba(var(--accent-rgb),0.06)' }, transition: 'all 0.2s' }}
                  >
                    <Box sx={{ fontSize: '1.2rem', width: 28, textAlign: 'center', flexShrink: 0 }}>📦</Box>
                    <Box>
                      <Typography sx={{ color: '#bbb', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1 }}>Export Tasks</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem' }}>Copy all tasks → clipboard</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Vesper Identity */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#ff66ff' }}>✨ Vesper Identity</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1.5 }}>
                  Customize Vesper's mood, vibe, look, and voice for today
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {[
                    { label: 'Mood', value: vesperIdentity?.mood?.label || vesperIdentity?.mood?.id, idValue: vesperIdentity?.mood?.id, emoji: vesperIdentity?.mood?.emoji, color: vesperIdentity?.mood?.color, key: 'mood_override', options: identityOptions?.moods },
                    { label: 'Vibe', value: vesperIdentity?.gender?.label || vesperIdentity?.gender?.id, idValue: vesperIdentity?.gender?.id, emoji: vesperIdentity?.gender?.emoji || (vesperIdentity?.gender?.id === 'feminine' ? '♀️' : vesperIdentity?.gender?.id === 'masculine' ? '♂️' : '⚧️'), color: '#ff66ff', key: 'gender_override', options: identityOptions?.genders },
                    { label: 'Look', value: vesperIdentity?.look, idValue: vesperIdentity?.look, emoji: '👁️', color: '#66ffcc', key: 'look_override', options: identityOptions?.looks },
                    { label: 'Voice', value: vesperIdentity?.voice_vibe?.label || vesperIdentity?.voice_vibe?.id, idValue: vesperIdentity?.voice_vibe?.id, emoji: vesperIdentity?.voice_vibe?.emoji || '🎤', color: '#ffaa00', key: 'voice_vibe_override', options: identityOptions?.voice_vibes },
                  ].map((item) => (
                    <Box key={item.label} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      borderRadius: 2,
                      p: 1.5,
                      border: `1px solid ${item.color || 'var(--accent)'}22`,
                    }}>
                      <Box sx={{ fontSize: '1.4rem', width: 36, textAlign: 'center', flexShrink: 0 }}>{item.emoji}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1, display: 'block' }}>
                          {item.label}
                        </Typography>
                        {item.options ? (
                          <Select
                            value={item.idValue || ''}
                            onChange={(e) => {
                              const selectedOpt = (Array.isArray(item.options) ? item.options : []).find(o => (typeof o === 'string' ? o : o.id) === e.target.value);
                              setVesperIdentity(prev => ({ ...prev, [item.label.toLowerCase()]: selectedOpt || e.target.value }));
                            }}
                            size="small"
                            fullWidth
                            sx={{
                              color: item.color || '#fff',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '.MuiSelect-select': { py: 0.25, px: 0 },
                              '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.3)' },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  bgcolor: 'rgba(10,10,25,0.95)',
                                  backdropFilter: 'blur(20px)',
                                  border: '1px solid rgba(0,255,255,0.15)',
                                  '& .MuiMenuItem-root': { color: '#fff', fontSize: '0.85rem' },
                                  '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(0,255,255,0.1)' },
                                }
                              }
                            }}
                          >
                            {(Array.isArray(item.options) ? item.options : []).map((opt) => {
                              const optId = typeof opt === 'string' ? opt : opt.id;
                              const optDisplay = typeof opt === 'string' ? opt : `${opt.emoji || ''} ${opt.label || opt.id}`;
                              return <MenuItem key={optId} value={optId}>{optDisplay}</MenuItem>;
                            })}
                          </Select>
                        ) : (
                          <Typography variant="body2" sx={{ color: item.color || '#fff', fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                            {item.value || '—'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    onClick={handleRerollIdentity}
                    size="small"
                    sx={{
                      color: '#ff66ff',
                      borderColor: 'rgba(255,102,255,0.3)',
                      border: '1px solid',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      flex: 1,
                      '&:hover': { bgcolor: 'rgba(255,102,255,0.1)', borderColor: '#ff66ff' },
                    }}
                  >
                    🎲 Reroll
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleConfirmIdentity({
                      mood_override: vesperIdentity?.mood?.id || vesperIdentity?.mood,
                      gender_override: vesperIdentity?.gender?.id || vesperIdentity?.gender,
                      voice_vibe_override: vesperIdentity?.voice_vibe?.id || vesperIdentity?.voice_vibe,
                    })}
                    sx={{
                      bgcolor: 'var(--accent)',
                      color: '#000',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: 'var(--accent)', filter: 'brightness(1.2)' },
                    }}
                  >
                    ✨ Confirm Vibe
                  </Button>
                </Stack>
              </Box>

              {/* Audio & Voice */}
              <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>🎙️ Audio &amp; Voice</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Text-to-Speech</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {ttsEnabled ? 'Vesper speaks responses aloud' : 'AI responses read aloud'}
                      </Typography>
                    </Box>
                    <Switch 
                      checked={ttsEnabled} 
                      onChange={(e) => {
                        const newVal = e.target.checked;
                        setTtsEnabled(newVal);
                        try { localStorage.setItem('vesper_tts_enabled', String(newVal)); } catch(err) {}
                        if (!newVal) stopSpeaking();
                        playSound('click');
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--accent)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--accent)',
                        },
                      }}
                    />
                  </Box>

                  {/* ── Ambient Sounds Toggle ── */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Ambient Sounds</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {ambientEnabled ? `🔊 Playing: ${activeTheme.sound || 'ambient'}` : 'Theme-matched background soundscape'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={ambientEnabled}
                      onChange={(e) => {
                        const newVal = e.target.checked;
                        setAmbientEnabled(newVal);
                        try { localStorage.setItem('vesper_ambient', String(newVal)); } catch(err) {}
                        playSound('click');
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent)' },
                      }}
                    />
                  </Box>

                  {/* ── Default Voice Picker ── */}
                  {ttsEnabled && (
                    <Box sx={{ p: 1.5, border: '1px solid rgba(0,255,136,0.2)', borderRadius: 2, bgcolor: 'rgba(0,255,136,0.03)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#00ff88' }}>
                        🎙️ Default Voice
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
                        Choose Vesper's voice for all speech
                      </Typography>

                      {selectedVoiceName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, p: 0.75, px: 1, bgcolor: 'rgba(0,255,136,0.08)', borderRadius: 1, border: '1px solid rgba(0,255,136,0.2)' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#00ff88' }}>💾</Typography>
                          <Typography variant="caption" sx={{ color: '#00ff88', fontWeight: 700, fontSize: '0.72rem' }}>
                            Saved default: {cloudVoices.find(v => v.id === selectedVoiceName)?.name || selectedVoiceName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', ml: 'auto' }}>persists across sessions</Typography>
                        </Box>
                      )}

                      <Box sx={{
                        maxHeight: 200, overflowY: 'auto', borderRadius: 1,
                        border: '1px solid rgba(0,255,136,0.15)', p: 0.5,
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-thumb': { background: '#00ff88', borderRadius: 2 },
                      }}>
                        {cloudVoices.length > 0 ? cloudVoices.map((v) => {
                          const isActive = selectedVoiceName === v.id || (!selectedVoiceName && v.id === defaultVoiceId);
                          return (
                            <Box
                              key={v.id}
                              onClick={() => {
                                setSelectedVoiceName(v.id);
                                try { localStorage.setItem('vesper_tts_voice', v.id); } catch(e) {}
                                setToast(`🎙️ Voice set: ${v.name}`);
                                if (v.preview_url) playVoicePreview(v.preview_url);
                              }}
                              sx={{
                                p: 0.75, px: 1, cursor: 'pointer', borderRadius: 1, mb: 0.25,
                                background: isActive ? 'rgba(0,255,136,0.15)' : 'rgba(0,0,0,0.2)',
                                borderLeft: isActive ? '3px solid #00ff88' : '3px solid transparent',
                                '&:hover': { background: 'rgba(255,255,255,0.08)' },
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {isActive && <Typography sx={{ fontSize: '0.65rem', color: '#00ff88' }}>✓</Typography>}
                                <Typography variant="caption" sx={{
                                  color: isActive ? '#00ff88' : 'rgba(255,255,255,0.7)', fontWeight: isActive ? 700 : 500, fontSize: '0.75rem',
                                }}>
                                  {v.name}
                                </Typography>
                                {v.gender && (
                                  <Typography variant="caption" sx={{
                                    fontSize: '0.55rem', px: 0.5, py: 0.1, borderRadius: 0.5,
                                    background: v.gender === 'Female' ? 'rgba(255,100,255,0.15)' : 'rgba(100,200,255,0.15)',
                                    color: v.gender === 'Female' ? '#ff88ff' : '#88ccff', fontWeight: 700,
                                  }}>
                                    {v.gender === 'Female' ? '♀' : '♂'}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem' }}>
                                {v.locale || ''}{v.style ? ` · ${v.style}` : ''}
                              </Typography>
                            </Box>
                          );
                        }) : (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', p: 1 }}>
                            No voices loaded — check that the backend is reachable
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* ── Voice Quality Sliders (ElevenLabs stability + similarity) ── */}
                  {ttsEnabled && (
                    <Box sx={{ p: 1.5, border: '1px solid rgba(168,85,247,0.2)', borderRadius: 2, bgcolor: 'rgba(168,85,247,0.03)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#c084fc' }}>
                        🎛️ Voice Quality
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1.5 }}>
                        Fine-tune ElevenLabs voice performance
                      </Typography>

                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600 }}>Stability</Typography>
                          <Typography variant="caption" sx={{ color: '#c084fc', fontSize: '0.72rem', fontFamily: 'monospace' }}>{(voiceStability * 100).toFixed(0)}%</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem', display: 'block', mb: 0.5 }}>
                          Low = more expressive · High = more consistent
                        </Typography>
                        <Slider
                          value={voiceStability}
                          min={0} max={1} step={0.01}
                          onChange={(_, v) => {
                            setVoiceStability(v);
                            try { localStorage.setItem('vesper_voice_stability', String(v)); } catch(e) {}
                          }}
                          sx={{
                            color: '#c084fc',
                            height: 3,
                            '& .MuiSlider-thumb': { width: 12, height: 12, '&:hover': { boxShadow: '0 0 0 6px rgba(192,132,252,0.16)' } },
                            '& .MuiSlider-rail': { opacity: 0.18 },
                          }}
                        />
                      </Box>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600 }}>Similarity Boost</Typography>
                          <Typography variant="caption" sx={{ color: '#c084fc', fontSize: '0.72rem', fontFamily: 'monospace' }}>{(voiceSimilarityBoost * 100).toFixed(0)}%</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem', display: 'block', mb: 0.5 }}>
                          Higher = closer to reference voice timbre
                        </Typography>
                        <Slider
                          value={voiceSimilarityBoost}
                          min={0} max={1} step={0.01}
                          onChange={(_, v) => {
                            setVoiceSimilarityBoost(v);
                            try { localStorage.setItem('vesper_voice_similarity', String(v)); } catch(e) {}
                          }}
                          sx={{
                            color: '#c084fc',
                            height: 3,
                            '& .MuiSlider-thumb': { width: 12, height: 12, '&:hover': { boxShadow: '0 0 0 6px rgba(192,132,252,0.16)' } },
                            '& .MuiSlider-rail': { opacity: 0.18 },
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* ── Auto-Speak Toggle ── */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Speak Replies</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Automatically read AI responses aloud</Typography>
                    </Box>
                    <Switch
                      checked={autoSpeak}
                      onChange={(e) => {
                        const newVal = e.target.checked;
                        setAutoSpeak(newVal);
                        try { localStorage.setItem('vesper_auto_speak', String(newVal)); } catch(err) {}
                        playSound('click');
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent)' },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>UI Sound Effects</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Button clicks and notifications</Typography>
                    </Box>
                    <Switch 
                      checked={soundEnabled} 
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setSoundEnabled(newValue);
                        localStorage.setItem('vesper_sound_enabled', newValue.toString());
                        if (newValue) playSound('success');
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--accent)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--accent)',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Voice Input</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Hold V to speak</Typography>
                    </Box>
                    <Chip label="Always On" size="small" sx={{ bgcolor: 'rgba(0,255,255,0.2)', color: 'var(--accent)' }} />
                  </Box>
                </Stack>
                </AccordionDetails>
              </Accordion>

              {/* 3D Avatar Settings */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>👤 3D Talking Avatar</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setMetaPersonOpen(true)}
                    sx={{ bgcolor: 'var(--accent)', color: '#000', fontWeight: 700, textTransform: 'none', fontSize: '0.72rem', py: 0.25,
                      '&:hover': { filter: 'brightness(1.2)' } }}
                  >
                    ✨ Open Avatar Creator
                  </Button>
                </Box>
                <Stack spacing={1.5}>

                  {/* Manual GLB URL override */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 0.5, fontSize: '0.68rem' }}>
                      Or paste a GLB URL directly (Ready Player Me / any .glb)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="https://…/avatar.glb"
                        value={rpmAvatarUrl}
                        onChange={(e) => {
                          setRpmAvatarUrl(e.target.value);
                          try { localStorage.setItem('vesper_rpm_avatar', e.target.value); } catch(_) {}
                        }}
                        style={{ flex: 1, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.14)',
                          borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: '0.78rem', outline: 'none' }}
                      />
                      {rpmAvatarUrl && (
                        <Button size="small" onClick={() => { setRpmAvatarUrl(''); try { localStorage.removeItem('vesper_rpm_avatar'); } catch(_) {} }}
                          sx={{ minWidth: 32, color: 'rgba(255,255,255,0.35)', p: 0.5 }}>✕</Button>
                      )}
                    </Box>
                  </Box>

                  {rpmAvatarUrl ? (
                    <Box sx={{ p: 1, bgcolor: 'rgba(0,255,200,0.07)', borderRadius: 1.5, border: '1px solid rgba(0,255,200,0.2)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(0,255,200,0.8)' }}>
                        ✓ Custom avatar active — lip-syncs to Lily’s voice
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1.5, border: '1px dashed rgba(255,255,255,0.1)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                        Using bundled model.fbx — click “Open Avatar Creator” to customise
                      </Typography>
                    </Box>
                  )}
                </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Voice Lab (ElevenLabs Premium) */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,187,68,0.2)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#ffbb44' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffbb44' }}>★ Voice Lab</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { setVoiceLabOpen(true); setVoiceLabTab('sfx'); }}
                    sx={{ borderColor: '#ffbb44', color: '#ffbb44', textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25 }}
                  >
                    Open Full Lab ↗
                  </Button>
                </Box>
                <Stack spacing={1.5}>
                  {/* Sound Effects Generator */}
                  <Box sx={{ p: 1.5, border: '1px solid rgba(255,180,50,0.3)', borderRadius: 2, bgcolor: 'rgba(255,180,50,0.05)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#ffcc55' }}>Sound Effects AI</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Generate any sound from a text description
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <input
                        type="text"
                        placeholder="e.g. cyberpunk door opening..."
                        id="sfx-prompt-input"
                        style={{
                          flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: '0.8rem', outline: 'none',
                        }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          const input = document.getElementById('sfx-prompt-input');
                          if (!input?.value.trim()) return;
                          setToast('Generating sound effect...');
                          try {
                            const res = await fetch(`${apiBase}/api/sfx/generate`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ prompt: input.value.trim(), duration: 5 }),
                            });
                            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const audio = new Audio(url);
                            audio.play();
                            audio.onended = () => URL.revokeObjectURL(url);
                            setToast('Playing sound effect!');
                          } catch (e) {
                            setToast('SFX Error: ' + e.message);
                          }
                        }}
                        sx={{ bgcolor: '#ffbb44', color: '#000', fontWeight: 700, minWidth: 60, '&:hover': { bgcolor: '#ffcc66' } }}
                      >
                        Generate
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {['Spaceship engine hum', 'Magical spell cast', 'Rain on cyberpunk city', 'Level up chime', 'Dramatic reveal'].map(p => (
                        <Chip key={p} label={p} size="small"
                          onClick={() => { const i = document.getElementById('sfx-prompt-input'); if (i) i.value = p; }}
                          sx={{ fontSize: '0.65rem', height: 22, bgcolor: 'rgba(255,180,50,0.1)', color: '#ffbb44', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,180,50,0.2)' } }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Voice Personas — inline assignment */}
                  <Box sx={{ p: 1.5, border: '1px solid rgba(0,255,255,0.2)', borderRadius: 2, bgcolor: 'rgba(0,255,255,0.03)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: 'var(--accent)' }}>Voice Personas</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Assign different voices for different contexts — Vesper adapts automatically
                    </Typography>
                    <PersonaAssigner apiBase={apiBase} cloudVoices={cloudVoices} setToast={setToast} playVoicePreview={playVoicePreview} onSave={fetchPersonaCache} />
                  </Box>

                  {/* Voice Cloning */}
                  <Box sx={{ p: 1.5, border: '1px solid rgba(200,100,255,0.3)', borderRadius: 2, bgcolor: 'rgba(200,100,255,0.05)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#cc88ff' }}>Voice Cloning</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Clone any voice from audio samples — make Vesper sound like anyone
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ borderColor: '#cc88ff', color: '#cc88ff', textTransform: 'none' }}
                    >
                      Upload Voice Samples
                      <input type="file" hidden multiple accept="audio/*"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files?.length) return;
                          setToast('Cloning voice... this takes a moment');
                          const formData = new FormData();
                          formData.append('name', 'Vesper Custom');
                          formData.append('description', 'Custom cloned voice');
                          Array.from(files).forEach(f => formData.append('files', f));
                          try {
                            const res = await fetch(`${apiBase}/api/voice/clone?name=VesperCustom`, {
                              method: 'POST', body: formData,
                            });
                            const data = await res.json();
                            if (data.success) {
                              setToast('Voice cloned! It\'s now in your voice picker.');
                              // Refresh voices
                              const vRes = await fetch(`${apiBase}/api/tts/voices`);
                              const vData = await vRes.json();
                              if (vData.voices) setCloudVoices(vData.voices);
                            } else {
                              setToast('Clone failed: ' + (data.error || 'Unknown error'));
                            }
                          } catch (err) { setToast('Clone error: ' + err.message); }
                        }}
                      />
                    </Button>
                  </Box>

                  {/* Voice Isolation */}
                  <Box sx={{ p: 1.5, border: '1px solid rgba(100,255,200,0.3)', borderRadius: 2, bgcolor: 'rgba(100,255,200,0.05)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#66ffbb' }}>Voice Isolation</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Remove background noise from any audio file
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ borderColor: '#66ffbb', color: '#66ffbb', textTransform: 'none' }}
                    >
                      Upload Audio to Clean
                      <input type="file" hidden accept="audio/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setToast('Isolating voice... removing background noise');
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const res = await fetch(`${apiBase}/api/voice/isolate`, {
                              method: 'POST', body: formData,
                            });
                            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const audio = new Audio(url);
                            audio.play();
                            audio.onended = () => URL.revokeObjectURL(url);
                            setToast('Playing isolated voice!');
                          } catch (err) { setToast('Isolation error: ' + err.message); }
                        }}
                      />
                    </Button>
                  </Box>
                </Stack>
                </AccordionDetails>
              </Accordion>

              {/* AI Models */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>🤖 AI Model</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Box sx={{ p: 1.5, border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 2, bgcolor: 'rgba(var(--accent-rgb),0.04)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>Active Model</Typography>
                    <Select
                      size="small"
                      fullWidth
                      value={selectedModel}
                      onChange={(e) => {
                        setSelectedModel(e.target.value);
                        try { localStorage.setItem('vesper_model', e.target.value); } catch(ex) {}
                        setToast(`Model set: ${e.target.value === 'auto' ? 'Auto (best available)' : e.target.value}`);
                      }}
                      sx={{
                        color: 'var(--accent)', bgcolor: 'rgba(0,0,0,0.25)',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--accent-rgb),0.3)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--accent)' },
                        '.MuiSvgIcon-root': { color: 'var(--accent)' },
                      }}
                      MenuProps={{ PaperProps: { sx: { bgcolor: 'rgba(8,8,18,0.97)', backdropFilter: 'blur(20px)', border: '1px solid rgba(0,255,255,0.15)', '& .MuiMenuItem-root': { color: '#fff', fontSize: '0.85rem' }, '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(0,255,255,0.1)' } } } }}
                    >
                      <MenuItem value="auto">🔄 Auto (best available)</MenuItem>
                      {availableModels.map(m => (
                        <MenuItem key={m.id} value={m.id}>{m.icon} {m.label}{m.badge ? ` · ${m.badge}` : ''}</MenuItem>
                      ))}
                      {availableModels.length === 0 && (
                        <MenuItem value="gpt-4o-mini">☁️ GPT-4o-mini (cloud)</MenuItem>
                      )}
                    </Select>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.75, display: 'block' }}>
                      {availableModels.length > 0 ? `${availableModels.length} models available` : 'Connect to Ollama for local models'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* System Info */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>🩺 System Status</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDiagnosticsOpen(true)}
                      startIcon={<SpeedIcon sx={{ fontSize: '0.85rem !important' }} />}
                      sx={{ borderColor: 'var(--accent)', color: 'var(--accent)', textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25 }}
                    >
                      Run Diagnostics
                    </Button>
                  </Box>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Backend / Railway</Typography>
                      <Chip label="Check Status →" size="small" onClick={() => setDiagnosticsOpen(true)}
                        sx={{ bgcolor: 'rgba(0,255,255,0.12)', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,255,255,0.2)' } }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Conversations</Typography>
                      <Chip label={`${threads.length} saved`} size="small" sx={{ bgcolor: 'rgba(0,255,255,0.2)', color: 'var(--accent)', fontWeight: 600 }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Storage</Typography>
                      <Chip label="Auto-Save ON" size="small" sx={{ bgcolor: 'rgba(74,222,128,0.2)', color: '#4ade80', fontWeight: 600 }} />
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Actions */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>🗄️ Data Management</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Stack spacing={1}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      size="small"
                      onClick={() => {
                        exportAllData();
                        playSound('click');
                      }}
                      sx={{ 
                        borderColor: 'var(--accent)', 
                        color: 'var(--accent)',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(0,255,255,0.1)', borderColor: 'var(--accent)' }
                      }}
                    >
                      Export All Data
                    </Button>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      size="small"
                      onClick={() => {
                        clearHistory();
                        playSound('click');
                      }}
                      sx={{ 
                        borderColor: 'rgba(255,255,255,0.2)', 
                        color: '#fff',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.3)' }
                      }}
                    >
                      Clear Current Chat
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Advanced Customization */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>⚙️ Advanced Customization</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack spacing={1.5}>
                  {/* Font Size */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Font Size</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {['small', 'medium', 'large'].map(size => (
                        <Chip
                          key={size}
                          label={size}
                          onClick={() => applyFontSize(size)}
                          variant={customizations.fontSize === size ? 'filled' : 'outlined'}
                          color={customizations.fontSize === size ? 'primary' : 'default'}
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Compact Mode */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Compact Mode</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Reduce spacing and padding</Typography>
                    </Box>
                    <Switch
                      checked={customizations.compactMode}
                      onChange={toggleCompactMode}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--accent)' },
                      }}
                    />
                  </Box>

                  {/* Analytics Display */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Analytics Display</Typography>
                    <Select
                      value={customizations.analyticsCharts}
                      onChange={(e) => updateCustomization('analyticsCharts', e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#fff' }}
                    >
                      <MenuItem value="all">All Charts</MenuItem>
                      <MenuItem value="summary">Summary Only</MenuItem>
                      <MenuItem value="detailed">Detailed Only</MenuItem>
                    </Select>
                  </Box>

                  {/* Custom Memory Categories */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Memory Categories</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1 }}>
                      {customizations.memoryCategories.map(cat => (
                        <Chip
                          key={cat}
                          label={cat}
                          onDelete={() => removeMemoryCategory(cat)}
                          size="small"
                          className="chip-soft"
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <TextField
                        value={newMemoryCategory}
                        onChange={(e) => setNewMemoryCategory(e.target.value)}
                        placeholder="New category..."
                        size="small"
                        variant="filled"
                        onKeyPress={(e) => e.key === 'Enter' && addMemoryCategory()}
                        InputProps={{ sx: { color: '#fff' } }}
                        sx={{ flex: 1 }}
                      />
                      <Button onClick={addMemoryCategory} size="small" variant="outlined">Add</Button>
                    </Stack>
                  </Box>

                  {/* Custom Research Sources */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Research Sources</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1 }}>
                      {customizations.researchSources.map(src => (
                        <Chip
                          key={src}
                          label={src}
                          onDelete={() => removeResearchSource(src)}
                          size="small"
                          className="chip-soft"
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <TextField
                        value={newResearchSource}
                        onChange={(e) => setNewResearchSource(e.target.value)}
                        placeholder="New source..."
                        size="small"
                        variant="filled"
                        onKeyPress={(e) => e.key === 'Enter' && addResearchSource()}
                        InputProps={{ sx: { color: '#fff' } }}
                        sx={{ flex: 1 }}
                      />
                      <Button onClick={addResearchSource} size="small" variant="outlined">Add</Button>
                    </Stack>
                  </Box>
                </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Advanced Export */}
              <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px !important' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--accent)' }} />} sx={{ px: 2, py: 0.5, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--accent)' }}>📥 Advanced Export</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Select what to export:</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                      {['memories', 'tasks', 'research', 'documents', 'conversations'].map(item => (
                        <FormControlLabel
                          key={item}
                          control={
                            <Checkbox
                              checked={exportSelection[item]}
                              onChange={(e) => setExportSelection(s => ({ ...s, [item]: e.target.checked }))}
                              size="small"
                            />
                          }
                          label={<Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{item}</Typography>}
                          sx={{ m: 0, mr: 1 }}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={exportAsMarkdown}
                        sx={{ flex: 1 }}
                      >
                        📄 Markdown
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={exportAsJSON}
                        sx={{ flex: 1 }}
                      >
                        {} JSON
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={exportAsCSV}
                        sx={{ flex: 1 }}
                      >
                        📊 CSV
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Paper>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={baseTheme}>
      <CssBaseline />
      
      {/* Background layers — absolute positioned in a full-page wrapper */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <div className="bg-layer gradient-background" style={{ background: activeTheme.bg || '#000', position: 'absolute', inset: 0, ...(customBackground ? { opacity: backgroundSettings?.overlay !== false ? (backgroundSettings?.opacity ?? 0.3) : 0 } : {}) }} />
        {/* Custom background image layer */}
        {customBackground?.url && (
          <div className="bg-layer custom-background" style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${customBackground.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: (backgroundSettings?.blur || 0) > 0 ? `blur(${backgroundSettings.blur}px)` : 'none',
            zIndex: -1,
          }} />
        )}
        <div className="bg-layer hex-grid" style={{ position: 'absolute', inset: 0, ...(activeTheme.bgImage && !customBackground ? { backgroundImage: activeTheme.bgImage, backgroundSize: activeTheme.bgSize || 'cover', backgroundPosition: 'center' } : {}) }} />
        <div className="bg-layer scanlines" style={{ position: 'absolute', inset: 0, ...(activeTheme.scanlines === false ? { display: 'none' } : {}) }} />
      </Box>
      
      <Box sx={{ 
        transform: `scale(${uiScale})`,
        transformOrigin: 'top left',
        width: `${100 / uiScale}%`,
        minHeight: `${100 / uiScale}vh`,
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'relative',
        zIndex: 1,
      }}>
      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onCommand={handleCommand} />


      {/* Background layers removed from here — they're now above the transform Box */}
      
      {/* Subtle Matrix binary - only shown on Neon Green theme */}
      {activeTheme.id === 'green' && [...Array(8)].map((_, i) => {
        const digits = Array.from({ length: 60 }, () => Math.random() > 0.5 ? '1' : '0');
        return (
          <div 
            key={i} 
            className="binary-column-stack" 
            style={{
              left: `${15 + (i * 10)}%`,
              animationDuration: `${12 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 5}s`,
              fontSize: `${9 + Math.random() * 6}px`,
            }}
          >
            {digits.map((digit, idx) => (
              <div key={idx} className="single-digit">{digit}</div>
            ))}
          </div>
        );
      })}

      <Box className={`app-shell ${launchMode ? 'launch-mode' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={themeVars} data-style={activeTheme.style || 'cyber'}>
        {/* Scanlines overlay - renders when theme has scanlines enabled */}
        {activeTheme.scanlines && (
          <div className="scanlines" style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9999,
          }} />
        )}
        <aside ref={sidebarRef} className={`sidebar glass-panel${mobileSidebarOpen ? ' mobile-open' : ''}${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box className="sidebar-title-block" sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 2 }}>
                VESPER AI
              </Typography>
              <Typography variant="h6" sx={{ color: 'var(--accent)', fontWeight: 800 }}>
                Ops Console
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: 'rgba(255,255,255,0.48)', letterSpacing: 0.8 }}>
                {UI_RELEASE}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.35)' }}>
                Theme: {activeTheme.label}
              </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
            {NAV.map(({ id, label, icon: Icon }) => (
              <Box
                key={id}
                className={`nav-item ${activeSection === id ? 'active' : ''}`}
                title={label}
                onClick={() => {
                  setActiveSection(id);
                  playSound('click');
                  setMobileSidebarOpen(false);
                }}
              >
                <Icon fontSize="small" />
                <span className="nav-label">{label}</span>
                {id === 'income' && incomeStats && incomeStats.total_est_monthly > 0 && (
                  <span style={{
                    marginLeft: 'auto', fontSize: '0.6rem', fontFamily: 'monospace',
                    color: '#6bcb77', fontWeight: 700, opacity: 0.85,
                    background: 'rgba(107,203,119,0.1)', borderRadius: 6, padding: '1px 5px',
                  }}>
                    ~${Math.round(incomeStats.total_est_monthly)}/mo
                  </span>
                )}
              </Box>
            ))}
          </Box>

          {/* Chat Sessions List - Gemini Style */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            pt: 1
          }}>
            {/* Header row: label + new chat button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>
                Conversations
              </Typography>
              <Tooltip title="New Chat" placement="right">
                <IconButton size="small" onClick={startNewChat} sx={{ color: 'var(--accent)', p: 0.3, '&:hover': { bgcolor: 'rgba(0,255,255,0.12)' } }}>
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {/* Thread List - Gemini Style */}
            <Box sx={{ 
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.3,
              pr: 0.3,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 255, 255, 0.3)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0, 255, 255, 0.5)',
              }
            }}>
              {threadsLoading ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <CircularProgress size={20} sx={{ color: 'var(--accent)' }} />
                </Box>
              ) : threads && threads.length > 0 ? (
                (() => {
                  const sortedThreads = [...threads].sort((a, b) => {
                    if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
                    return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
                  });
                  const sidebarPinned = sortedThreads.filter(t => t.pinned);
                  const sidebarRecent = sortedThreads.filter(t => !t.pinned);

                  const renderSidebarThread = (thread) => (
                  <Box
                    key={thread.id}
                    sx={{
                      p: '8px 10px',
                      borderRadius: '8px',
                      bgcolor: currentThreadId === thread.id ? 'rgba(0,255,255,0.15)' : 'transparent',
                      border: currentThreadId === thread.id ? '1px solid rgba(0,255,255,0.4)' : '1px solid transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      '&:hover': {
                        bgcolor: 'rgba(0,255,255,0.1)',
                        borderColor: 'rgba(0,255,255,0.3)',
                      },
                      group: 'hover'
                    }}
                    onClick={() => !editingThreadId && loadThread(thread.id)}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {thread.pinned && (
                        <PinIcon sx={{ fontSize: '0.75rem', color: 'var(--accent)', flexShrink: 0 }} />
                      )}
                      {editingThreadId === thread.id ? (
                        <TextField
                          value={editingThreadTitle}
                          onChange={(e) => setEditingThreadTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameThread(thread.id);
                            if (e.key === 'Escape') cancelRenameThread();
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          size="small"
                          variant="standard"
                          sx={{ 
                            flex: 1,
                            input: { 
                              color: '#fff', 
                              fontSize: '0.85rem',
                              padding: '4px 0'
                            }
                          }}
                        />
                      ) : (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: thread.pinned ? 600 : 400,
                            color: thread.pinned ? 'var(--accent)' : '#fff',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.85rem'
                          }}
                          title={thread.title}
                        >
                          {sanitizeTitle(thread.title)}
                        </Typography>
                      )}
                    </Box>
                    {editingThreadId === thread.id ? (
                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              renameThread(thread.id);
                            }}
                            sx={{ 
                              p: 0.2,
                              color: 'var(--accent)',
                              '&:hover': { color: '#00ff88' }
                            }}
                          >
                            <ChecklistRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelRenameThread();
                            }}
                            sx={{ 
                              p: 0.2,
                              color: 'rgba(255,255,255,0.5)',
                              '&:hover': { color: '#ff4444' }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: 0.25,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          flexShrink: 0,
                          '&:has(> button:hover)': { opacity: 1 },
                          '.MuiBox-root:hover &': { opacity: 1 }
                        }}
                      >
                        <Tooltip title="Share">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Share thread - export as JSON
                              const threadData = {
                                title: thread.title,
                                timestamp: thread.created_at,
                                messages: thread.messages || [],
                              };
                              const json = JSON.stringify(threadData, null, 2);
                              const link = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
                              
                              // Download the thread
                              const downloadLink = document.createElement('a');
                              downloadLink.href = link;
                              downloadLink.download = `vesper-thread-${thread.id}.json`;
                              downloadLink.click();
                              setToast(`📥 Thread exported as JSON`);
                            }}
                            sx={{ 
                              p: 0.2,
                              color: 'rgba(255,255,255,0.5)',
                              '&:hover': { color: 'var(--accent)' }
                            }}
                          >
                            <span style={{ fontSize: '0.9rem' }}>↗</span>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={thread.pinned ? 'Unpin' : 'Pin'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePinThread(thread.id);
                            }}
                            sx={{ 
                              p: 0.2,
                              color: thread.pinned ? 'var(--accent)' : 'rgba(255,255,255,0.5)',
                              '&:hover': { color: 'var(--accent)' }
                            }}
                          >
                            {thread.pinned ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              startRenameThread(thread.id, thread.title);
                            }}
                            sx={{ 
                              p: 0.2,
                              color: 'rgba(255,255,255,0.5)',
                              '&:hover': { color: 'var(--accent)' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteThread(thread.id);
                            }}
                            sx={{ 
                              p: 0.2,
                              color: 'rgba(255,255,255,0.5)',
                              '&:hover': { color: '#ff4444' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  );

                  return (
                    <>
                      {sidebarPinned.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: 'var(--accent)', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.5, px: 1, pt: 0.5 }}>
                            📌 Pinned
                          </Typography>
                          {sidebarPinned.map(renderSidebarThread)}
                          <Box sx={{ borderBottom: '1px solid rgba(0,255,255,0.15)', my: 0.5, mx: 1 }} />
                        </>
                      )}
                      {sidebarRecent.length > 0 && (
                        <>
                          {sidebarPinned.length > 0 && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1.5, px: 1, pt: 0.5 }}>
                              Recent
                            </Typography>
                          )}
                          {sidebarRecent.map(renderSidebarThread)}
                        </>
                      )}
                    </>
                  );
                })()
              ) : !threadsLoading && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.5)',
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  No chats yet
                </Typography>
              )}
            </Box>
          </Box>

          {/* Sidebar collapse toggle */}
          <Box
            sx={{
              mt: 'auto',
              pt: 1,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            }}
          >
            {!sidebarCollapsed && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.62rem', userSelect: 'none' }}>
                VESPER AI
              </Typography>
            )}
            <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton
                size="small"
                onClick={() => {
                  const next = !sidebarCollapsed;
                  setSidebarCollapsed(next);
                  try { localStorage.setItem('vesper_sidebar_collapsed', String(next)); } catch (_) {}
                }}
                sx={{
                  color: 'rgba(255,255,255,0.35)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  width: 28, height: 28,
                  '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(var(--accent-rgb),0.1)', borderColor: 'rgba(var(--accent-rgb),0.3)' },
                }}
              >
                {sidebarCollapsed
                  ? <ChevronRightIcon sx={{ fontSize: 16 }} />
                  : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </aside>

        {/* Mobile backdrop – closes sidebar when tapped */}
        {mobileSidebarOpen && (
          <div
            className="mobile-sidebar-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        <main className="content-grid" data-section={activeSection}>
          <section className="chat-panel glass-panel">
            <Box className="chat-inner-wrap" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                {/* Hamburger – mobile only */}
                <IconButton
                  className="mobile-menu-btn"
                  size="small"
                  onClick={() => setMobileSidebarOpen(o => !o)}
                  sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' }, flexShrink: 0 }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--accent)' }}>
                    Neural Chat
                  </Typography>
                  <Chip
                    label={runtimeCapabilities?.features?.tts ? 'Voice Ready' : 'Voice Blocked'}
                    size="small"
                    onClick={() => setActiveSection('settings')}
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      bgcolor: runtimeCapabilities?.features?.tts ? 'rgba(0,255,136,0.12)' : 'rgba(255,190,92,0.12)',
                      color: runtimeCapabilities?.features?.tts ? '#7ff2b8' : '#f6d38c',
                      border: `1px solid ${runtimeCapabilities?.features?.tts ? 'rgba(0,255,136,0.28)' : 'rgba(255,190,92,0.28)'}`,
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      height: 24,
                      cursor: 'pointer',
                    }}
                  />
                  {vesperIdentity?.mood && (
                    <Chip
                      label={`${vesperIdentity.mood?.emoji || '✨'} ${vesperIdentity.mood?.label || vesperIdentity.mood?.id || ''}`}
                      size="small"
                      onClick={() => setActiveSection('settings')}
                      sx={{
                        display: { xs: 'none', sm: 'inline-flex' },
                        bgcolor: vesperIdentity.mood?.color ? `${vesperIdentity.mood.color}22` : 'rgba(0,255,255,0.1)',
                        color: vesperIdentity.mood?.color || 'var(--accent)',
                        border: `1px solid ${vesperIdentity.mood?.color || 'var(--accent)'}44`,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: vesperIdentity.mood?.color ? `${vesperIdentity.mood.color}44` : 'rgba(0,255,255,0.2)',
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  )}
                </Box>
                {currentThreadId && (
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      💬 {currentThreadTitle}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<HistoryRounded fontSize="small" />}
                      onClick={() => setThreadsDialogOpen(true)}
                      sx={{
                        color: '#f2deaa',
                        minWidth: 0,
                        px: 0.75,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: 'rgba(242,222,170,0.08)' },
                      }}
                    >
                      Conversations
                    </Button>
                  </Box>
                )}
                {threads.length > 1 && !launchMode && (
                  <Box className="recent-threads-row" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.85, maxWidth: 560 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', mr: 0.25 }}>
                      Recent:
                    </Typography>
                    {threads
                      .filter((thread) => thread.id !== currentThreadId)
                      .slice(0, 3)
                      .map((thread) => (
                        <Chip
                          key={thread.id}
                          label={thread.title || 'Untitled'}
                          size="small"
                          onClick={() => loadThread(thread.id)}
                          sx={{
                            maxWidth: 180,
                            bgcolor: 'rgba(255,255,255,0.04)',
                            color: 'rgba(255,255,255,0.82)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.12)', borderColor: 'rgba(var(--accent-rgb),0.26)' },
                          }}
                        />
                      ))}
                  </Box>
                )}
              </Box>
              </Box>{/* end mobile-menu-btn + title wrapper */}
              <Box className="chat-header-controls" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5 }}>
                {/* Model Picker */}
                {availableModels.length > 0 && (
                  <Select
                    size="small"
                    value={selectedModel}
                    onChange={(e) => {
                      setSelectedModel(e.target.value);
                      try { localStorage.setItem('vesper_model', e.target.value); } catch(ex) {}
                    }}
                    sx={{
                      height: 32,
                      minWidth: 100,
                      fontSize: '0.75rem',
                      color: 'var(--accent)',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,255,255,0.25)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--accent)' },
                      '.MuiSvgIcon-root': { color: 'var(--accent)', fontSize: 16 },
                      bgcolor: 'rgba(0,0,0,0.2)',
                    }}
                  >
                    <MenuItem value="auto" sx={{ fontSize: '0.8rem' }}>🔄 Auto</MenuItem>
                    {availableModels.map(m => (
                      <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.8rem' }}>
                        {m.icon} {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={startNewChat}
                  startIcon={<AddIcon />}
                  sx={{
                    color: 'var(--accent)',
                    borderColor: 'rgba(0,255,255,0.35)',
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: { xs: 36, sm: 96 },
                    height: 32,
                    px: { xs: 0.5, sm: 1.25 },
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                    '&:hover': {
                      bgcolor: 'rgba(0,255,255,0.1)',
                      borderColor: 'var(--accent)',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Chat</Box>
                </Button>

                {/* Auto-speak Toggle */}
                <Tooltip title={autoSpeak ? "Auto-speak ON (click to disable)" : "Auto-speak OFF (click to enable)"} placement="left">
                  <IconButton
                    size="small"
                    onClick={toggleAutoSpeak}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      color: autoSpeak ? '#00ff88' : 'rgba(255,255,255,0.3)',
                      bgcolor: autoSpeak ? 'rgba(0,255,136,0.1)' : 'transparent',
                      border: autoSpeak ? '1px solid rgba(0,255,136,0.4)' : '1px solid rgba(255,255,255,0.15)',
                      '&:hover': { color: '#00ff88', bgcolor: 'rgba(0,255,136,0.15)' },
                      width: 32, height: 32,
                    }}
                  >
                    <RecordVoiceOverIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                {/* Export Chat */}
                <Tooltip title="Export chat" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => exportChat('markdown')}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      color: 'rgba(255,255,255,0.4)',
                      '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(0,255,255,0.1)' },
                      width: 32, height: 32,
                    }}
                  >
                    <SaveAltIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={launchMode ? 'Launch Mode ON (click to edit)' : 'Launch Mode OFF (click for clean demo view)'} placement="left">
                  <IconButton
                    size="small"
                    onClick={() => setLaunchMode((prev) => !prev)}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      color: launchMode ? '#f2deaa' : 'rgba(255,255,255,0.35)',
                      bgcolor: launchMode ? 'rgba(242,222,170,0.1)' : 'transparent',
                      border: launchMode ? '1px solid rgba(242,222,170,0.4)' : '1px solid rgba(255,255,255,0.15)',
                      '&:hover': { color: '#f2deaa', bgcolor: 'rgba(242,222,170,0.14)' },
                      width: 32,
                      height: 32,
                    }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 800 }}>L</span>
                  </IconButton>
                </Tooltip>

                {/* Voice ON/OFF Toggle */}
                <Tooltip title={ttsEnabled ? "Turn voice OFF" : "Turn voice ON"} placement="left">
                  <IconButton 
                    size="small" 
                    onClick={toggleTTS}
                    sx={{ 
                      color: ttsEnabled ? 'var(--accent)' : 'rgba(255,255,255,0.3)',
                      bgcolor: ttsEnabled ? 'rgba(0,255,255,0.1)' : 'transparent',
                      border: ttsEnabled ? '1px solid var(--accent)' : '1px solid rgba(255,255,255,0.15)',
                      '&:hover': { color: 'var(--accent)', bgcolor: 'rgba(0,255,255,0.15)' },
                      width: 32, height: 32,
                    }}
                  >
                    {ttsEnabled ? <VolumeUpIcon sx={{ fontSize: 18 }} /> : <VolumeOffIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>

                {/* Voice picker moved to Voice Lab > Personas tab */}

                {isSpeaking && (
                  <Chip 
                    label="Speaking..." 
                    size="small"
                    onClick={stopSpeaking}
                    sx={{ 
                      height: 24,
                      bgcolor: 'rgba(0,255,255,0.15)',
                      color: 'var(--accent)',
                      borderColor: 'var(--accent)',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      cursor: 'pointer'
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Voice selector moved to Voice Lab > Personas tab */}

            <Box className="chat-avatar-wrapper" sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: { xs: 1.5, sm: 1 }, alignItems: { xs: 'center', sm: 'flex-start' } }}>
              <Box
                className="chat-avatar-card"
                sx={{ borderColor: `${activeTheme.accent}55`, width: { xs: 60, sm: 220 }, height: { xs: 60, sm: 280 }, borderRadius: { xs: '50%', sm: '20px' }, flexShrink: 0 }}
              >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 'inherit',
                      maskImage: 'linear-gradient(to bottom, black 0%, black 62%, transparent 94%)',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 62%, transparent 94%)',
                      animation: isSpeaking ? 'portraitShimmer 3.2s ease-in-out infinite, portraitBreathing 5.5s ease-in-out infinite' : 'portraitBreathing 7s ease-in-out infinite',
                    }}
                  >
                    <video
                      ref={avatarVideoRef}
                      src={videoAvatarUrl || `${mediaBase}/media/source/vesper_base.mp4`}
                      autoPlay
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: isSpeaking ? `brightness(1.15) drop-shadow(0 0 12px ${activeTheme.accent}88)` : 'brightness(1)',
                        animation: isSpeaking ? 'portraitLips 1.25s ease-in-out infinite, portraitGaze 16s ease-in-out infinite' : 'none',
                      }}
                      muted
                      playsInline
                      controls={false}
                      onLoadedData={() => {
                        const shouldAnimate = Boolean(streamingMessageId) || isSpeaking || videoShouldAutoplay;
                        if (shouldAnimate) return;
                        if (avatarVideoRef.current) {
                          avatarVideoRef.current.pause();
                          try {
                            avatarVideoRef.current.currentTime = 0.1;
                          } catch (error) {
                            // Ignore seek timing issues while media initializes.
                          }
                        }
                      }}
                      onError={() => {
                        // Video failed — retry loading after 3s
                        setTimeout(() => {
                          if (avatarVideoRef.current) {
                            avatarVideoRef.current.load();
                          }
                        }, 3000);
                      }}
                      onEnded={() => {
                        if (!videoShouldAutoplay) return;
                        if (avatarVideoRef.current) {
                          const holdAt = Math.max(0, avatarVideoRef.current.duration - 0.04);
                          avatarVideoRef.current.currentTime = holdAt;
                        }
                        setVideoShouldAutoplay(false);
                      }}
                    />
                  </Box>
              </Box>

              {/* Mobile-only: name + status beside the face */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0.3, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'var(--accent)', letterSpacing: '0.06em', lineHeight: 1.1 }}>
                  VESPER
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500, lineHeight: 1.2 }}>
                  {vesperIdentity?.mood?.emoji || '✨'} {vesperIdentity?.mood?.label || 'Ready'}
                </Typography>
                {isSpeaking && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.1em', animation: 'textGlow 1.5s ease-in-out infinite' }}>
                    ● SPEAKING
                  </Typography>
                )}
              </Box>

            </Box>

            <Paper 
              ref={chatContainerRef} 
              className="chat-window glass-card"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{ position: 'relative' }}
            >
              {/* Drag overlay */}
              {isDraggingFile && (
                <Box sx={{
                  position: 'absolute', inset: 0, zIndex: 10,
                  bgcolor: 'rgba(0,255,255,0.08)', border: '2px dashed var(--accent)',
                  borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                }}>
                  <Typography sx={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1.1rem' }}>
                    📎 Drop files here
                  </Typography>
                </Box>
              )}

              {/* ── In-chat search bar ─────────────────────────────── */}
              {chatSearchOpen && (
                <Box sx={{
                  position: 'sticky', top: 0, zIndex: 50,
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 0.9,
                  background: 'rgba(8,10,18,0.97)',
                  borderBottom: '1px solid rgba(var(--accent-rgb),0.18)',
                  backdropFilter: 'blur(12px)',
                  animation: 'searchSlideDown 0.18s cubic-bezier(0.22,1,0.36,1)',
                }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', flexShrink: 0 }}>⌕</Typography>
                  <input
                    autoFocus
                    value={chatSearchQuery}
                    onChange={(e) => { setChatSearchQuery(e.target.value); setChatSearchIdx(0); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (chatSearchMatches.length > 0) setChatSearchIdx(i => (i + 1) % chatSearchMatches.length);
                      }
                      if (e.key === 'Escape') { setChatSearchOpen(false); setChatSearchQuery(''); }
                    }}
                    placeholder="Search in conversation…"
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontSize: '0.85rem', fontFamily: 'inherit' }}
                  />
                  {chatSearchMatches.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                        {chatSearchIdx + 1} / {chatSearchMatches.length}
                      </Typography>
                      <IconButton size="small" onClick={() => setChatSearchIdx(i => (i - 1 + chatSearchMatches.length) % chatSearchMatches.length)} sx={{ p: 0.25, color: 'rgba(255,255,255,0.35)', '&:hover': { color: 'var(--accent)' } }}>
                        <Box component="span" sx={{ fontSize: '0.75rem' }}>▲</Box>
                      </IconButton>
                      <IconButton size="small" onClick={() => setChatSearchIdx(i => (i + 1) % chatSearchMatches.length)} sx={{ p: 0.25, color: 'rgba(255,255,255,0.35)', '&:hover': { color: 'var(--accent)' } }}>
                        <Box component="span" sx={{ fontSize: '0.75rem' }}>▼</Box>
                      </IconButton>
                    </Box>
                  )}
                  {chatSearchQuery && chatSearchMatches.length === 0 && (
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,100,100,0.7)', flexShrink: 0 }}>No matches</Typography>
                  )}
                  <IconButton size="small" onClick={() => { setChatSearchOpen(false); setChatSearchQuery(''); }} sx={{ p: 0.25, color: 'rgba(255,255,255,0.25)', '&:hover': { color: '#fff' }, flexShrink: 0 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              )}

              {/* ── Pinned messages panel ─────────────────────────── */}
              {Object.keys(pinnedMsgIds).length > 0 && (
                <Box sx={{
                  mx: 1, mb: 1, borderRadius: 2,
                  border: '1px solid rgba(var(--accent-rgb),0.2)',
                  bgcolor: 'rgba(var(--accent-rgb),0.04)',
                  backdropFilter: 'blur(8px)',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid rgba(var(--accent-rgb),0.1)' }}>
                    <PinIcon sx={{ fontSize: 13, color: 'var(--accent)' }} />
                    <Typography sx={{ fontSize: '0.68rem', color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', flex: 1 }}>
                      {Object.keys(pinnedMsgIds).length} Pinned
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1.5, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {Object.values(pinnedMsgIds).map(pm => (
                      <Box key={pm.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.71rem', color: 'rgba(255,255,255,0.58)', lineHeight: 1.5, flex: 1 }}>
                          {pm.content.slice(0, 120)}{pm.content.length > 120 ? '…' : ''}
                        </Typography>
                        <IconButton size="small" onClick={() => togglePinMessage(pm)}
                          sx={{ p: 0.2, color: 'rgba(255,255,255,0.28)', '&:hover': { color: '#ff5555' }, flexShrink: 0 }}>
                          <CloseIcon sx={{ fontSize: 11 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <AnimatePresence>
                {/* Ambient background orbs */}
                <Box key="orb1" sx={{
                  position: 'absolute', pointerEvents: 'none', zIndex: 0,
                  width: 320, height: 320, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(var(--accent-rgb),0.07) 0%, transparent 70%)',
                  top: '15%', left: '-60px',
                  animation: 'orbFloat1 18s ease-in-out infinite',
                  filter: 'blur(40px)',
                }} />
                <Box key="orb2" sx={{
                  position: 'absolute', pointerEvents: 'none', zIndex: 0,
                  width: 240, height: 240, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(var(--accent-rgb),0.05) 0%, transparent 70%)',
                  bottom: '10%', right: '-40px',
                  animation: 'orbFloat2 22s ease-in-out infinite',
                  filter: 'blur(50px)',
                }} />
                {messages.map((message, idx) => {
                  const prev = messages[idx - 1];
                  const isGrouped = prev && prev.role === message.role && !['chart','sandbox_image','weather_card'].includes(message.type);
                  const isSearchMatch = chatSearchOpen && chatSearchMatches.includes(idx);
                  const isActiveMatch = isSearchMatch && chatSearchMatches[chatSearchIdx] === idx;
                  return (
                    <div
                      key={message.id}
                      ref={isActiveMatch ? (el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }) : null}
                      style={isSearchMatch ? {
                        outline: `2px solid ${isActiveMatch ? 'rgba(var(--accent-rgb),0.8)' : 'rgba(var(--accent-rgb),0.25)'}`,
                        borderRadius: '16px',
                        boxShadow: isActiveMatch ? '0 0 20px rgba(var(--accent-rgb),0.25)' : 'none',
                        transition: 'all 0.2s ease',
                      } : undefined}
                    >
                      {renderMessage(message, isGrouped)}
                    </div>
                  );
                })}
              </AnimatePresence>
              {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, my: 1 }}>
                  {activeToolName ? (
                    // ── EXECUTING a tool — bright, urgent, animated ──
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2, py: 1, borderRadius: 2,
                      background: 'linear-gradient(90deg, rgba(0,255,136,0.12), rgba(0,200,255,0.08))',
                      border: '1px solid rgba(0,255,136,0.35)',
                      boxShadow: '0 0 12px rgba(0,255,136,0.15)',
                      animation: 'vesperToolPulse 1.8s ease-in-out infinite',
                    }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%', bgcolor: '#00ff88', flexShrink: 0,
                        boxShadow: '0 0 6px #00ff88',
                        animation: 'vesperDotPulse 1s ease-in-out infinite',
                      }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#00ff88', fontFamily: 'monospace', letterSpacing: '0.08em', flexShrink: 0 }}>
                        ⚡ WORKING
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.88)', fontFamily: 'monospace' }}>
                        {activeToolLabel}
                      </Typography>
                    </Box>
                  ) : (
                    // ── Just thinking / generating text ──
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box className="vesper-thinking">
                        <span /><span /><span />
                      </Box>
                      {thinkingStatus && (
                        <Typography variant="caption" sx={{ color: 'var(--accent)', opacity: 0.7, fontStyle: 'italic' }}>
                          {thinkingStatus}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {/* Tool history pills for this turn */}
                  {toolHistory.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1.5 }}>
                      {toolHistory.map((t, i) => (
                        <Box key={i} sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          px: 1, py: 0.25, borderRadius: 10,
                          fontSize: '0.62rem', fontFamily: 'monospace', letterSpacing: '0.03em',
                          bgcolor: t.status === 'done'
                            ? 'rgba(0,255,136,0.08)'
                            : t.status === 'error'
                            ? 'rgba(255,80,80,0.1)'
                            : 'rgba(0,200,255,0.08)',
                          color: t.status === 'done' ? '#00dd77' : t.status === 'error' ? '#ff6060' : 'var(--accent)',
                          border: `1px solid ${t.status === 'done' ? 'rgba(0,255,136,0.2)' : t.status === 'error' ? 'rgba(255,80,80,0.2)' : 'rgba(0,200,255,0.18)'}`,
                        }}>
                          <span>{t.status === 'done' ? '✓' : t.status === 'error' ? '✗' : '◌'}</span>
                          <span>{t.label || t.name}</span>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              <div ref={messagesEndRef} />
              {/* Scroll to bottom button */}
              {showScrollBottom && (
                <Box sx={{ position: 'sticky', bottom: 8, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 5 }}>
                  <IconButton
                    onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{
                      pointerEvents: 'auto',
                      bgcolor: 'rgba(var(--accent-rgb), 0.15)',
                      border: '1px solid rgba(var(--accent-rgb), 0.35)',
                      color: 'var(--accent)',
                      width: 30, height: 30,
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: 'rgba(var(--accent-rgb), 0.3)', transform: 'scale(1.1)' },
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
            </Paper>

            {/* Chat Box Resize Handle */}
            <Box
              onMouseDown={startResizeChat}
              className="chat-resize-handle"
              sx={{
                height: '8px',
                cursor: 'ns-resize',
                background: 'linear-gradient(to bottom, rgba(0,255,255,0.3), rgba(0,255,255,0.1))',
                borderRadius: '0 0 8px 8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(to bottom, rgba(0,255,255,0.6), rgba(0,255,255,0.3))',
                  boxShadow: '0 0 10px rgba(0,255,255,0.4)',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
              }}
              title="Drag to resize chat window"
            >
              <Box sx={{ width: '30px', height: '2px', bgcolor: 'rgba(0,255,255,0.4)' }} />
            </Box>

            {/* Image Preview Area */}
            {uploadedImages.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, p: 1, overflowX: 'auto', mb: 1, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 2 }}>
                {uploadedImages.map((img, index) => (
                  <Box key={index} sx={{ position: 'relative', width: 60, height: 60, flexShrink: 0 }}>
                    <img 
                      src={img.dataUrl} 
                      alt={img.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--accent)' }} 
                    />
                    <IconButton
                      size="small"
                      onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== index))}
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8, 
                        width: 20, 
                        height: 20, 
                        bgcolor: '#ff4444', 
                        color: '#fff', 
                        '&:hover': { bgcolor: '#cc0000' } 
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Empty state welcome + suggested prompts */}
            {messages.length === 0 && (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', flex: 1, py: 4, px: 2, gap: 3,
                animation: 'fadeIn 0.6s ease',
              }}>
                {/* Vesper wordmark */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: '2.2rem', fontWeight: 700,
                    color: 'rgba(255,255,255,0.9)',
                    letterSpacing: '0.06em', lineHeight: 1,
                    mb: 0.5,
                  }}>
                    Vesper
                  </Typography>
                  {vesperGreeting ? (
                    <Typography sx={{ color: 'var(--accent)', fontStyle: 'italic', fontSize: '0.87rem', opacity: 0.85, maxWidth: 320, lineHeight: 1.5 }}>
                      {vesperGreeting}
                    </Typography>
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      How can I help you today?
                    </Typography>
                  )}
                  {/* Session briefing card */}
                  {sessionBriefing && (
                    <Box sx={{ mt: 1.5, px: 2, py: 1.25, borderRadius: '10px', border: '1px solid rgba(var(--accent-rgb), 0.18)', background: 'rgba(var(--accent-rgb), 0.05)', maxWidth: 420, textAlign: 'left' }}>
                      <Typography sx={{ fontSize: '0.67rem', color: 'var(--accent)', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5, fontFamily: 'monospace' }}>Last session</Typography>
                      <Typography
                        sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}
                        dangerouslySetInnerHTML={{ __html: sessionBriefing.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>') }}
                      />
                    </Box>
                  )}
                  {/* XP badge */}
                  {vesperXp.xp > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>LVL {vesperXp.level}</Typography>
                      <Box sx={{ width: 80, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', borderRadius: 2, bgcolor: 'var(--accent)', width: `${Math.min(100, (vesperXp.xp % 500) / 5)}%`, transition: 'width 0.6s ease' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}>{vesperXp.xp} XP</Typography>
                    </Box>
                  )}
                </Box>

                {/* Money-making prompt grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, width: '100%', maxWidth: 560 }}>
                  {[
                    { icon: '💰', label: 'Plan income',     prompt: 'Plan my income streams for this month. What are the top 3 ways to monetize my skills?' },
                    { icon: '📈', label: 'SEO article',     prompt: 'Write an SEO article about ' },
                    { icon: '🔑', label: 'Keywords',        prompt: 'Research profitable keywords for ' },
                    { icon: '📅', label: 'Content plan',    prompt: 'Create a 30-day content calendar for ' },
                    { icon: '📦', label: 'Digital product', prompt: 'Generate 5 digital product ideas for my audience. Include revenue projections.' },
                    { icon: '✦',  label: 'Just write',      prompt: 'Help me write a ' },
                  ].map(({ icon, label, prompt }) => (
                    <Box
                      key={label}
                      onClick={() => setInput(prompt)}
                      sx={{
                        px: 1.5, py: 1.25, borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.07)',
                        background: 'rgba(255,255,255,0.025)',
                        cursor: 'pointer', transition: 'all 0.18s ease',
                        '&:hover': {
                          border: '1px solid rgba(var(--accent-rgb), 0.35)',
                          background: 'rgba(var(--accent-rgb), 0.06)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.88rem', mb: 0.15, lineHeight: 1 }}>{icon}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.01em' }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
                {/* Money Ops shortcut */}
                <Box
                  onClick={() => setActiveSection('income')}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.7, borderRadius: '20px', border: '1px solid rgba(var(--accent-rgb), 0.2)', background: 'rgba(var(--accent-rgb), 0.04)', cursor: 'pointer', transition: 'all 0.18s', '&:hover': { background: 'rgba(var(--accent-rgb), 0.1)' } }}
                >
                  <TrendingUpIcon sx={{ fontSize: '0.9rem', color: 'var(--accent)', opacity: 0.7 }} />
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em' }}>Open Money Ops dashboard</Typography>
                </Box>
              </Box>
            )}


            {/* Proactive nudges — tool-specific next-action suggestions */}
            {pendingNudges.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, overflowX: 'auto', px: 1, alignItems: 'center' }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', flexShrink: 0 }}>next →</span>
                {pendingNudges.map((n, i) => (
                  <Chip
                    key={i}
                    label={n}
                    size="small"
                    onClick={() => { setInput(n); setPendingNudges([]); }}
                    onDelete={() => setPendingNudges(prev => prev.filter((_, idx) => idx !== i))}
                    sx={{
                      bgcolor: 'rgba(var(--accent-rgb), 0.08)',
                      border: '1px solid rgba(var(--accent-rgb), 0.28)',
                      color: 'var(--accent)', fontSize: '0.7rem',
                      '&:hover': { bgcolor: 'rgba(var(--accent-rgb), 0.18)' },
                    }}
                  />
                ))}
              </Box>
            )}
            {/* Proactive Suggestions */}
            {suggestions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, overflowX: 'auto', px: 1 }}>
                {suggestions.map((s, i) => (
                  <Chip 
                    key={i} 
                    label={s} 
                    onClick={() => {
                        setInput(s);
                        setSuggestions([]); // Clear after selection
                    }}
                    onDelete={() => setSuggestions(prev => prev.filter((_, idx) => idx !== i))}
                    sx={{ 
                      bgcolor: 'rgba(0,255,255,0.1)', 
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(0,255,255,0.3)',
                      color: 'var(--accent)',
                      '&:hover': { bgcolor: 'rgba(0,255,255,0.2)' }
                    }} 
                  />
                ))}
              </Box>
            )}

            {/* Background workers strip — live view of unattended agents */}
            {(workersInfo.running > 0 || workersInfo.total > 0) && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 1, alignItems: 'center' }}>
                <Chip
                  size="small"
                  onClick={openWorkersPanel}
                  icon={workersInfo.running > 0 ? <CircularProgress size={12} sx={{ color: 'var(--accent)' }} /> : undefined}
                  label={workersInfo.running > 0
                    ? `⚙️ ${workersInfo.running} worker${workersInfo.running > 1 ? 's' : ''} running — view progress`
                    : `⚙️ Workers & autopilot (${workersInfo.total})`}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: workersInfo.running > 0 ? 'rgba(var(--accent-rgb),0.15)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(var(--accent-rgb),0.3)',
                    color: workersInfo.running > 0 ? 'var(--accent)' : 'rgba(255,255,255,0.6)',
                    fontSize: '0.72rem',
                    '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.22)' },
                  }}
                />
              </Box>
            )}

            {/* Workers & autopilot panel */}
            <Dialog open={workersOpen} onClose={() => setWorkersOpen(false)} maxWidth="md" fullWidth
              PaperProps={{ sx: { bgcolor: 'rgba(8,12,24,0.97)', border: '1px solid rgba(var(--accent-rgb),0.3)', backdropFilter: 'blur(18px)' } }}>
              <DialogTitle sx={{ color: 'var(--accent)', fontFamily: 'monospace', fontSize: '1rem', letterSpacing: 1 }}>
                ⚙️ BACKGROUND WORKERS & AUTOPILOT
              </DialogTitle>
              <DialogContent>
                {workersInfo.workers.length === 0 && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                    No workers yet. Ask Vesper to handle something long-running and she'll spawn one.
                  </Typography>
                )}
                {workersInfo.workers.map((w) => (
                  <Box key={w.worker_id} sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: w.status === 'running' ? 'var(--accent)' : (w.status === 'completed' ? '#7fff9e' : '#ff8080') }}>
                        {w.status === 'running' ? '🔄' : (w.status === 'completed' ? '✅' : '⚠️')} {w.worker_name || w.worker_id}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', ml: 'auto', fontFamily: 'monospace' }}>
                        {w.status}{w.started ? ` · ${String(w.started).slice(0, 16).replace('T', ' ')}` : ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', mb: 0.75 }}>{w.task}</Typography>
                    {(w.steps || []).length > 0 && (
                      <Box sx={{ pl: 1, borderLeft: '2px solid rgba(var(--accent-rgb),0.25)' }}>
                        {w.steps.map((s, i) => (
                          <Typography key={i} sx={{ fontSize: '0.7rem', color: s.ok ? 'rgba(255,255,255,0.55)' : '#ffb080', fontFamily: 'monospace' }}>
                            {s.ok ? '·' : '✗'} step {s.step}: {s.tool}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {w.output_preview && (
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', mt: 0.75, fontStyle: 'italic' }}>
                        {w.output_preview.slice(0, 250)}{w.output_preview.length > 250 ? '…' : ''}
                      </Typography>
                    )}
                    {w.error && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#ff8080', mt: 0.5 }}>{w.error}</Typography>
                    )}
                  </Box>
                ))}
                {autopilotLog.length > 0 && (
                  <>
                    <Typography sx={{ color: 'var(--accent)', fontSize: '0.8rem', fontFamily: 'monospace', letterSpacing: 1, mt: 2, mb: 1 }}>
                      🤖 AUTOPILOT ACTIVITY
                    </Typography>
                    {autopilotLog.slice(0, 10).map((e, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: e.success ? '#7fff9e' : '#ff8080', fontFamily: 'monospace', flexShrink: 0 }}>
                          {e.success ? '✅' : '⚠️'} {String(e.ended || e.started || '').slice(0, 16).replace('T', ' ')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>
                          {e.job_name}: {String(e.output || '').slice(0, 120)}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </DialogContent>
            </Dialog>

            <Paper
              component="form"
              onSubmit={(e) => { e.preventDefault(); }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className="input-bar glass-card"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileShare}
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.txt,.md,.json,.csv,.log,.yaml,.yml"
              />
              <Tooltip title="Attach file — images, PDF, Word, Excel, CSV, text" placement="top">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  className="ghost-button"
                  size="small"
                  disabled={analyzingImage}
                >
                  {analyzingImage ? <CircularProgress size={20} sx={{ color: 'var(--accent)' }} /> : <AddIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Get Proactive Suggestions" placement="top">
                <IconButton
                  onClick={fetchSuggestions}
                  className="ghost-button"
                  size="small"
                  disabled={suggestionsLoading}
                >
                  {suggestionsLoading ? <CircularProgress size={20} sx={{ color: 'var(--accent)' }} /> : <AutoFixHigh fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Share clipboard with Vesper" placement="top">
                <IconButton
                  className="ghost-button"
                  size="small"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      if (text && text.trim()) {
                        setInput(prev => prev + (prev ? '\n\n' : '') + `[Clipboard: ${text.slice(0, 2000)}]`);
                      }
                    } catch {
                      // Permission denied or no clipboard content — silently ignore
                    }
                  }}
                >
                  <ContentPasteRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <VoiceInput onTranscript={handleVoiceTranscript} />
              {/* Slash command menu */}
              {slashMenuOpen && (() => {
                const filtered = SLASH_CMD_LIST.filter(c => c.cmd.startsWith('/' + slashQuery));
                return filtered.length > 0 ? (
                  <Box sx={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0, mb: 0.75,
                    bgcolor: 'rgba(5,10,20,0.97)', border: '1px solid rgba(var(--accent-rgb),0.35)',
                    borderRadius: 2, overflow: 'hidden', boxShadow: '0 -8px 30px rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(16px)', zIndex: 100,
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', px: 1.5, py: 0.5, display: 'block', fontSize: '0.6rem', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      Slash Commands — ↑↓ navigate · Enter execute
                    </Typography>
                    {filtered.map((c, i) => (
                      <Box
                        key={c.cmd}
                        onClick={() => executeSlashCommand(c.cmd, input.slice(c.cmd.length))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                          cursor: 'pointer', transition: 'all 0.15s',
                          bgcolor: i === slashIdx ? 'rgba(var(--accent-rgb),0.12)' : 'transparent',
                          borderLeft: i === slashIdx ? '2px solid var(--accent)' : '2px solid transparent',
                          '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.08)' },
                        }}
                      >
                        <Box sx={{ fontSize: '1.1rem', width: 24, textAlign: 'center', flexShrink: 0 }}>{c.icon}</Box>
                        <Box>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: i === slashIdx ? 'var(--accent)' : '#e0e0e0', fontWeight: 600 }}>{c.cmd}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>{c.desc}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ ml: 'auto', color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem', fontStyle: 'italic' }}>{c.hint}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null;
              })()}
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={8}
                value={input}
                onChange={(e) => {
                  const val = e.target.value;
                  setInput(val);
                  try { localStorage.setItem(`vesper_draft_${currentThreadId || 'new'}`, val); } catch(_) {}
                  if (val.match(/^\//)) {
                    const q = val.slice(1).split(' ')[0].toLowerCase();
                    setSlashQuery(q);
                    setSlashMenuOpen(true);
                    setSlashIdx(0);
                  } else {
                    setSlashMenuOpen(false);
                  }
                }}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (slashMenuOpen) {
                    const filtered = SLASH_CMD_LIST.filter(c => c.cmd.startsWith('/' + slashQuery));
                    if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIdx(p => Math.min(p + 1, filtered.length - 1)); return; }
                    if (e.key === 'ArrowUp')   { e.preventDefault(); setSlashIdx(p => Math.max(p - 1, 0)); return; }
                    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); if (filtered[slashIdx]) executeSlashCommand(filtered[slashIdx].cmd, input.slice(filtered[slashIdx].cmd.length)); else setSlashMenuOpen(false); return; }
                    if (e.key === 'Escape')     { e.preventDefault(); setSlashMenuOpen(false); return; }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
                placeholder={loading ? "Vesper is working — type your next message here…" : "Ask Vesper… (paste images supported)"}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    color: '#fff',
                    fontSize: '14px',
                    '& textarea::placeholder': {
                      color: 'rgba(255, 255, 255, 0.35)',
                    },
                  },
                }}
                size="small"
              />
              {loading ? (
                <Tooltip title="Stop generation" placement="top">
                  <IconButton onClick={stopGeneration} className="cta-button" size="small" sx={{ bgcolor: '#ff4444', '&:hover': { bgcolor: '#cc0000' } }}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Send (Enter)" placement="top">
                  <span style={{ position: 'relative' }}>
                    <IconButton onClick={sendMessage} disabled={!input.trim()} className="cta-button" size="small">
                      <SendIcon />
                    </IconButton>
                    {/* Send burst particles */}
                    {sendBurst && [0,1,2,3,4,5,6,7].map(i => (
                      <Box key={i} className={`send-particle send-particle-${i}`} />
                    ))}
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Clear chat" placement="top">
                <IconButton onClick={clearHistory} className="ghost-button" size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>

            {/* Tools Section - Below Input */}
            <Box className="command-deck" sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(5, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
              gap: 0.5,
              mt: 0.5,
              p: 0.5,
              borderRadius: 1.5,
              border: '1px solid rgba(230,214,182,0.12)',
              background: 'linear-gradient(145deg, rgba(14,14,20,0.92), rgba(8,10,14,0.94))',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
            }}>
              {featuredTools.map((tool) => (
                <Box
                  key={tool.id}
                  onClick={() => {
                    if (tool.id === 'canvas') {
                      setCanvasOpen(true);
                    } else if (tool.id === 'research') {
                      setResearchOpen(true);
                    } else if (tool.id === 'graph') {
                      setGraphOpen(true);
                    } else if (tool.id === 'images') {
                      setImageOpen(true);
                    } else if (tool.id === 'videos') {
                      setVideoOpen(true);
                    } else if (tool.id === 'learning') {
                      setLearningOpen(true);
                    } else if (tool.id === 'enterWorld') {
                      setGameMode(true);
                    } else if (tool.id === 'newChat') {
                      startNewChat();
                    } else if (tool.id === 'clearHistory') {
                      clearHistory();
                    } else if (tool.id === 'mindmap') {
                      setGraphOpen(true);
                    } else if (tool.id === 'sfx') {
                      setVoiceLabOpen(true); setVoiceLabTab('sfx');
                    } else if (tool.id === 'voiceClone') {
                      setVoiceLabOpen(true); setVoiceLabTab('clone');
                    } else if (tool.id === 'voiceIsolate') {
                      setVoiceLabOpen(true); setVoiceLabTab('isolate');
                    } else if (tool.id === 'voicePersonas') {
                      setVoiceLabOpen(true); setVoiceLabTab('personas');
                    } else if (tool.id === 'settings') {
                      setActiveSection('settings');
                    } else {
                      setToast(`${tool.label} feature coming soon`);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.2,
                    minHeight: 32,
                    padding: '3px 2px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'rgba(255,255,255,0.84)',
                    fontSize: '0.68rem',
                    transition: 'all 0.2s ease',
                    border: '1px solid rgba(232,214,180,0.14)',
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                    '&:hover': {
                      bgcolor: 'rgba(242,222,170,0.12)',
                      color: '#fff',
                      borderColor: 'rgba(242,222,170,0.45)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <span style={{ fontSize: '0.75rem', filter: 'drop-shadow(0 0 6px rgba(242,222,170,0.25))' }}>{tool.icon}</span>
                  <Typography variant="caption" sx={{ fontSize: '0.55rem', textAlign: 'center', lineHeight: 1.05, fontWeight: 700, letterSpacing: 0.1 }}>
                    {tool.label}
                  </Typography>
                </Box>
              ))}

              <Box
                onClick={() => setActiveSection('nyxshift')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.2,
                  minHeight: 32,
                  padding: '3px 2px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.84)',
                  fontSize: '0.68rem',
                  transition: 'all 0.2s ease',
                  border: '1px dashed rgba(232,214,180,0.22)',
                  background: 'linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))',
                  '&:hover': {
                    bgcolor: 'rgba(242,222,170,0.1)',
                    color: '#fff',
                    borderColor: 'rgba(242,222,170,0.45)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <span style={{ fontSize: '0.75rem', filter: 'drop-shadow(0 0 6px rgba(242,222,170,0.25))' }}>✦</span>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', textAlign: 'center', lineHeight: 1.05, fontWeight: 700, letterSpacing: 0.1 }}>
                  Full Toolkit
                </Typography>
              </Box>
            </Box>

            {/* ── Voice Lab Panel (ElevenLabs Tools) ───────────────────── */}
            {voiceLabOpen && (
              <Box sx={{
                mt: 1, p: 2,
                background: 'rgba(10,10,30,0.95)',
                border: '1px solid rgba(255,180,50,0.4)',
                borderRadius: 2,
                backdropFilter: 'blur(12px)',
                position: 'relative',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffbb44', display: 'flex', alignItems: 'center', gap: 1 }}>
                    ★ Voice Lab — ElevenLabs
                  </Typography>
                  <IconButton size="small" onClick={() => setVoiceLabOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Tab Row */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { key: 'sfx', label: '🔊 Sound FX', color: '#ffcc55' },
                    { key: 'clone', label: '🎙️ Clone Voice', color: '#cc88ff' },
                    { key: 'isolate', label: '🎛️ Isolate Voice', color: '#66ffbb' },
                    { key: 'personas', label: '🎭 Personas', color: 'var(--accent)' },
                  ].map(t => (
                    <Chip
                      key={t.key}
                      label={t.label}
                      size="small"
                      onClick={() => setVoiceLabTab(t.key)}
                      sx={{
                        bgcolor: voiceLabTab === t.key ? `${t.color}22` : 'rgba(255,255,255,0.05)',
                        color: voiceLabTab === t.key ? t.color : 'rgba(255,255,255,0.5)',
                        border: voiceLabTab === t.key ? `1px solid ${t.color}` : '1px solid rgba(255,255,255,0.1)',
                        fontWeight: voiceLabTab === t.key ? 700 : 500,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `${t.color}15` },
                      }}
                    />
                  ))}
                </Box>

                {/* ── SFX Tab ── */}
                {voiceLabTab === 'sfx' && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Generate any sound effect from a text description using ElevenLabs AI
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <input
                        type="text"
                        placeholder="e.g. cyberpunk door opening, laser blast, rain on a tin roof..."
                        id="voicelab-sfx-input"
                        style={{
                          flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,180,50,0.3)',
                          borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: '0.85rem', outline: 'none',
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('voicelab-sfx-go')?.click(); }}
                      />
                      <Button
                        id="voicelab-sfx-go"
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          const input = document.getElementById('voicelab-sfx-input');
                          if (!input?.value.trim()) return;
                          setToast('⏳ Generating sound effect...');
                          try {
                            const res = await fetch(`${apiBase}/api/sfx/generate`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ prompt: input.value.trim(), duration: 5 }),
                            });
                            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const audio = new Audio(url);
                            audio.play();
                            audio.onended = () => URL.revokeObjectURL(url);
                            setToast('🔊 Playing sound effect!');
                          } catch (e) {
                            setToast('SFX Error: ' + e.message);
                          }
                        }}
                        sx={{ bgcolor: '#ffbb44', color: '#000', fontWeight: 700, minWidth: 80, '&:hover': { bgcolor: '#ffcc66' } }}
                      >
                        Generate
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {['Spaceship engine hum', 'Magical spell cast', 'Rain on cyberpunk city', 'Level up chime', 'Dramatic reveal', 'Laser beam charging', 'Digital glitch noise', 'Thunder rumble'].map(p => (
                        <Chip key={p} label={p} size="small"
                          onClick={() => { const i = document.getElementById('voicelab-sfx-input'); if (i) i.value = p; }}
                          sx={{ fontSize: '0.65rem', height: 22, bgcolor: 'rgba(255,180,50,0.1)', color: '#ffbb44', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,180,50,0.2)' } }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* ── Clone Voice Tab ── */}
                {voiceLabTab === 'clone' && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Upload audio samples to clone any voice. The cloned voice will appear in your voice picker.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <input
                        type="text"
                        placeholder="Voice name (e.g. My Custom Voice)"
                        id="voicelab-clone-name"
                        defaultValue="VesperCustom"
                        style={{
                          flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(200,100,255,0.3)',
                          borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: '0.85rem', outline: 'none',
                        }}
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ bgcolor: '#cc88ff', color: '#fff', fontWeight: 700, mb: 1, '&:hover': { bgcolor: '#dd99ff' } }}
                    >
                      🎙️ Upload Voice Samples (audio files)
                      <input type="file" hidden multiple accept="audio/*"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files?.length) return;
                          const name = document.getElementById('voicelab-clone-name')?.value || 'VesperCustom';
                          setToast('⏳ Cloning voice... this takes a moment');
                          const formData = new FormData();
                          formData.append('name', name);
                          formData.append('description', 'Custom cloned voice via Voice Lab');
                          Array.from(files).forEach(f => formData.append('files', f));
                          try {
                            const res = await fetch(`${apiBase}/api/voice/clone?name=${encodeURIComponent(name)}`, {
                              method: 'POST', body: formData,
                            });
                            const data = await res.json();
                            if (data.success) {
                              setToast('✅ Voice cloned! Check the voice picker.');
                              const vRes = await fetch(`${apiBase}/api/tts/voices`);
                              const vData = await vRes.json();
                              if (vData.voices) setCloudVoices(vData.voices);
                            } else {
                              setToast('Clone failed: ' + (data.error || 'Unknown error'));
                            }
                          } catch (err) { setToast('Clone error: ' + err.message); }
                        }}
                      />
                    </Button>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>
                      Tip: Upload at least 1 minute of clear speech for best results. Multiple files OK.
                    </Typography>
                  </Box>
                )}

                {/* ── Voice Isolation Tab ── */}
                {voiceLabTab === 'isolate' && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
                      Remove background noise, music, and other sounds — isolate just the voice from any audio.
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ bgcolor: '#66ffbb', color: '#000', fontWeight: 700, mb: 1, '&:hover': { bgcolor: '#88ffcc' } }}
                    >
                      🎛️ Upload Audio to Clean
                      <input type="file" hidden accept="audio/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setToast('⏳ Isolating voice... removing background noise');
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const res = await fetch(`${apiBase}/api/voice/isolate`, {
                              method: 'POST', body: formData,
                            });
                            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `isolated_${file.name}`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            const audio = new Audio(url);
                            audio.play();
                            audio.onended = () => URL.revokeObjectURL(url);
                            setToast('🎛️ Playing isolated voice! (also downloaded)');
                          } catch (err) { setToast('Isolation error: ' + err.message); }
                        }}
                      />
                    </Button>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}>
                      Supported: MP3, WAV, M4A, OGG. The cleaned audio will auto-play and download.
                    </Typography>
                  </Box>
                )}

                {/* ── Voice Personas Tab (now includes main voice selector) ── */}
                {voiceLabTab === 'personas' && (
                  <Box>
                    {/* Main Voice Selector */}
                    <Typography variant="caption" sx={{ color: '#ffbb44', fontWeight: 700, display: 'block', mb: 0.5 }}>
                      🎙️ VESPER’S VOICE
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
                      Choose the default voice for all speech
                    </Typography>
                    <Box sx={{
                      maxHeight: 180, overflowY: 'auto', mb: 2, borderRadius: 1,
                      border: '1px solid rgba(255,180,50,0.2)', p: 1,
                      '&::-webkit-scrollbar': { width: 4 },
                      '&::-webkit-scrollbar-thumb': { background: '#ffbb44', borderRadius: 2 },
                    }}>
                      {cloudVoices.length > 0 ? cloudVoices.map((v) => {
                        const isActive = selectedVoiceName === v.id || (!selectedVoiceName && v.id === defaultVoiceId);
                        return (
                          <Box
                            key={v.id}
                            onClick={() => handleVoiceChange(v.id)}
                            sx={{
                              p: 0.75, px: 1, cursor: 'pointer', borderRadius: 1, mb: 0.25,
                              background: isActive ? 'rgba(0,255,136,0.15)' : 'rgba(255,180,50,0.04)',
                              borderLeft: isActive ? '3px solid #00ff88' : '3px solid rgba(255,180,50,0.3)',
                              '&:hover': { background: 'rgba(255,255,255,0.08)' },
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="caption" sx={{
                                color: isActive ? '#00ff88' : '#ffbb44', fontWeight: isActive ? 700 : 600, fontSize: '0.75rem',
                              }}>
                                {v.name}
                              </Typography>
                              {v.gender && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.55rem', px: 0.5, py: 0.1, borderRadius: 0.5,
                                  background: v.gender === 'Female' ? 'rgba(255,100,255,0.15)' : 'rgba(100,200,255,0.15)',
                                  color: v.gender === 'Female' ? '#ff88ff' : '#88ccff', fontWeight: 700,
                                }}>
                                  {v.gender === 'Female' ? '♀' : '♂'}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}>
                                {v.locale || ''}{v.style ? ` • ${v.style}` : ''}
                              </Typography>
                              {v.preview_url && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); playVoicePreview(v.preview_url); }}
                                  sx={{ p: 0.25, color: 'rgba(255,180,50,0.5)', '&:hover': { color: '#ffbb44' } }}
                                >
                                  <PlayArrowIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        );
                      }) : (
                        <Box>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            {voicesLoading ? '⏳ Loading voices...' : '⚠️ Could not load voices'}
                          </Typography>
                          {!voicesLoading && (
                            <Box onClick={() => fetchVoices()} sx={{ mt: 0.5, cursor: 'pointer', color: 'var(--accent)', fontSize: '0.7rem', textDecoration: 'underline' }}>↻ Retry</Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Persona Assignments */}
                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', pt: 1.5 }}>
                      <Typography variant="caption" sx={{ color: 'var(--accent)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                        🎭 PERSONA VOICES
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1 }}>
                        Override the voice for specific contexts (optional)
                      </Typography>
                      <PersonaAssigner apiBase={apiBase} cloudVoices={cloudVoices} setToast={setToast} playVoicePreview={playVoicePreview} onSave={fetchPersonaCache} />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            </Box>
          </section>

          <section className="ops-panel">
            {/* Logo */}
            <Box
              component="img"
              src="/Vesper_Logo.png"
              alt="Vesper"
              className="ops-panel-logo"
              sx={{
                width: '100%',
                maxWidth: 500,
                display: 'block',
                mx: 'auto',
                mb: 1,
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 12px 28px rgba(0,0,0,0.45), 0 0 20px rgba(var(--accent-rgb),0.14)',
                background: 'rgba(0,0,0,0.18)',
              }}
            />



            {/* Dashboard widgets row */}
            <Box className="panel-grid" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mt: 1 }}>
              <WeatherWidget />
              {showSystemStatus && (
                <div 
                  onClick={(e) => {
                    const target = e.target;
                    if (target.closest('.MuiSlider-root') || target.closest('button')) return;
                    setDiagnosticsOpen(true);
                  }} 
                  style={{ cursor: 'pointer' }}
                >
                  <SystemStatusCard apiBase={apiBase} onHide={() => setShowSystemStatus(false)} />
                </div>
              )}
              {!showSystemStatus && (
                <Button 
                  variant="outlined" size="small" 
                  onClick={() => setShowSystemStatus(true)}
                  sx={{ borderColor: 'var(--accent)', color: 'var(--accent)', width: '100%', height: '50px' }}
                >
                  Show System Widget
                </Button>
              )}
            </Box>

            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <div key={activeSection} className="page-transition">
                {renderActiveBoard()}
              </div>
            </DndContext>

          </section>
        </main>
      </Box>

      <Drawer
        anchor="left"
        variant="temporary"
        open={threadsDialogOpen}
        onClose={() => setThreadsDialogOpen(false)}
        transitionDuration={{ enter: 320, exit: 220 }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(2, 4, 10, 0.56)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '94vw', sm: 500 },
            maxWidth: { xs: '94vw', md: `calc(100vw - ${drawerLeft}px)` },
            left: { xs: 0, md: `${drawerLeft}px` },
            top: { xs: 0, md: `${drawerFrame.top}px` },
            height: { xs: '100%', md: `${drawerFrame.height}px` },
            borderRadius: { xs: 0, md: '0 22px 22px 0' },
            bgcolor: 'rgba(10,12,18,0.97)',
            border: '1px solid rgba(230,214,182,0.16)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
            backdropFilter: 'blur(24px)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 2.2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pb: 1.2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#f4e2bb' }}>
                Memory Core
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)' }}>
                Thread history with pinning, organized in one place.
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.8}>
              <Button
                size="small"
                variant="outlined"
                onClick={startNewChat}
                startIcon={<AddIcon />}
                sx={{
                  borderColor: 'rgba(242,222,170,0.22)',
                  color: '#f4e2bb',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                New Chat
              </Button>
              <IconButton
                size="small"
                onClick={() => setThreadsDialogOpen(false)}
                sx={{ color: 'rgba(255,255,255,0.72)' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <TextField
            fullWidth
            size="small"
            value={threadSearchQuery}
            onChange={(e) => setThreadSearchQuery(e.target.value)}
            placeholder="Search conversations"
            sx={{
              mb: 2,
              '.MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.03)',
                borderRadius: '14px',
              },
            }}
          />
          <Stack spacing={1} sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
            {visibleThreads.length === 0 && (
              <Alert severity="info" sx={{ bgcolor: 'rgba(0,255,255,0.08)', color: '#bffaff' }}>
                No conversations matched that search.
              </Alert>
            )}
            {visibleThreads.map((thread) => (
              <Box
                key={thread.id}
                sx={{
                  p: 1.35,
                  borderRadius: '16px',
                  border: currentThreadId === thread.id ? '1px solid rgba(242,222,170,0.34)' : '1px solid rgba(255,255,255,0.08)',
                  bgcolor: currentThreadId === thread.id ? 'rgba(242,222,170,0.08)' : 'rgba(255,255,255,0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => loadThread(thread.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
                    {thread.pinned && <PinIcon sx={{ fontSize: 14, color: '#f4d188' }} />}
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sanitizeTitle(thread.title) || 'Untitled conversation'}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    {thread.updated_at ? new Date(thread.updated_at).toLocaleString() : 'No timestamp'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                  <Tooltip title={thread.pinned ? 'Unpin' : 'Pin'}>
                    <IconButton size="small" onClick={() => togglePinThread(thread.id)} sx={{ color: thread.pinned ? '#f4d188' : 'rgba(255,255,255,0.45)' }}>
                      {thread.pinned ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => deleteThread(thread.id)} sx={{ color: 'rgba(255,255,255,0.45)' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Drawer>

      {/* ─── FOCUS MODE OVERLAY ──────────────────────────────────────── */}
      {focusMode && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.94)',
          backdropFilter: 'blur(14px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          <Typography sx={{ color: 'var(--accent)', fontSize: '0.7rem', letterSpacing: 5, textTransform: 'uppercase', opacity: 0.65 }}>
            {focusPhase === 'work' ? '⚡ FOCUS SESSION' : '☕ BREAK TIME'}
          </Typography>
          <Typography sx={{
            fontFamily: 'monospace', fontSize: '5.5rem', fontWeight: 100, color: '#fff', letterSpacing: '-3px',
            textShadow: '0 0 60px var(--accent), 0 0 20px var(--accent)',
            lineHeight: 1,
          }}>
            {String(Math.floor(focusTimeLeft / 60)).padStart(2, '0')}:{String(focusTimeLeft % 60).padStart(2, '0')}
          </Typography>
          {/* Task selector */}
          {!focusTask ? (
            <Select
              value=""
              displayEmpty
              size="small"
              onChange={(e) => { const t = tasks.find(x => x.title === e.target.value); if (t) setFocusTask(t); }}
              sx={{ minWidth: 260, color: 'rgba(255,255,255,0.6)', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--accent)' } }}
            >
              <MenuItem value="" disabled sx={{ color: 'rgba(255,255,255,0.3)' }}>🎯 Pick a task to focus on…</MenuItem>
              {tasks.filter(t => t.status !== 'done').map((t, i) => (
                <MenuItem key={i} value={t.title} sx={{ color: '#fff' }}>{t.title}</MenuItem>
              ))}
            </Select>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 3, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Current Task</Typography>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1.1rem', maxWidth: 400, textAlign: 'center' }}>{focusTask.title}</Typography>
              <Button size="small" onClick={() => setFocusTask(null)} sx={{ color: 'rgba(255,255,255,0.3)', mt: 0.5, fontSize: '0.65rem' }}>change task</Button>
            </Box>
          )}
          {/* Progress bar */}
          <Box sx={{ width: 300, height: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1, overflow: 'hidden', mt: 1 }}>
            <Box sx={{
              height: '100%', bgcolor: 'var(--accent)',
              width: `${(1 - focusTimeLeft / (focusPhase === 'work' ? 25*60 : 5*60)) * 100}%`,
              transition: 'width 1s linear',
              boxShadow: '0 0 8px var(--accent)',
            }} />
          </Box>
          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => { setFocusRunning(p => !p); if (!focusRunning && focusPhase === 'work') vesperReact('focusStart'); }}
              sx={{ bgcolor: 'var(--accent)', color: '#000', fontWeight: 700, minWidth: 110, '&:hover': { filter: 'brightness(1.15)', bgcolor: 'var(--accent)' } }}
            >
              {focusRunning ? '⏸ Pause' : '▶ Start'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setFocusRunning(false); setFocusTimeLeft(focusPhase === 'work' ? 25*60 : 5*60); }}
              sx={{ borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.55)', '&:hover': { borderColor: 'rgba(255,255,255,0.35)' } }}
            >
              Reset
            </Button>
            <Button
              variant="text"
              onClick={() => { setFocusMode(false); setFocusRunning(false); setFocusPhase('work'); setFocusTimeLeft(25*60); }}
              sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}
            >
              Exit
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>Ctrl+Shift+F to toggle</Typography>
        </Box>
      )}

      {/* ─── KEYBOARD SHORTCUTS MODAL ────────────────────────────────── */}
      <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)}
        PaperProps={{ sx: { bgcolor: 'rgba(4,8,20,0.98)', border: '1px solid rgba(var(--accent-rgb),0.35)', borderRadius: 3, minWidth: 400 } }}
      >
        <DialogTitle sx={{ color: 'var(--accent)', fontWeight: 700, pb: 1 }}>⌨️ Keyboard Shortcuts</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {[
            { key: 'Ctrl + 1 – 5',       desc: 'Switch Chat / Research / Memory / Tasks / Settings' },
            { key: 'Ctrl + K',          desc: 'Open command palette' },
            { key: 'Ctrl + /',           desc: 'Toggle this shortcuts panel' },
            { key: 'Ctrl + Shift + F',   desc: 'Enter / exit Focus (Pomodoro) mode' },
            { key: 'Enter',              desc: 'Send chat message' },
            { key: 'Shift + Enter',      desc: 'New line in chat' },
            { key: '/ (in chat)',        desc: 'Slash commands: /task /remember /search /focus /export /stats' },
            { key: '↑ ↓ + Enter',         desc: 'Navigate and execute slash commands' },
            { key: 'Esc',                desc: 'Close any overlay' },
          ].map(({ key, desc }) => (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', gap: 2 }}>
              <Box sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', px: 1.5, py: 0.4, borderRadius: 1, fontSize: '0.78rem', color: 'var(--accent)', whiteSpace: 'nowrap', flexShrink: 0 }}>{key}</Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}>{desc}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2800}
        onClose={() => { setToast(''); setToastVariant('default'); }}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ContentProps={{
          className: 'toast-enter',
          sx: {
            background: toastVariant === 'celebrate'
              ? 'linear-gradient(135deg, rgba(255,200,0,0.22), rgba(255,100,0,0.15))'
              : toastVariant === 'success'
              ? `linear-gradient(135deg, rgba(var(--accent-rgb),0.2), rgba(0,180,80,0.12))`
              : toastVariant === 'warn'
              ? 'linear-gradient(135deg, rgba(255,150,0,0.2), rgba(200,80,0,0.12))'
              : `linear-gradient(135deg, rgba(var(--accent-rgb),0.15), rgba(0,136,255,0.15))`,
            border: toastVariant === 'celebrate'
              ? '1px solid rgba(255,200,0,0.45)'
              : `1px solid rgba(var(--accent-rgb),0.3)`,
            borderRadius: '12px',
            backdropFilter: 'blur(20px)',
            boxShadow: toastVariant === 'celebrate'
              ? '0 0 30px rgba(255,200,0,0.35), 0 8px 32px rgba(0,0,0,0.4)'
              : `0 0 30px rgba(var(--accent-rgb),0.3), 0 8px 32px rgba(0,0,0,0.4)`,
            color: '#fff',
            fontWeight: 600,
          }
        }}
      />

      <Snackbar
        open={!!voiceErrorToast}
        autoHideDuration={12000}
        onClose={() => setVoiceErrorToast('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setVoiceErrorToast('')}
          sx={{ width: '100%', maxWidth: 900, fontWeight: 600, alignItems: 'center' }}
        >
          {voiceErrorToast}
        </Alert>
      </Snackbar>

      {/* Reminder alarm toasts — stacked at top-right, each dismissible */}
      {reminderAlerts.map((alert, idx) => (
        <Snackbar
          key={alert.id}
          open={true}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ top: `${(idx * 72) + 16}px !important` }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setReminderAlerts(prev => prev.filter(a => a.id !== alert.id))}
            icon="⏰"
            sx={{
              background: 'linear-gradient(135deg, rgba(255,180,0,0.25), rgba(200,80,0,0.18))',
              border: '1px solid rgba(255,180,0,0.5)',
              backdropFilter: 'blur(20px)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.92rem',
              maxWidth: 380,
              boxShadow: '0 0 28px rgba(255,150,0,0.4), 0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            {alert.text}
          </Alert>
        </Snackbar>
      ))}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => { setExportMenuAnchor(null); setExportThreadData(null); }}
      >
        <MenuItem onClick={() => {
          if (exportThreadData) downloadThreadMD(exportThreadData.id, exportThreadData.title);
          setExportMenuAnchor(null);
          setExportThreadData(null);
        }}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export as Markdown
        </MenuItem>
        <MenuItem onClick={() => {
          if (exportThreadData) downloadThreadJSON(exportThreadData.id, exportThreadData.title);
          setExportMenuAnchor(null);
          setExportThreadData(null);
        }}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export as JSON
        </MenuItem>
      </Menu>

      {/* Thread Actions Context Menu */}
      <Menu
        anchorEl={threadMenuAnchor}
        open={Boolean(threadMenuAnchor)}
        onClose={() => { setThreadMenuAnchor(null); setThreadMenuThread(null); }}
        PaperProps={{ sx: { bgcolor: 'rgba(15,18,35,0.95)', border: '1px solid rgba(0,255,255,0.15)', backdropFilter: 'blur(12px)', minWidth: 180 } }}
      >
        {threadMenuThread && [
          <MenuItem key="rename" onClick={() => {
            startRenameThread(threadMenuThread.id, threadMenuThread.title);
            setThreadMenuAnchor(null); setThreadMenuThread(null);
          }} sx={{ color: '#fff', fontSize: '0.875rem', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
            <EditIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--accent)' }} /> Rename
          </MenuItem>,
          <MenuItem key="pin" onClick={() => {
            togglePinThread(threadMenuThread.id);
            setThreadMenuAnchor(null); setThreadMenuThread(null);
          }} sx={{ color: '#fff', fontSize: '0.875rem', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
            {threadMenuThread.pinned
              ? <><PinIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--accent)' }} /> Unpin</>
              : <><PinOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: 'rgba(255,255,255,0.6)' }} /> Pin</>
            }
          </MenuItem>,
          <MenuItem key="export" onClick={(e) => {
            setExportThreadData({ id: threadMenuThread.id, title: threadMenuThread.title });
            setExportMenuAnchor(e.currentTarget);
            setThreadMenuAnchor(null); setThreadMenuThread(null);
          }} sx={{ color: '#fff', fontSize: '0.875rem', '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}>
            <DownloadIcon fontSize="small" sx={{ mr: 1.5, color: 'rgba(255,255,255,0.6)' }} /> Export
          </MenuItem>,
          <Divider key="divider" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />,
          <MenuItem key="delete" onClick={() => {
            deleteThread(threadMenuThread.id);
            playSound('click');
            setThreadMenuAnchor(null); setThreadMenuThread(null);
          }} sx={{ color: '#ff4444', fontSize: '0.875rem', '&:hover': { bgcolor: 'rgba(255,68,68,0.1)' } }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete
          </MenuItem>,
        ]}
      </Menu>
      
      {/* Canvas Modal */}
      <Dialog open={canvasOpen} onClose={() => setCanvasOpen(false)} maxWidth="lg" fullWidth fullScreen>
        <Canvas 
            onClose={() => setCanvasOpen(false)} 
            onShare={() => setToast('Canvas shared!')} 
            initialCode={canvasAppCode}
            initialTab={canvasActiveTab}
        />
      </Dialog>

      {/* Knowledge Graph Modal */}
      <Dialog 
        open={graphOpen} 
        onClose={() => setGraphOpen(false)} 
        maxWidth="xl" 
        fullWidth 
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0,0,0,0.92)',
          },
        }}
      >
        <Box sx={{ position: 'relative', width: '100vw', height: '100vh' }}>
            <KnowledgeGraph apiBase={apiBase} />
            <IconButton
              onClick={() => setGraphOpen(false)}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(255,0,0,0.5)' },
                zIndex: 1000
              }}
            >
              <CloseIcon />
            </IconButton>
        </Box>
      </Dialog>

      {/* Deep Research Drawer */}
      <Drawer
        anchor="left"
        open={researchOpen}
        onClose={() => setResearchOpen(false)}
        transitionDuration={{ enter: 320, exit: 220 }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(2, 4, 10, 0.56)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 560, md: 680 },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerLeft}px)` },
            left: { xs: 0, md: `${drawerLeft}px` },
            top: { xs: 0, md: `${drawerFrame.top}px` },
            height: { xs: '100%', md: `${drawerFrame.height}px` },
            borderRadius: { xs: 0, md: '0 22px 22px 0' },
            bgcolor: 'rgba(10,12,18,0.98)',
            border: '1px solid rgba(230,214,182,0.16)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
            backdropFilter: 'blur(24px)',
            overflow: 'auto',
          },
        }}
      >
        <DeepResearch apiBase={apiBase} onClose={() => setResearchOpen(false)} />
      </Drawer>

      {/* Image Generator Drawer */}
      <Drawer
        anchor="left"
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        transitionDuration={{ enter: 320, exit: 220 }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(2, 4, 10, 0.56)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 560, md: 680 },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerLeft}px)` },
            left: { xs: 0, md: `${drawerLeft}px` },
            top: { xs: 0, md: `${drawerFrame.top}px` },
            height: { xs: '100%', md: `${drawerFrame.height}px` },
            borderRadius: { xs: 0, md: '0 22px 22px 0' },
            bgcolor: 'rgba(10,12,18,0.98)',
            border: '1px solid rgba(230,214,182,0.16)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
            backdropFilter: 'blur(24px)',
            overflow: 'auto',
          },
        }}
      >
        <ImageGenerator apiBase={apiBase} onClose={() => setImageOpen(false)} />
      </Drawer>

      {/* Video Creator Drawer */}
      <Drawer
        anchor="left"
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        transitionDuration={{ enter: 320, exit: 220 }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(2, 4, 10, 0.56)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 560, md: 680 },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerLeft}px)` },
            left: { xs: 0, md: `${drawerLeft}px` },
            top: { xs: 0, md: `${drawerFrame.top}px` },
            height: { xs: '100%', md: `${drawerFrame.height}px` },
            borderRadius: { xs: 0, md: '0 22px 22px 0' },
            bgcolor: 'rgba(10,12,18,0.98)',
            border: '1px solid rgba(230,214,182,0.16)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
            backdropFilter: 'blur(24px)',
            overflow: 'auto',
          },
        }}
      >
        <VideoCreator apiBase={apiBase} onClose={() => setVideoOpen(false)} />
      </Drawer>

      {/* Guided Learning Drawer */}
      <Drawer
        anchor="left"
        open={learningOpen}
        onClose={() => setLearningOpen(false)}
        transitionDuration={{ enter: 320, exit: 220 }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(2, 4, 10, 0.56)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 560, md: 680 },
            maxWidth: { xs: '100vw', md: `calc(100vw - ${drawerLeft}px)` },
            left: { xs: 0, md: `${drawerLeft}px` },
            top: { xs: 0, md: `${drawerFrame.top}px` },
            height: { xs: '100%', md: `${drawerFrame.height}px` },
            borderRadius: { xs: 0, md: '0 22px 22px 0' },
            bgcolor: 'rgba(10,12,18,0.98)',
            border: '1px solid rgba(230,214,182,0.16)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.72), 0 0 48px rgba(218,165,32,0.1)',
            backdropFilter: 'blur(24px)',
            overflow: 'auto',
          },
        }}
      >
        <GuidedLearning apiBase={apiBase} onClose={() => setLearningOpen(false)} />
      </Drawer>
      
      {/* System Diagnostics Modal */}
      <SystemDiagnostics 
        open={diagnosticsOpen} 
        onClose={() => setDiagnosticsOpen(false)} 
        apiBase={apiBase} 
      />

      {/* Background Studio */}
      <BackgroundStudio
        open={backgroundStudioOpen}
        onClose={() => setBackgroundStudioOpen(false)}
        apiBase={apiBase}
        setToast={setToast}
        activeTheme={activeTheme}
        customBackground={customBackground}
        setCustomBackground={setCustomBackground}
        backgroundGallery={backgroundGallery}
        setBackgroundGallery={setBackgroundGallery}
        backgroundSettings={backgroundSettings}
        setBackgroundSettings={setBackgroundSettings}
      />



      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteConfirm} 
        onClose={() => setShowDeleteConfirm(false)}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(10, 10, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            borderRadius: '16px',
            minWidth: '300px'
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <DeleteIcon sx={{ fontSize: 48, color: '#ff4444', mb: 2, opacity: 0.8 }} />
          <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 700 }}>
            {deleteTargetId === 'bulk' 
              ? `Delete ${selectedThreadIds.length} Conversations?` 
              : 'Delete Conversation?'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            This action cannot be undone. All messages and data will be permanently removed.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              onClick={() => setShowDeleteConfirm(false)}
              sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={executeDelete}
              startIcon={<DeleteIcon />}
              sx={{ bgcolor: '#ff4444', '&:hover': { bgcolor: '#ff0000' } }}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Floating Zoom Control — bottom-right of screen */}
      <Box sx={{
        position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
        bgcolor: 'rgba(8, 12, 24, 0.85)', backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2,
        p: 0.5, opacity: 0.6, transition: 'opacity 0.2s ease',
        '&:hover': { opacity: 1 },
      }}>
        <IconButton
          size="small"
          onClick={() => { const v = Math.min(uiScale + 0.1, 1.5); setUiScale(v); localStorage.setItem('vesper_ui_scale', v.toString()); }}
          sx={{ color: 'var(--accent)', width: 28, height: 28, '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}
        >
          <ZoomInIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.55rem', lineHeight: 1, userSelect: 'none' }}>
          {Math.round(uiScale * 100)}%
        </Typography>
        <IconButton
          size="small"
          onClick={() => { const v = Math.max(uiScale - 0.1, 0.5); setUiScale(v); localStorage.setItem('vesper_ui_scale', v.toString()); }}
          sx={{ color: 'var(--accent)', width: 28, height: 28, '&:hover': { bgcolor: 'rgba(0,255,255,0.1)' } }}
        >
          <ZoomOutIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <Box sx={{
          position: 'fixed',
          bottom: { xs: 0, sm: 20 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          width: { xs: '100%', sm: 'auto' },
          minWidth: { sm: 340 },
          maxWidth: { xs: '100%', sm: 440 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
          background: 'linear-gradient(135deg, rgba(10,14,22,0.97), rgba(6,10,18,0.97))',
          border: '1px solid rgba(var(--accent-rgb),0.35)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.8), 0 0 30px rgba(var(--accent-rgb),0.15)',
          backdropFilter: 'blur(20px)',
          animation: 'slideUpFade 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          '@keyframes slideUpFade': {
            from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
            to:   { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
          },
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.2), rgba(var(--accent-rgb),0.08))',
            border: '1px solid rgba(var(--accent-rgb),0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem',
          }}>✨</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', lineHeight: 1.2 }}>
              Install Vesper
            </Typography>
            <Typography sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.55)', mt: 0.2 }}>
              Add to home screen for instant access
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={() => {
                localStorage.setItem('vesper_install_dismissed', '1');
                setShowInstallBanner(false);
              }}
              sx={{ color: 'rgba(255,255,255,0.4)', width: 30, height: 30, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Box
              component="button"
              onClick={async () => {
                if (!installPromptEvent) return;
                installPromptEvent.prompt();
                const { outcome } = await installPromptEvent.userChoice;
                if (outcome === 'accepted') {
                  setShowInstallBanner(false);
                  setInstallPromptEvent(null);
                }
              }}
              sx={{
                px: 1.8, py: 0.6, borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, var(--accent), rgba(var(--accent-rgb),0.7))',
                color: '#000', fontWeight: 800, fontSize: '0.78rem',
                fontFamily: 'inherit',
                boxShadow: '0 2px 12px rgba(var(--accent-rgb),0.4)',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 18px rgba(var(--accent-rgb),0.6)' },
                '&:active': { transform: 'translateY(0)' },
              }}
            >
              Install
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── STARRED MESSAGES PANEL ──────────────────────────────────────── */}
      {starredPanelOpen && (
        <Box onClick={() => setStarredPanelOpen(false)}
          sx={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', p: 2 }}>
          <Box onClick={e => e.stopPropagation()}
            sx={{ width: 380, maxHeight: '70vh', borderRadius: 3, bgcolor: 'rgba(8,11,20,0.98)', border: '1px solid rgba(var(--accent-rgb),0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'cmdSlideIn 0.18s cubic-bezier(0.22,1,0.36,1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <StarRounded sx={{ color: '#FFD700', fontSize: 16, mr: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', flex: 1 }}>Starred Messages</Typography>
              <IconButton size="small" onClick={() => setStarredPanelOpen(false)} sx={{ color: 'rgba(255,255,255,0.35)' }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {starredMsgs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <StarBorderIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.15)', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.82rem' }}>No starred messages yet.</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', mt: 0.5 }}>Star any message to save it here.</Typography>
                </Box>
              ) : [...starredMsgs].reverse().map(m => (
                <Box key={m.id} sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{m.role}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>{m.content.slice(0, 280)}{m.content.length > 280 ? '…' : ''}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.85 }}>
                    <Chip label="Copy" size="small" onClick={() => navigator.clipboard.writeText(m.content).then(() => setToast('Copied!'))}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer', bgcolor: 'rgba(var(--accent-rgb),0.1)', color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.2)' } }} />
                    <Chip label="Unstar" size="small" onClick={() => toggleStarMessage(m)}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── GLOBAL SEARCH ────────────────────────────────────────────────── */}
      {globalSearchOpen && (
        <Box onClick={() => setGlobalSearchOpen(false)}
          sx={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: '10vh' }}>
          <Box onClick={e => e.stopPropagation()}
            sx={{ width: '100%', maxWidth: 580, mx: 2, borderRadius: 3, bgcolor: 'rgba(8,11,20,0.99)', border: '1px solid rgba(var(--accent-rgb),0.25)', overflow: 'hidden', animation: 'cmdSlideIn 0.15s cubic-bezier(0.22,1,0.36,1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <ManageSearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18, flexShrink: 0 }} />
              <input
                autoFocus
                value={globalSearchQuery}
                onChange={e => setGlobalSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') runGlobalSearch(globalSearchQuery);
                  if (e.key === 'Escape') setGlobalSearchOpen(false);
                }}
                placeholder="Search all conversations…"
                style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontSize: '0.92rem', fontFamily: 'inherit' }}
              />
              <IconButton size="small" onClick={() => runGlobalSearch(globalSearchQuery)} sx={{ color: 'var(--accent)', '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.12)' } }}>
                <ManageSearchIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => setGlobalSearchOpen(false)} sx={{ color: 'rgba(255,255,255,0.3)' }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
            </Box>
            <Box sx={{ maxHeight: 420, overflowY: 'auto', p: 1.5 }}>
              {globalSearchLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} sx={{ color: 'var(--accent)' }} />
                </Box>
              )}
              {!globalSearchLoading && !globalSearchQuery && (
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.79rem', textAlign: 'center', py: 3 }}>
                  Type and press Enter to search across all your conversations
                </Typography>
              )}
              {!globalSearchLoading && globalSearchQuery && globalSearchResults.length === 0 && (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.82rem', textAlign: 'center', py: 3 }}>
                  No results for "{globalSearchQuery}"
                </Typography>
              )}
              {globalSearchResults.map((r, i) => (
                <Box key={i}
                  onClick={() => { if (r.thread_id) loadThread(r.thread_id); setGlobalSearchOpen(false); }}
                  sx={{ p: 1.25, mb: 0.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(var(--accent-rgb),0.08)', borderColor: 'rgba(var(--accent-rgb),0.2)' } }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#fff', fontWeight: 600, mb: 0.3 }}>
                    {r.title || (r.text || r.content || '').slice(0, 60) || 'Memory'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.45 }}>
                    {(r.content || r.text || '').slice(0, 160)}{(r.content || r.text || '').length > 160 ? '…' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ─── CMD+K COMMAND PALETTE ─────────────────────────────────────── */}
      {cmdOpen && (
        <Box
          onClick={() => setCmdOpen(false)}
          sx={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            pt: '12vh',
            animation: 'fadeIn 0.12s ease',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: '100%', maxWidth: 580,
              mx: 2,
              background: 'linear-gradient(145deg, rgba(14,18,28,0.98), rgba(8,10,18,0.99))',
              border: '1px solid rgba(var(--accent-rgb), 0.25)',
              borderRadius: '18px',
              boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(var(--accent-rgb),0.12), 0 0 60px rgba(var(--accent-rgb),0.08)',
              overflow: 'hidden',
              animation: 'cmdSlideIn 0.15s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            {/* Search input */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}>⌘</Typography>
              <input
                autoFocus
                value={cmdQuery}
                onChange={(e) => { setCmdQuery(e.target.value); setCmdFocusIdx(0); }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setCmdFocusIdx(i => Math.min(i + 1, filteredCommands.length - 1)); }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setCmdFocusIdx(i => Math.max(i - 1, 0)); }
                  if (e.key === 'Enter') {
                    const cmd = filteredCommands[cmdFocusIdx];
                    if (cmd) { cmd.action(); setCmdOpen(false); }
                  }
                  if (e.key === 'Escape') { setCmdOpen(false); }
                }}
                placeholder="Search commands, themes, sections…"
                style={{
                  flex: 1, background: 'none', border: 'none', outline: 'none',
                  color: '#fff', fontSize: '0.95rem', fontFamily: 'inherit',
                  letterSpacing: '0.01em',
                }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.65rem', letterSpacing: '0.05em', flexShrink: 0 }}>ESC</Typography>
            </Box>

            {/* Results */}
            <Box sx={{ maxHeight: 420, overflowY: 'auto', py: 0.75 }}>
              {(() => {
                let lastGroup = null;
                return filteredCommands.map((cmd, i) => {
                  const isActive = i === cmdFocusIdx;
                  const showHeader = cmd.group !== lastGroup;
                  lastGroup = cmd.group;
                  return (
                    <React.Fragment key={`${cmd.group}-${cmd.label}`}>
                      {showHeader && (
                        <Typography sx={{
                          px: 2, pt: i === 0 ? 0.5 : 1.5, pb: 0.4,
                          fontSize: '0.62rem', fontWeight: 700,
                          color: 'rgba(255,255,255,0.25)',
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                        }}>
                          {cmd.group}
                        </Typography>
                      )}
                      <Box
                        onClick={() => { cmd.action(); setCmdOpen(false); }}
                        onMouseEnter={() => setCmdFocusIdx(i)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2, py: 0.75, mx: 0.75, borderRadius: '10px',
                          cursor: 'pointer',
                          background: isActive ? 'rgba(var(--accent-rgb), 0.12)' : 'transparent',
                          border: isActive ? '1px solid rgba(var(--accent-rgb), 0.2)' : '1px solid transparent',
                          transition: 'all 0.1s ease',
                        }}
                      >
                        {cmd.accent ? (
                          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: cmd.accent, flexShrink: 0, boxShadow: `0 0 6px ${cmd.accent}88` }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.8rem', color: isActive ? 'var(--accent)' : 'rgba(255,255,255,0.35)', lineHeight: 1, flexShrink: 0, width: 14, textAlign: 'center' }}>
                            {cmd.icon}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.85rem', color: isActive ? '#fff' : 'rgba(255,255,255,0.72)', fontWeight: isActive ? 600 : 400, flex: 1 }}>
                          {cmd.label}
                        </Typography>
                        {isActive && <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.04em' }}>↵</Typography>}
                      </Box>
                    </React.Fragment>
                  );
                });
              })()}
              {filteredCommands.length === 0 && (
                <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem' }}>No results for "{cmdQuery}"</Typography>
                </Box>
              )}
            </Box>

            {/* Footer hint */}
            <Box sx={{
              display: 'flex', gap: 2, px: 2, py: 1,
              borderTop: '1px solid rgba(255,255,255,0.05)',
              alignItems: 'center',
            }}>
              {[['↑↓', 'navigate'], ['↵', 'select'], ['esc', 'close']].map(([key, hint]) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ px: 0.75, py: 0.2, borderRadius: '5px', border: '1px solid rgba(255,255,255,0.12)', bgcolor: 'rgba(255,255,255,0.04)' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>{key}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.2)' }}>{hint}</Typography>
                </Box>
              ))}
              <Typography sx={{ ml: 'auto', fontSize: '0.62rem', color: 'rgba(255,255,255,0.15)', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
                Vesper
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      </Box>
    </ThemeProvider>
  );
}

export default App;

